// Long-form patient education articles — Estonian only (initial).
// Translations (RU/EN) will be added later.
//
// Each article structure:
//   id, slug, category, title, summary, readingMinutes, updated, author,
//   sections: array of blocks { h2, p, ul, ol, callout, q&a }

const DR_AUTHOR = "Dr. Verner Fogel, endokrinoloog";

export const DISEASE_ARTICLES = [
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "1-tuupi-diabeet",
    slug: "1-tuupi-diabeet",
    category: "Diabeet",
    title: "1. tüüpi diabeet",
    image: "/api/static/ai-images/articles/1-tuupi-diabeet.webp",
    imageCaption: "Immuunsüsteem ründab kõhunäärme insuliini tootvaid rakke",
    summary:
      "1. tüüpi diabeet (T1D) on autoimmuunhaigus, mille korral organism ise hävitab kõhunäärme insuliini tootvad beetarakud. Insuliini tootmine organismis lõpeb ning haige vajab eluaegset insuliinravi. Käesolev artikkel selgitab haiguse tekkemehhanismi, sümptomeid, diagnoosimist, kaasaegset ravi ning seda, kuidas elada täisväärtuslikku elu.",
    readingMinutes: 8,
    updated: "2026-06-10",
    author: DR_AUTHOR,
    sections: [
      { h2: "Mis on 1. tüüpi diabeet?" },
      {
        p: "1. tüüpi diabeet on krooniline autoimmuunhaigus, mille puhul keha immuunsüsteem hakkab ekslikult ründama kõhunäärme Langerhansi saarekestes asuvaid beetarakke. Just need rakud toodavad hormooni insuliini, mis on hädavajalik veres oleva glükoosi (suhkru) transportimiseks kehas asuvatesse rakkudesse, kus seda kasutatakse energiana. Kui beetarakud on hävitatud, ei suuda organism enam insuliini toota — vere glükoosisisaldus tõuseb ja patsient vajab eluaegset insuliinravi.",
      },
      {
        p: "Erinevalt 2. tüüpi diabeedist ei ole 1. tüüpi diabeet seotud elustiili ega ülekaaluga. See haigus algab sageli lapsepõlves või noores täiskasvanueas, kuid võib tekkida igas vanuses. Eestis on 1. tüüpi diabeedi diagnoosiga umbes 5 000 inimest ning igal aastal lisandub 200–300 uut juhtu.",
      },
      { h2: "Põhjused ja riskitegurid" },
      {
        p: "Täpne põhjus, miks immuunsüsteem hakkab ründama oma keha rakke, on tänaseni teadmata. Teadlased on avastanud kaks peamist tegurit, mis koos haiguse käivitavad:",
      },
      {
        ul: [
          "Geneetiline eelsoodumus — eriti teatud HLA-geeni variandid suurendavad riski 10–20 korda. Sugulasel haiguse esinemine tõstab riski, kuid enamik haigeid on ilma perekondliku ajaloota.",
          "Keskkonnafaktor — arvatakse, et viirusinfektsioonid (näiteks enteroviirused, leetrid), varajane lehmapiima tarbimine imikueas, D-vitamiini puudus või soolestiku mikrofloora muutused võivad olla käivitavateks teguriteks geneetiliselt vastuvõtlikel inimestel.",
        ],
      },
      {
        p: "Haigus tekib aeglaselt. Autoimmuunprotsess võib alata aastaid enne, kui ilmnevad esimesed sümptomid. 80–90% beetarakkudest on tavaliselt juba hävinud, kui haigus avastatakse kõrge veresuhkru tõttu.",
      },
      { h2: "Sümptomid" },
      {
        p: "1. tüüpi diabeedi sümptomid arenevad sageli kiiresti — mõne nädala või kuu jooksul. Kõige tüüpilisemad on:",
      },
      {
        ul: [
          "Tugev janu (polüdipsia) — patsient joob mitu liitrit vett ööpäevas",
          "Sage urineerimine, eriti öösel (polüuuria)",
          "Seletamatu kaalulangus — sageli 5–10 kg mõne nädala jooksul, vaatamata heale söögiisule",
          "Pidev väsimus ja nõrkus",
          "Hägune nägemine",
          "Aeglaselt paranevad haavad ja sagedased nahainfektsioonid",
          "Atsetoonilõhn hingeõhus (juba kaugelearenenud staadiumis)",
        ],
      },
      {
        callout: {
          type: "warning",
          title: "Hädaolukord — ketoatsidoos",
          body:
            "Kui patsient ei saa õigeaegset diagnoosi, võib tekkida diabeetiline ketoatsidoos (DKA) — eluohtlik seisund, mille tunnusteks on iiveldus, oksendamine, kõhuvalu, sügav ja sage hingamine ning teadvuse hägunemine. DKA puhul tuleb pöörduda kohe erakorralise meditsiini osakonda.",
        },
      },
      { h2: "Diagnoosimine" },
      { p: "1. tüüpi diabeedi diagnoos pannakse järgmiste analüüside põhjal:" },
      {
        ul: [
          "Tühja kõhu veresuhkur ≥ 7,0 mmol/L",
          "Juhuslik veresuhkur ≥ 11,1 mmol/L koos klassikaliste sümptomitega",
          "HbA1c (glükohemoglobiin) ≥ 6,5% — näitab keskmist veresuhkru taset viimase 2–3 kuu jooksul",
          "Glükoosi tolerantsustest (läheb harva vaja) — 2 tunni veresuhkur ≥ 11,1 mmol/L peale 75 g glükoosi joomist",
        ],
      },
      {
        p: "Selleks, et eristada 1. tüüpi diabeeti 2. tüüpi diabeedist, määratakse täiendavalt autoantikehad (GAD65, IA-2, ZnT8, IAA) ja C-peptiidi tase. 1. tüüpi diabeedi puhul on autoantikehad sageli positiivsed ning C-peptiid madal (oma insuliini tootmine on vähene või puudub).",
      },
      { h2: "Ravi: kaasaegne lähenemine" },
      {
        p: "1. tüüpi diabeedis pole võimalik terveks saada. Ravi põhineb insuliini asendamisel ning koosneb järgmistest komponentidest:",
      },
      { h2: "Insuliinravi" },
      { p: "Insuliini saab manustada kahel viisil:" },
      {
        ul: [
          "Mitmesüstirežiim (MDI) — patsient süstib pikatoimelist baasinsuliini 1–2 korda päevas ning lühitoimelist söögiinsuliini iga söögikorra eel (4–6 süsti päevas).",
          "Insuliinipump — väike seade, mis süstib kogu päeva pidevalt lühitoimelist insuliini. Pumpa saab kombineerida pideva glükoosimonitooringuga (CGM) suletud-süsteemse \"tehispankrease\" loomiseks, mis reguleerib insuliini doosi automaatselt.",
        ],
      },
      { h2: "Pidev glükoosimonitooring (CGM)" },
      {
        p: "CGM-sensor (näiteks FreeStyle Libre, Dexcom, Linx, Airex) mõõdab glükoosi nahaaluskoes iga 1–5 minuti tagant. Patsient näeb oma veresuhkrut reaalajas telefonis ja saab hoiatusi, kui see tõuseb või langeb ohtlikule tasemele. CGM on tõestanud, et vähendab oluliselt HbA1c-d, hüpoglükeemia riski ja parandab elukvaliteeti.",
      },
      { h2: "Süsivesikute arvestamine" },
      {
        p: "Söögikorra eelse insuliini doosi arvutatakse söögis sisalduvate süsivesikute järgi. Diabeediõde õpetab arvestama 1 leivaühik = 10 g süsivesikuid ja määrama isikliku insuliini-süsivesikute suhte (näiteks 1 ühik insuliini 10 g söödud süsivesikute kohta). Antud teadmise ning selle teadmise rakendamisega igapäevases elus on võimalik patsiendil süüa peaaegu kõike, kuid peab oskama doosi õigesti arvutada.",
      },
      { h2: "Liikumine ja kehakaal" },
      {
        p: "Regulaarne füüsiline aktiivsus tõstab insuliinitundlikkust ja vähendab vajaminevat insuliinidoosi. Soovitus on 150 minutit mõõdukat aeroobset koormust nädalas (näiteks kiirkõnd, ujumine, rattasõit) ning 2 korda nädalas jõutreening. Treeningu ajal tuleb jälgida veresuhkrut, kuna see võib langeda kiiresti (eriti pärast pikemat aeroobset treeningut).",
      },
      { h2: "Tüsistused ja kuidas neid ennetada" },
      { p: "Pikaajaliselt kõrge veresuhkur kahjustab veresoonte seinu. Kõige sagedasemad tüsistused on:" },
      {
        ul: [
          "Retinopaatia (silmavõrkkesta kahjustus) — võib põhjustada pimedaks jäämist",
          "Nefropaatia (neerude kahjustus) — võib viia neeruasendusravini",
          "Neuropaatia (närvikahjustus) — põhjustab valu, tundlikkuse vähenemist, jalahaavandeid",
          "Südame-veresoonkonna haigused — südameinfarkti ja insuldi risk on 2–3 korda kõrgem kui mittediabeetikutel",
        ],
      },
      {
        p: "Hea teada: kui HbA1c hoida alla 7% (ideaalsel juhul 6,5%) ning vererõhk ja kolesterool kontrolli all, väheneb tüsistuste risk oluliselt. Vajalik on distsipliin söömisel ja veresuhkru jälgimine, regulaarsed analüüsid endokrinoloogi ja/või diabeediõe juures.",
      },
      { h2: "Millal pöörduda meie kliinikusse?" },
      {
        p: "Kui sul on diagnoositud 1. tüüpi diabeet või kahtlustad seda, broneeri vastuvõtt Tallinna Endokrinoloogia Kliinikusse. Meie kliinikus:",
      },
      {
        ul: [
          "Endokrinoloog hindab sinu hetkeseisu ja kohandab insuliiniravi.",
          "Diabeediõde õpetab insuliini süstimist, süsivesikute arvestamist, CGM-sensori paigaldamist ja kasutamist.",
          "Määrab analüüsid ja uuringud: HbA1c, albumiini/kreatiniini suhte ja silmapõhjade pildistamine.",
          "Mõõdab ABI-indeksit jalgade verevarustuse hindamiseks.",
        ],
      },
      {
        callout: {
          type: "info",
          title: "Kokkuvõte",
          body:
            "1. tüüpi diabeet on eluaegne autoimmuunhaigus, mis nõuab pidevat insuliiniravi. Kaasaegne ravi (CGM + pump või MDI), õige toitumine ja regulaarne jälgimine võimaldavad elada täisväärtuslikku ja pikka elu ilma tõsiste tüsistusteta.",
        },
      },
    ],
    translations: {
      ru: {
        category: "Диабет",
        title: "Сахарный диабет 1 типа",
        imageCaption: "Иммунная система атакует инсулин-продуцирующие клетки поджелудочной железы",
        summary:
          "Сахарный диабет 1 типа (СД1) — это аутоиммунное заболевание, при котором организм сам разрушает бета-клетки поджелудочной железы, вырабатывающие инсулин. Выработка инсулина прекращается, и пациенту требуется пожизненная инсулинотерапия. В этой статье объясняется механизм развития заболевания, симптомы, диагностика, современное лечение и то, как жить полноценной жизнью.",
        author: "Д-р Вернер Фогель, эндокринолог",
        sections: [
          { h2: "Что такое сахарный диабет 1 типа?" },
          { p: "Сахарный диабет 1 типа — это хроническое аутоиммунное заболевание, при котором иммунная система ошибочно начинает атаковать бета-клетки, находящиеся в островках Лангерганса поджелудочной железы. Именно эти клетки вырабатывают гормон инсулин, необходимый для транспорта глюкозы (сахара) из крови в клетки организма, где она используется в качестве источника энергии. Когда бета-клетки разрушены, организм больше не может вырабатывать инсулин — уровень глюкозы в крови повышается, и пациент нуждается в пожизненной инсулинотерапии." },
          { p: "В отличие от сахарного диабета 2 типа, СД1 не связан с образом жизни или избыточным весом. Заболевание часто начинается в детстве или в молодом взрослом возрасте, но может развиться в любом возрасте. В Эстонии диагноз СД1 имеют около 5 000 человек, и ежегодно регистрируется 200–300 новых случаев." },
          { h2: "Причины и факторы риска" },
          { p: "Точная причина, по которой иммунная система начинает атаковать собственные клетки организма, до сих пор неизвестна. Учёные обнаружили два основных фактора, которые вместе запускают заболевание:" },
          {
            ul: [
              "Генетическая предрасположенность — особенно определённые варианты гена HLA повышают риск в 10–20 раз. Наличие болезни у родственника увеличивает риск, но большинство пациентов не имеют семейного анамнеза.",
              "Фактор окружающей среды — считается, что вирусные инфекции (например, энтеровирусы, корь), раннее употребление коровьего молока в младенчестве, дефицит витамина D или изменения кишечной микрофлоры могут стать триггерами у генетически предрасположенных людей.",
            ],
          },
          { p: "Заболевание развивается медленно. Аутоиммунный процесс может начаться за годы до появления первых симптомов. На момент выявления болезни (из-за высокого уровня сахара в крови) обычно уже разрушено 80–90% бета-клеток." },
          { h2: "Симптомы" },
          { p: "Симптомы СД1 часто развиваются быстро — в течение нескольких недель или месяцев. Наиболее типичные:" },
          {
            ul: [
              "Сильная жажда (полидипсия) — пациент выпивает несколько литров воды в сутки",
              "Частое мочеиспускание, особенно ночью (полиурия)",
              "Необъяснимая потеря веса — часто 5–10 кг за несколько недель при хорошем аппетите",
              "Постоянная усталость и слабость",
              "Расплывчатое зрение",
              "Медленно заживающие раны и частые кожные инфекции",
              "Запах ацетона изо рта (на поздней стадии)",
            ],
          },
          {
            callout: {
              type: "warning",
              title: "Неотложное состояние — кетоацидоз",
              body:
                "Если пациент не получает своевременной диагностики, может развиться диабетический кетоацидоз (ДКА) — угрожающее жизни состояние, признаками которого являются тошнота, рвота, боль в животе, глубокое и частое дыхание, помутнение сознания. При ДКА необходимо немедленно обратиться в отделение неотложной помощи.",
            },
          },
          { h2: "Диагностика" },
          { p: "Диагноз СД1 устанавливается на основании следующих анализов:" },
          {
            ul: [
              "Глюкоза крови натощак ≥ 7,0 ммоль/л",
              "Случайная глюкоза крови ≥ 11,1 ммоль/л в сочетании с классическими симптомами",
              "HbA1c (гликированный гемоглобин) ≥ 6,5% — отражает средний уровень сахара за последние 2–3 месяца",
              "Тест толерантности к глюкозе (требуется редко) — глюкоза через 2 часа после 75 г глюкозы ≥ 11,1 ммоль/л",
            ],
          },
          { p: "Чтобы отличить СД1 от СД2, дополнительно определяют аутоантитела (GAD65, IA-2, ZnT8, IAA) и уровень C-пептида. При СД1 аутоантитела часто положительны, а C-пептид низкий (собственная выработка инсулина минимальна или отсутствует)." },
          { h2: "Лечение: современный подход" },
          { p: "От СД1 невозможно вылечиться полностью. Лечение основано на заместительной инсулинотерапии и состоит из следующих компонентов:" },
          { h2: "Инсулинотерапия" },
          { p: "Инсулин можно вводить двумя способами:" },
          {
            ul: [
              "Режим многократных инъекций (MDI) — пациент вводит базальный инсулин длительного действия 1–2 раза в сутки и быстродействующий инсулин перед каждым приёмом пищи (4–6 инъекций в день).",
              "Инсулиновая помпа — небольшое устройство, которое непрерывно вводит быстродействующий инсулин в течение всего дня. Помпу можно сочетать с непрерывным мониторингом глюкозы (CGM) для создания «искусственной поджелудочной железы» с замкнутым контуром, которая автоматически регулирует дозу инсулина.",
            ],
          },
          { h2: "Непрерывный мониторинг глюкозы (CGM)" },
          { p: "CGM-сенсор (например, FreeStyle Libre, Dexcom, Linx, CareSense) измеряет глюкозу в подкожной ткани каждые 1–5 минут. Пациент видит уровень сахара в реальном времени на телефоне и получает оповещения при опасном повышении или понижении. Доказано, что CGM значительно снижает HbA1c, риск гипогликемии и улучшает качество жизни." },
          { h2: "Подсчёт углеводов" },
          { p: "Доза инсулина перед едой рассчитывается по количеству углеводов в пище. Медсестра по диабету учит считать: 1 хлебная единица = 10 г углеводов и определять индивидуальное соотношение инсулин-углеводы (например, 1 единица инсулина на 10 г съеденных углеводов). С этими знаниями и их применением в повседневной жизни пациент может есть практически всё, главное — правильно рассчитывать дозу." },
          { h2: "Движение и масса тела" },
          { p: "Регулярная физическая активность повышает чувствительность к инсулину и снижает необходимую дозу. Рекомендуется 150 минут умеренной аэробной нагрузки в неделю (например, быстрая ходьба, плавание, велосипед) и 2 раза в неделю силовая тренировка. Во время тренировок необходимо контролировать уровень сахара, так как он может быстро снизиться (особенно после длительной аэробной нагрузки)." },
          { h2: "Осложнения и как их предотвратить" },
          { p: "Длительно повышенный уровень сахара повреждает стенки сосудов. Наиболее частые осложнения:" },
          {
            ul: [
              "Ретинопатия (повреждение сетчатки) — может привести к слепоте",
              "Нефропатия (повреждение почек) — может потребовать заместительной почечной терапии",
              "Нейропатия (повреждение нервов) — вызывает боль, снижение чувствительности, язвы стоп",
              "Сердечно-сосудистые заболевания — риск инфаркта и инсульта в 2–3 раза выше, чем у людей без диабета",
            ],
          },
          { p: "Хорошая новость: если поддерживать HbA1c ниже 7% (в идеале 6,5%), а артериальное давление и холестерин под контролем, риск осложнений значительно снижается. Необходима дисциплина в питании и контроль сахара, регулярные анализы у эндокринолога и/или медсестры по диабету." },
          { h2: "Когда обратиться в нашу клинику?" },
          { p: "Если у вас диагностирован СД1 или вы подозреваете заболевание, запишитесь на приём в Таллиннскую клинику эндокринологии. В нашей клинике:" },
          {
            ul: [
              "Эндокринолог оценит ваше текущее состояние и скорректирует инсулинотерапию.",
              "Медсестра по диабету научит технике инъекций, подсчёту углеводов, установке и использованию CGM-сенсора.",
              "Будут назначены анализы и исследования: HbA1c, соотношение альбумин/креатинин, фото глазного дна.",
              "Измерят ABI-индекс для оценки кровоснабжения ног.",
            ],
          },
          {
            callout: {
              type: "info",
              title: "Резюме",
              body:
                "Сахарный диабет 1 типа — пожизненное аутоиммунное заболевание, требующее постоянной инсулинотерапии. Современное лечение (CGM + помпа или MDI), правильное питание и регулярный контроль позволяют жить полноценной и долгой жизнью без серьёзных осложнений.",
            },
          },
        ],
      },
      en: {
        category: "Diabetes",
        title: "Type 1 diabetes",
        imageCaption: "The immune system attacks the insulin-producing cells of the pancreas",
        summary:
          "Type 1 diabetes (T1D) is an autoimmune disease in which the body destroys its own insulin-producing beta cells in the pancreas. Insulin production stops and the patient needs lifelong insulin therapy. This article explains the mechanism, symptoms, diagnosis and modern treatment, and how to live a full life.",
        author: "Dr. Verner Fogel, endocrinologist",
        sections: [
          { h2: "What is type 1 diabetes?" },
          { p: "Type 1 diabetes is a chronic autoimmune disease in which the body's immune system mistakenly attacks the beta cells in the islets of Langerhans in the pancreas. These cells produce the hormone insulin, which is essential for transporting glucose (sugar) from the blood into the body's cells, where it is used for energy. Once the beta cells are destroyed, the body can no longer produce insulin — blood glucose rises and the patient needs lifelong insulin therapy." },
          { p: "Unlike type 2 diabetes, type 1 diabetes is not linked to lifestyle or excess weight. It often starts in childhood or young adulthood but can occur at any age. About 5,000 people in Estonia have a T1D diagnosis, and 200–300 new cases are added every year." },
          { h2: "Causes and risk factors" },
          { p: "The exact reason why the immune system starts attacking the body's own cells is still unknown. Researchers have discovered two main factors that together trigger the disease:" },
          {
            ul: [
              "Genetic predisposition — certain HLA gene variants in particular increase the risk by 10–20 times. Having a relative with the disease raises the risk, but most patients have no family history.",
              "Environmental factor — viral infections (e.g. enteroviruses, measles), early cow's milk exposure in infancy, vitamin D deficiency or changes in gut microbiota are thought to be triggers in genetically susceptible people.",
            ],
          },
          { p: "The disease develops slowly. The autoimmune process can start years before the first symptoms appear. By the time the condition is found through high blood sugar, 80–90% of the beta cells have usually already been destroyed." },
          { h2: "Symptoms" },
          { p: "T1D symptoms often develop rapidly — within a few weeks or months. The most typical are:" },
          {
            ul: [
              "Severe thirst (polydipsia) — the patient drinks several litres of water a day",
              "Frequent urination, especially at night (polyuria)",
              "Unexplained weight loss — often 5–10 kg in a few weeks despite a good appetite",
              "Constant fatigue and weakness",
              "Blurred vision",
              "Slow-healing wounds and frequent skin infections",
              "Acetone smell on the breath (already at an advanced stage)",
            ],
          },
          {
            callout: {
              type: "warning",
              title: "Emergency — ketoacidosis",
              body:
                "If the patient does not receive a timely diagnosis, diabetic ketoacidosis (DKA) can develop — a life-threatening condition with nausea, vomiting, abdominal pain, deep and rapid breathing and altered consciousness. In case of DKA, go to the emergency department immediately.",
            },
          },
          { h2: "Diagnosis" },
          { p: "A diagnosis of T1D is made based on the following tests:" },
          {
            ul: [
              "Fasting blood glucose ≥ 7.0 mmol/L",
              "Random blood glucose ≥ 11.1 mmol/L together with classic symptoms",
              "HbA1c (glycated haemoglobin) ≥ 6.5% — reflects average blood glucose over the past 2–3 months",
              "Oral glucose tolerance test (rarely needed) — 2-hour glucose ≥ 11.1 mmol/L after drinking 75 g of glucose",
            ],
          },
          { p: "To distinguish T1D from T2D, autoantibodies (GAD65, IA-2, ZnT8, IAA) and C-peptide are additionally measured. In T1D, autoantibodies are often positive and C-peptide is low (own insulin production is reduced or absent)." },
          { h2: "Treatment: a modern approach" },
          { p: "T1D cannot be cured. Treatment is based on insulin replacement and consists of the following components:" },
          { h2: "Insulin therapy" },
          { p: "Insulin can be administered in two ways:" },
          {
            ul: [
              "Multiple daily injections (MDI) — the patient injects long-acting basal insulin 1–2 times a day and rapid-acting bolus insulin before every meal (4–6 injections a day).",
              "Insulin pump — a small device that continuously delivers rapid-acting insulin throughout the day. The pump can be combined with continuous glucose monitoring (CGM) to create a closed-loop \"artificial pancreas\" that automatically adjusts the insulin dose.",
            ],
          },
          { h2: "Continuous glucose monitoring (CGM)" },
          { p: "A CGM sensor (e.g. FreeStyle Libre, Dexcom, Linx, CareSense) measures glucose in subcutaneous tissue every 1–5 minutes. The patient sees blood glucose in real time on the phone and receives alerts when it rises or falls to dangerous levels. CGM has been proven to significantly reduce HbA1c, hypoglycaemia risk and improve quality of life." },
          { h2: "Carbohydrate counting" },
          { p: "The pre-meal insulin dose is calculated from the carbohydrates in the meal. The diabetes nurse teaches you that 1 bread unit = 10 g of carbohydrate and how to determine your personal insulin-to-carb ratio (e.g. 1 unit of insulin per 10 g of carbs eaten). With this knowledge applied in everyday life, the patient can eat almost anything as long as the dose is calculated correctly." },
          { h2: "Exercise and body weight" },
          { p: "Regular physical activity improves insulin sensitivity and reduces the insulin dose needed. The recommendation is 150 minutes of moderate aerobic activity per week (e.g. brisk walking, swimming, cycling) and strength training twice a week. During exercise, blood glucose must be monitored as it can drop quickly (especially after prolonged aerobic activity)." },
          { h2: "Complications and how to prevent them" },
          { p: "Long-term high blood glucose damages the walls of blood vessels. The most common complications are:" },
          {
            ul: [
              "Retinopathy (damage to the retina) — can lead to blindness",
              "Nephropathy (kidney damage) — can lead to renal replacement therapy",
              "Neuropathy (nerve damage) — causes pain, reduced sensation, foot ulcers",
              "Cardiovascular disease — the risk of heart attack and stroke is 2–3 times higher than in non-diabetics",
            ],
          },
          { p: "Good to know: if HbA1c is kept below 7% (ideally 6.5%) and blood pressure and cholesterol are under control, the risk of complications is significantly reduced. This requires dietary discipline, blood glucose monitoring and regular check-ups with an endocrinologist and/or diabetes nurse." },
          { h2: "When to come to our clinic?" },
          { p: "If you have been diagnosed with T1D or suspect it, book an appointment at Tallinn Endocrinology Clinic. At our clinic:" },
          {
            ul: [
              "The endocrinologist will assess your current status and adjust your insulin therapy.",
              "The diabetes nurse will teach injection technique, carbohydrate counting and how to set up and use a CGM sensor.",
              "Tests and investigations will be ordered: HbA1c, albumin/creatinine ratio and retinal photography.",
              "The ABI index will be measured to assess blood flow in the legs.",
            ],
          },
          {
            callout: {
              type: "info",
              title: "Summary",
              body:
                "Type 1 diabetes is a lifelong autoimmune disease requiring continuous insulin therapy. Modern treatment (CGM + pump or MDI), proper nutrition and regular monitoring make it possible to live a full, long life without serious complications.",
            },
          },
        ],
      },
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "2-tuupi-diabeet",
    slug: "2-tuupi-diabeet",
    category: "Diabeet",
    title: "2. tüüpi diabeet",
    image: "/api/static/ai-images/articles/2-tuupi-diabeet.webp",
    imageCaption: "Insuliiniresistentsus ja glükoos ei saa minna rakkudesse",
    summary:
      "2. tüüpi diabeet (T2D) on kõige sagedasem diabeedi vorm — moodustab umbes 90% kõigist diabeedi juhtudest. Selle aluseks on insuliiniresistentsus ja järk-järgult kahanev insuliinitootmise võime. Käesolev artikkel selgitab haiguse tekkemehhanismi, riskitegureid, sümptomeid, diagnoosi ja kaasaegseid ravivõimalusi.",
    readingMinutes: 10,
    updated: "2026-06-10",
    author: DR_AUTHOR,
    sections: [
      { h2: "Mis on 2. tüüpi diabeet?" },
      {
        p: "2. tüüpi diabeet on krooniline ainevahetushaigus, mille puhul keha rakud muutuvad insuliini suhtes tundetuks (insuliiniresistentsus) ning kõhunääre ei suuda toota piisavalt insuliini, et seda kompenseerida. Vere glükoosisisaldus tõuseb pidevalt, kahjustades aja jooksul veresooni, närve ja siseorganeid.",
      },
      {
        p: "Eestis on 2. tüüpi diabeediga umbes 90 000 inimest ning hinnanguliselt 30 000–40 000 inimest haigestub, ilma et nad sellest teaksid. Maailmas on diabeedist saanud üks 21. sajandi suurimaid rahvatervise probleeme — haigete arv on viimase 30 aasta jooksul kolmekordistunud.",
      },
      { h2: "Riskitegurid" },
      {
        p: "2. tüüpi diabeet tekib geneetilise eelsoodumuse ja elustiilitegurite kombinatsioonil. Kõige olulisemad riskifaktorid on:",
      },
      {
        ul: [
          "Vanus üle 45 eluaasta",
          "Kõhupiirkonna rasvumine (vööümbermõõt > 94 cm meestel, > 80 cm naistel)",
          "Kehaline passiivsus — vähem kui 150 minutit aktiivset liikumist nädalas",
          "Lähisugulasel (vanemal, õel-vennal) on 2. tüüpi diabeet",
          "Naistel rasedusdiabeedi anamnees või polütsüstiliste munasarjade sündroom",
          "Kõrgenenud vererõhk (≥ 140/90 mmHg)",
          "Düslipideemia — madal HDL-kolesterool ja kõrge triglütseriidide tase",
          "Eelnevalt diagnoositud prediabeet (HbA1c 5,7–6,4% või tühja kõhu veresuhkur 5,6–6,9 mmol/L)",
        ],
      },
      { h2: "Patogenees: miks veresuhkur tõuseb?" },
      {
        p: "2. tüüpi diabeet areneb tavaliselt aastate jooksul kahe põhilise mehhanismi koostoimes:",
      },
      {
        ol: [
          "Insuliiniresistentsus — maksa-, lihas- ja rasvarakud ei reageeri insuliinile korralikult. Kõhunääre kompenseerib seda esialgu suurema insuliini tootmisega.",
          "Beetarakkude kurnatus — aja jooksul ei suuda kõhunääre enam piisavalt insuliini toota. Vere glükoosisisaldus hakkab tõusma ning diagnoositakse diabeet.",
        ],
      },
      {
        p: "Lisaks mängivad rolli ka teised mehhanismid: maksas suurenenud glükoosi tootmine/vabastamine, mao tühjenemise häirumine, soolehormoonide (GLP-1, GIP) tootmise häire ning ajus glükoosi taset reguleerivate mehhanismide häire. Sellepärast on ka ravi mitmekülgne ja keskendub mitmele mehhanismile korraga.",
      },
      { h2: "Sümptomid" },
      {
        p: "2. tüüpi diabeedi algus on hiiliv — sümptomid arenevad järk-järgult ja võivad jääda märkamatuks aastaid. Tüüpilisemad on:",
      },
      {
        ul: [
          "Suurenenud janu ja sage urineerimine",
          "Pidev väsimus",
          "Hägune nägemine",
          "Aeglaselt paranevad haavad, eriti jalgadel",
          "Sagedased nahainfektsioonid ja seenhaigused (eriti naistel pärmiseente vaginiit)",
          "Tundlikkuse vähenemine või kipitus jalgades (varajane neuropaatia)",
          "Tumedad nahalaigud kaela- ja kaenlaalustes (acanthosis nigricans)",
        ],
      },
      {
        p: "Paljudel patsientidel avastatakse 2. tüüpi diabeet rutiinanalüüside käigus või alles siis, kui ilmnevad tüsistused (südameinfarkt, jalahaavand, nägemise halvenemine). Selleks soovitamegi pärast 45. eluaastat lasta HbA1c kontrollida vähemalt iga 2–3 aasta tagant — riskigruppides igal aastal.",
      },
      { h2: "Diagnoosimine" },
      {
        p: "Diabeedi diagnoos pannakse ühe järgmise kriteeriumi alusel (kinnitatuna kahe eraldi analüüsiga, kui sümptomid puuduvad):",
      },
      {
        ul: [
          "HbA1c ≥ 6,5%",
          "Tühja kõhu veresuhkur ≥ 7,0 mmol/L",
          "2-tunni veresuhkur ≥ 11,1 mmol/L glükoosi tolerantsustestis (75 g glükoosijoogiga)",
          "Juhuslik veresuhkur ≥ 11,1 mmol/L koos klassikaliste sümptomitega",
        ],
      },
      {
        p: "Prediabeet (HbA1c 5,7–6,4%) tähendab, et veresuhkur on tavalisest kõrgem, kuid ei vasta veel diabeedi kriteeriumitele. Õigeaegne sekkumine — toitumise muutmine, kehakaalu langetamine 7–10% ulatuses ja regulaarne liikumine — võib diabeedi tekkimise edasi lükata aastate või kümnendite võrra.",
      },
      { h2: "Ravi: samm-sammult" },
      {
        p: "2. tüüpi diabeedi ravi on individualiseeritud, kuid järgib kindlat järjekorda:",
      },
      { h2: "1. Elustiilimuutused" },
      {
        p: "Toitumise korrastamine, regulaarne liikumine ja kehakaalu langetamine 5–10% ulatuses on igasuguse ravi alus. Mitmed uuringud (Look AHEAD, DiRECT) on näidanud, et oluline kehakaalu langus võib viia 2. tüüpi diabeedi täielikku remissiooni — eriti haiguse varases staadiumis.",
      },
      { h2: "2. Metformiin" },
      {
        p: "Metformiin on enamasti esmavaliku ravim. See vähendab maksas glükoosi tootmist/vabastamist ja parandab insuliinitundlikkust. On odav, ohutu ja sellel on isegi tõestatud südant kaitsev toime. Tüüpiline annus on 1500–2000 mg päevas.",
      },
      { h2: "3. Lisaravimid vastavalt patsiendi profiilile" },
      {
        ul: [
          "SGLT2-inhibiitorid (empagliflosiin, dapagliflosiin) — viivad glükoosi välja neerude kaudu, kaitsevad südant ja neere.",
          "GLP-1 agonistid (semaglutiid, liraglutiid, dulaglutiid) — süstitavad ravimid, mis aeglustavad mao tühjenemist, vähendavad söögiisu, langetavad veresuhkrut ja kehakaalu.",
          "DPP-4 inhibiitorid (sitagliptiin, linagliptiin) — tugevdavad keha enda GLP-1 toimet.",
          "Sulfonüüluurea (gliklasiid, glimepiriid) — stimuleerivad insuliini tootmist.",
          "Insuliin — kui muu ravi enam ei aita või beetarakkude funktsioon on oluliselt langenud.",
        ],
      },
      { h2: "Tüsistused" },
      {
        p: "Pikaajaline kõrge veresuhkur kahjustab veresooni nii suuri kui väikseid. Tüüpilisemad tüsistused:",
      },
      {
        ul: [
          "Mikrovaskulaarsed: retinopaatia, nefropaatia, neuropaatia",
          "Makrovaskulaarsed: südameinfarkt, insult, alajäsemete ateroskleroos",
          "Jalahaavandid ja diabeetiline jalg — võivad viia amputatsioonini",
          "Rasvmaks ja maksatsirroos",
          "Mitmesugused infektsioonid",
        ],
      },
      {
        p: "Sellepärast on diabeedi ravi mitmekülgne — lisaks veresuhkrule jälgime ka vererõhku, kolesterooli, neerufunktsiooni ja kaalu.",
      },
      { h2: "Regulaarne jälgimine: mida ja kui sageli?" },
      {
        ul: [
          "HbA1c — iga 3–6 kuu järel",
          "Vererõhk — kodus regulaarselt, eesmärk < 130/80 mmHg",
          "Lipiidid (LDL, HDL, triglütseriidid) — kord aastas",
          "Albumiini/kreatiniini suhe (mikroalbuminuuria) — kord aastas",
          "Silmapõhjade pildistamine — kord 1–2 aastas",
          "Jalgade kontroll (tundlikkus, ABI-indeks) — kord aastas",
          "Kehakaal ja vööümbermõõt — igal vastuvõtul",
        ],
      },
      { h2: "Millal pöörduda meie kliinikusse?" },
      {
        p: "Kui sul on diagnoositud 2. tüüpi diabeet või prediabeet, või kui tunned, et vajad professionaalset abi diabeedi juhtimisel, broneeri vastuvõtt Tallinna Endokrinoloogia Kliinikusse. Pakume:",
      },
      {
        ul: [
          "Endokrinoloogi vastuvõttu — ravi planeerimine ja kohandamine",
          "Diabeediõe konsultatsiooni — toitumis-, süsti- ja sensorinõustamine",
          "Silmapõhjade pildistamist ja ABI-indeksi mõõtmist kohapeal",
          "Glükoosi tolerantsustesti diagnoosi täpsustamiseks",
        ],
      },
    ],
    translations: {
      ru: {
        category: "Диабет",
        title: "Сахарный диабет 2 типа",
        imageCaption: "Инсулинорезистентность — глюкоза не может попасть в клетки",
        summary:
          "Сахарный диабет 2 типа (СД2) — самая распространённая форма диабета, на которую приходится около 90% всех случаев. В основе лежит инсулинорезистентность и постепенно снижающаяся способность к выработке инсулина. В этой статье объясняется механизм развития заболевания, факторы риска, симптомы, диагностика и современные методы лечения.",
        author: "Д-р Вернер Фогель, эндокринолог",
        sections: [
          { h2: "Что такое сахарный диабет 2 типа?" },
          { p: "Сахарный диабет 2 типа — это хроническое обменное заболевание, при котором клетки организма становятся нечувствительными к инсулину (инсулинорезистентность), а поджелудочная железа не может вырабатывать достаточное количество инсулина для компенсации. Уровень глюкозы в крови постоянно повышен и со временем повреждает сосуды, нервы и внутренние органы." },
          { p: "В Эстонии СД2 страдают около 90 000 человек, и по оценкам ещё 30 000–40 000 человек больны, не зная об этом. В мире диабет стал одной из крупнейших проблем общественного здравоохранения XXI века — число больных утроилось за последние 30 лет." },
          { h2: "Факторы риска" },
          { p: "Сахарный диабет 2 типа развивается при сочетании генетической предрасположенности и образа жизни. Наиболее важные факторы риска:" },
          {
            ul: [
              "Возраст старше 45 лет",
              "Абдоминальное ожирение (окружность талии > 94 см у мужчин, > 80 см у женщин)",
              "Низкая физическая активность — менее 150 минут активного движения в неделю",
              "Сахарный диабет 2 типа у близких родственников (родителей, братьев или сестёр)",
              "У женщин — гестационный диабет в анамнезе или синдром поликистозных яичников",
              "Повышенное артериальное давление (≥ 140/90 mmHg)",
              "Дислипидемия — низкий ЛПВП-холестерин и высокий уровень триглицеридов",
              "Ранее диагностированный преддиабет (HbA1c 5,7–6,4% или глюкоза натощак 5,6–6,9 ммоль/л)",
            ],
          },
          { h2: "Патогенез: почему повышается сахар?" },
          { p: "Сахарный диабет 2 типа обычно развивается годами в результате взаимодействия двух основных механизмов:" },
          {
            ol: [
              "Инсулинорезистентность — клетки печени, мышц и жировой ткани плохо реагируют на инсулин. Поджелудочная железа компенсирует это, вырабатывая больше инсулина.",
              "Истощение бета-клеток — со временем поджелудочная железа не может вырабатывать достаточно инсулина. Уровень глюкозы в крови начинает повышаться, и диагностируется диабет.",
            ],
          },
          { p: "Кроме того, играют роль и другие механизмы: повышенная выработка/высвобождение глюкозы в печени, нарушение опорожнения желудка, нарушение выработки кишечных гормонов (GLP-1, GIP) и нарушение механизмов регуляции уровня глюкозы в мозге. Поэтому лечение многокомпонентное и воздействует на несколько механизмов одновременно." },
          { h2: "Симптомы" },
          { p: "Начало СД2 скрытое — симптомы развиваются постепенно и могут оставаться незамеченными годами. Наиболее типичные:" },
          {
            ul: [
              "Усиленная жажда и частое мочеиспускание",
              "Постоянная усталость",
              "Расплывчатое зрение",
              "Медленно заживающие раны, особенно на ногах",
              "Частые кожные инфекции и грибковые заболевания (особенно у женщин — кандидозный вагинит)",
              "Снижение чувствительности или покалывание в ногах (ранняя нейропатия)",
              "Тёмные участки кожи на шее и в подмышечных впадинах (acanthosis nigricans)",
            ],
          },
          { p: "У многих пациентов СД2 выявляется при рутинных анализах или только при появлении осложнений (инфаркт миокарда, язва стопы, ухудшение зрения). Поэтому после 45 лет рекомендуется проверять HbA1c хотя бы раз в 2–3 года, а пациентам из групп риска — каждый год." },
          { h2: "Диагностика" },
          { p: "Диагноз диабета ставится на основании одного из следующих критериев (подтверждённого двумя независимыми анализами при отсутствии симптомов):" },
          {
            ul: [
              "HbA1c ≥ 6,5%",
              "Глюкоза крови натощак ≥ 7,0 ммоль/л",
              "Глюкоза через 2 часа ≥ 11,1 ммоль/л в тесте толерантности к глюкозе (с 75 г глюкозы)",
              "Случайная глюкоза крови ≥ 11,1 ммоль/л в сочетании с классическими симптомами",
            ],
          },
          { p: "Преддиабет (HbA1c 5,7–6,4%) означает, что уровень сахара повышен, но ещё не соответствует критериям диабета. Своевременное вмешательство — изменение питания, снижение веса на 7–10% и регулярное движение — может отсрочить развитие диабета на годы или десятилетия." },
          { h2: "Лечение: шаг за шагом" },
          { p: "Лечение СД2 индивидуально, но следует определённой последовательности:" },
          { h2: "1. Изменение образа жизни" },
          { p: "Упорядочение питания, регулярная физическая активность и снижение веса на 5–10% — основа любого лечения. Несколько исследований (Look AHEAD, DiRECT) показали, что значительное снижение веса может привести к полной ремиссии СД2 — особенно на ранней стадии заболевания." },
          { h2: "2. Метформин" },
          { p: "Метформин — это, как правило, препарат первого выбора. Он уменьшает выработку/высвобождение глюкозы в печени и улучшает чувствительность к инсулину. Дешёвый, безопасный и обладает даже доказанным кардиопротекторным действием. Типичная доза — 1500–2000 мг в сутки." },
          { h2: "3. Дополнительные препараты в зависимости от профиля пациента" },
          {
            ul: [
              "Ингибиторы SGLT2 (эмпаглифлозин, дапаглифлозин) — выводят глюкозу через почки, защищают сердце и почки.",
              "Агонисты GLP-1 (семаглутид, лираглутид, дулаглутид) — инъекционные препараты, замедляющие опорожнение желудка, снижающие аппетит, уровень сахара и вес.",
              "Ингибиторы DPP-4 (ситаглиптин, линаглиптин) — усиливают действие собственного GLP-1.",
              "Производные сульфонилмочевины (гликлазид, глимепирид) — стимулируют выработку инсулина.",
              "Инсулин — если другая терапия больше не помогает или функция бета-клеток значительно снижена.",
            ],
          },
          { h2: "Осложнения" },
          { p: "Длительно повышенный уровень сахара повреждает сосуды — как крупные, так и мелкие. Наиболее типичные осложнения:" },
          {
            ul: [
              "Микрососудистые: ретинопатия, нефропатия, нейропатия",
              "Макрососудистые: инфаркт миокарда, инсульт, атеросклероз нижних конечностей",
              "Язвы стоп и диабетическая стопа — могут привести к ампутации",
              "Жировая болезнь печени и цирроз",
              "Различные инфекции",
            ],
          },
          { p: "Поэтому лечение диабета многокомпонентное — кроме сахара мы контролируем артериальное давление, холестерин, функцию почек и вес." },
          { h2: "Регулярный контроль: что и как часто?" },
          {
            ul: [
              "HbA1c — каждые 3–6 месяцев",
              "Артериальное давление — регулярно дома, цель < 130/80 mmHg",
              "Липиды (ЛПНП, ЛПВП, триглицериды) — один раз в год",
              "Соотношение альбумин/креатинин (микроальбуминурия) — один раз в год",
              "Фото глазного дна — раз в 1–2 года",
              "Осмотр стоп (чувствительность, ABI-индекс) — один раз в год",
              "Вес и окружность талии — на каждом приёме",
            ],
          },
          { h2: "Когда обратиться в нашу клинику?" },
          { p: "Если у вас диагностирован СД2 или преддиабет, или вы чувствуете, что вам нужна профессиональная помощь в управлении диабетом, запишитесь на приём в Таллиннскую клинику эндокринологии. Мы предлагаем:" },
          {
            ul: [
              "Приём эндокринолога — планирование и коррекция лечения",
              "Консультация медсестры по диабету — питание, обучение инъекциям и установке сенсоров",
              "Фото глазного дна и измерение ABI-индекса на месте",
              "Тест толерантности к глюкозе для уточнения диагноза",
            ],
          },
        ],
      },
      en: {
        category: "Diabetes",
        title: "Type 2 diabetes",
        imageCaption: "Insulin resistance — glucose cannot enter the cells",
        summary:
          "Type 2 diabetes (T2D) is the most common form of diabetes — it accounts for about 90% of all diabetes cases. It is based on insulin resistance and a gradually decreasing ability to produce insulin. This article explains the disease mechanism, risk factors, symptoms, diagnosis and modern treatment options.",
        author: "Dr. Verner Fogel, endocrinologist",
        sections: [
          { h2: "What is type 2 diabetes?" },
          { p: "Type 2 diabetes is a chronic metabolic disease in which the body's cells become insensitive to insulin (insulin resistance), and the pancreas cannot produce enough insulin to compensate. Blood glucose is persistently elevated, gradually damaging blood vessels, nerves and internal organs." },
          { p: "About 90,000 people in Estonia have T2D, and an estimated 30,000–40,000 more are ill without knowing it. Worldwide, diabetes has become one of the greatest public health challenges of the 21st century — the number of patients has tripled over the past 30 years." },
          { h2: "Risk factors" },
          { p: "Type 2 diabetes develops from a combination of genetic predisposition and lifestyle factors. The most important risk factors are:" },
          {
            ul: [
              "Age over 45 years",
              "Abdominal obesity (waist circumference > 94 cm in men, > 80 cm in women)",
              "Physical inactivity — less than 150 minutes of active exercise per week",
              "A close relative (parent, sibling) with type 2 diabetes",
              "In women — history of gestational diabetes or polycystic ovary syndrome",
              "Elevated blood pressure (≥ 140/90 mmHg)",
              "Dyslipidaemia — low HDL cholesterol and high triglycerides",
              "Previously diagnosed prediabetes (HbA1c 5.7–6.4% or fasting glucose 5.6–6.9 mmol/L)",
            ],
          },
          { h2: "Pathogenesis: why does blood sugar rise?" },
          { p: "Type 2 diabetes usually develops over years through the interaction of two main mechanisms:" },
          {
            ol: [
              "Insulin resistance — liver, muscle and fat cells fail to respond properly to insulin. The pancreas initially compensates by producing more insulin.",
              "Beta-cell exhaustion — over time the pancreas can no longer produce enough insulin. Blood glucose starts to rise and diabetes is diagnosed.",
            ],
          },
          { p: "Other mechanisms also play a role: increased glucose production/release by the liver, impaired gastric emptying, disturbed gut hormone (GLP-1, GIP) secretion and impaired brain mechanisms regulating glucose levels. That is why treatment is multifaceted and targets several mechanisms at once." },
          { h2: "Symptoms" },
          { p: "The onset of T2D is insidious — symptoms develop gradually and can go unnoticed for years. The most typical are:" },
          {
            ul: [
              "Increased thirst and frequent urination",
              "Constant fatigue",
              "Blurred vision",
              "Slow-healing wounds, especially on the feet",
              "Frequent skin infections and fungal diseases (in women especially yeast vaginitis)",
              "Reduced sensation or tingling in the feet (early neuropathy)",
              "Dark patches of skin on the neck and armpits (acanthosis nigricans)",
            ],
          },
          { p: "In many patients T2D is found through routine tests or only when complications appear (heart attack, foot ulcer, vision loss). For this reason, we recommend checking HbA1c every 2–3 years after the age of 45 — annually in high-risk groups." },
          { h2: "Diagnosis" },
          { p: "A diagnosis of diabetes is made based on one of the following criteria (confirmed by two separate tests if symptoms are absent):" },
          {
            ul: [
              "HbA1c ≥ 6.5%",
              "Fasting blood glucose ≥ 7.0 mmol/L",
              "2-hour blood glucose ≥ 11.1 mmol/L in an oral glucose tolerance test (75 g glucose drink)",
              "Random blood glucose ≥ 11.1 mmol/L together with classic symptoms",
            ],
          },
          { p: "Prediabetes (HbA1c 5.7–6.4%) means blood sugar is higher than normal but does not yet meet diabetes criteria. Timely intervention — diet change, 7–10% weight loss and regular exercise — can delay the onset of diabetes by years or decades." },
          { h2: "Treatment: step by step" },
          { p: "T2D treatment is individualised but follows a defined sequence:" },
          { h2: "1. Lifestyle changes" },
          { p: "Improving diet, regular exercise and 5–10% weight loss are the foundation of every treatment. Several studies (Look AHEAD, DiRECT) have shown that significant weight loss can lead to complete remission of T2D — especially in the early stages." },
          { h2: "2. Metformin" },
          { p: "Metformin is usually the first-line drug. It reduces glucose production/release in the liver and improves insulin sensitivity. It is inexpensive, safe and even has a proven cardioprotective effect. Typical dose is 1500–2000 mg per day." },
          { h2: "3. Additional drugs based on patient profile" },
          {
            ul: [
              "SGLT2 inhibitors (empagliflozin, dapagliflozin) — eliminate glucose through the kidneys and protect the heart and kidneys.",
              "GLP-1 agonists (semaglutide, liraglutide, dulaglutide) — injectable drugs that slow gastric emptying, reduce appetite, lower blood sugar and body weight.",
              "DPP-4 inhibitors (sitagliptin, linagliptin) — strengthen the body's own GLP-1 effect.",
              "Sulfonylureas (gliclazide, glimepiride) — stimulate insulin production.",
              "Insulin — when other therapy is no longer effective or beta-cell function has significantly declined.",
            ],
          },
          { h2: "Complications" },
          { p: "Long-term high blood sugar damages both large and small blood vessels. The most common complications are:" },
          {
            ul: [
              "Microvascular: retinopathy, nephropathy, neuropathy",
              "Macrovascular: heart attack, stroke, lower-limb atherosclerosis",
              "Foot ulcers and diabetic foot — can lead to amputation",
              "Fatty liver and liver cirrhosis",
              "Various infections",
            ],
          },
          { p: "That is why diabetes care is multifaceted — in addition to blood sugar we also monitor blood pressure, cholesterol, kidney function and weight." },
          { h2: "Regular monitoring: what and how often?" },
          {
            ul: [
              "HbA1c — every 3–6 months",
              "Blood pressure — measured regularly at home, target < 130/80 mmHg",
              "Lipids (LDL, HDL, triglycerides) — once a year",
              "Albumin/creatinine ratio (microalbuminuria) — once a year",
              "Retinal photography — every 1–2 years",
              "Foot check (sensation, ABI index) — once a year",
              "Body weight and waist circumference — at every visit",
            ],
          },
          { h2: "When to come to our clinic?" },
          { p: "If you have been diagnosed with T2D or prediabetes, or feel that you need professional help managing diabetes, book an appointment at Tallinn Endocrinology Clinic. We offer:" },
          {
            ul: [
              "Endocrinologist consultation — treatment planning and adjustment",
              "Diabetes nurse consultation — nutrition, injection and sensor guidance",
              "Retinal photography and ABI index measurement on-site",
              "Oral glucose tolerance test to refine the diagnosis",
            ],
          },
        ],
      },
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "diabeetiline-retinopaatia",
    slug: "diabeetiline-retinopaatia",
    category: "Diabeedi tüsistused",
    title: "Diabeetiline retinopaatia",
    image: "/api/static/ai-images/articles/diabeetiline-retinopaatia.webp",
    imageCaption: "Silmapõhjadesse tekivad verevalumid",
    summary:
      "Retinopaatia on silmavõrkkesta veresoonte kahjustus, mis tekib pikaajalise kõrge veresuhkru toimel. See on tööealiste täiskasvanute kõige sagedasem pimedaks jäämise põhjus arenenud riikides. Kuid see on ennetatav ning ravitav, väga palju sõltub patsiendist.",
    readingMinutes: 7,
    updated: "2026-06-13",
    author: DR_AUTHOR,
    sections: [
      { h2: "Mis on diabeetiline retinopaatia?" },
      {
        p: "Silma võrkkest (reetina) on kihid valgustundlikke rakke, mis kannavad nägemissignaali ajju. Reetina verevarustus toimub väikeste veresoonte kaudu. Pikaajaliselt kõrge veresuhkur kahjustab nende veresoonte seinu — need muutuvad lekkivaks, ahenevad ja võivad rebeneda. Tulemuseks on nägemise hägunemine, väikesed mustad täpid (\"hõljujad\") nägemisväljas ning pikemas perspektiivis täielik nägemise kaotus.",
      },
      {
        p: "Statistika: kõigil 20+ aastat diabeeti põdenutel on tuvastatav retinopaatia. 1. tüüpi diabeediga patsientidel ilmnevad esimesed muutused tüüpiliselt 5 aasta möödumisel diagnoosist, 2. tüüpi diabeedi puhul võivad muutused olla juba diagnoosi hetkel olemas.",
      },
      { h2: "Etapid" },
      {
        p: "Retinopaatia areneb järjest, samm-sammult:",
      },
      {
        ol: [
          "Mitteproliferatiivne retinopaatia (NPDR) — varased muutused. Veresoonte seintes tekivad väikesed laienemised (mikroaneurüsmid), võivad ilmuda väikesed verevalumid ja kollased laigud (eksudaadid). Nägemine on sageli normaalne.",
          "Mõõdukas NPDR — rohkem mikroaneurüsme ja verevalumeid, mõned veresooned ummistuvad.",
          "Raske NPDR — paljud veresooned ummistunud. Reetina osad jäävad ilma verevarustuseta (isheemia). Nägemine võib veel olla normaalne, kuid risk nägemise kaotamiseks on suur.",
          "Proliferatiivne retinopaatia (PDR) — isheemia stimuleerib uute, ebanormaalsete veresoonte kasvu (neovaskularisatsioon). Need uued veresooned on rabedad ning võivad rebeneda, põhjustades verejooksu klaaskehasse ning lõpuks võrkkesta irdumise (võrkkest tuleb lahti silmapõhjast).",
          "Diabeetiline makulaarne ödeem (DME) — vedeliku kogunemine reetina keskosas (maakulas), mis vastutab terava nägemise eest. Võib tekkida igas retinopaatia etapis ja on kõige sagedasem nägemiskaotuse põhjus.",
        ],
      },
      { h2: "Sümptomid" },
      {
        p: "Retinopaatia puhul on tähtis teada, et varased etapid on täiesti sümptomivabad. Patsient ei tunne ega näe midagi ebatavalist, kuigi võrkkest on juba kahjustatud. Kui sümptomid ilmuvad, on need:",
      },
      {
        ul: [
          "Hägune või moonutatud kesknägemine (DME tunnus)",
          "Mustad täpid või \"hõljumid\" nägemisväljas (klaaskehasse tunginud veri)",
          "Tumedad alad nägemisväljas",
          "Vähenenud öine nägemine",
          "Värvide tajumise muutused",
          "Järsk osaline või täielik nägemise kaotus (raske verejooks või võrkkesta irdumine — nõuab kiiret tegutsemist)",
        ],
      },
      {
        callout: {
          type: "warning",
          title: "Tähelepanu!",
          body:
            "Selleks ajaks, kui ilmnevad esimesed nägemissümptomid, on retinopaatia juba kaugele arenenud. Sellepärast on regulaarne silmapõhjade kontroll iga diabeedihaige jaoks elulise tähtsusega.",
        },
      },
      { h2: "Diagnoosimine" },
      {
        p: "Retinopaatia diagnoosimiseks on kaks põhilist meetodit:",
      },
      {
        ul: [
          "Silmapõhjade pildistamine (fundoskoopia) — pildistame võrkkesta digitaalse kaameraga ning hindame mikroaneurüsme, verevalumeid, ekssudaate ja uuskasvanud veresooni. See meetod sobib hästi sõeluuringuks ja meil saab seda teha kohapeal vastuvõtu ajal.",
          "Optiline koherentstomograafia (OCT) — kasutab valgusega skaneerimist, et saada võrkkesta läbilõike kujutist. Eriti hea makulaarse ödeemi diagnoosimiseks. Tehakse silmaarsti juures.",
        ],
      },
      {
        p: "Sõltuvalt diabeedi tüübist ja kestusest soovitame:",
      },
      {
        ul: [
          "1. tüüpi diabeet: esimene silmapõhja kontroll 5 aasta pärast diagnoosi, seejärel kord aastas.",
          "2. tüüpi diabeet: kontroll kohe diagnoosi hetkel ning seejärel kord aastas (või sagedamini, kui leitakse muutusi).",
          "Rasedusdiabeet või rasedaks jäämine diabeetikul: iga 3 kuu järel.",
        ],
      },
      { h2: "Ravi" },
      {
        p: "Ravi sõltub etapist:",
      },
      {
        ul: [
          "Varased etapid: langetada veresuhkru taset, vererõhku ja kolesterooli. Sageli ei vaja muud ravi, vaid regulaarne jälgimine.",
          "Raske NPDR ja PDR: laser-fotokoagulatsioon (laseriga \"ärapõletatakse\" isheemilised osad reetinas, et peatada uuskasvanud veresoonte teke). Tehakse silmaarsti juures.",
          "Diabeetiline makulaarne ödeem: anti-VEGF süstid silmakehasse (ranibizumab, aflibercept, brolucizumab) — vähendavad turset ja parandavad nägemist. Tehakse silmaarsti juures.",
          "Klaaskehas verevalum või võrkkesta irdumine: vitrektoomia — operatsioon, kus klaaskeha eemaldatakse ja võrkkest taastatakse.",
        ],
      },
      { h2: "Kuidas ennetada?" },
      {
        p: "Retinopaatia ennetamine on lihtne — kuid nõuab pidevat tähelepanu:",
      },
      {
        ul: [
          "Hoia HbA1c eesmärgi piires (< 7%, ideaalne 6,5%)",
          "Kontrolli vererõhk alla 130/80 mmHg",
          "Hoia LDL-kolesterool < 2,6 mmol/L (madalam, kui on juba olemas tüsistused)",
          "Loobu suitsetamisest — suitsetamine kiirendab retinopaatia teket",
          "Käi regulaarselt silmapõhjade kontrollis.",
        ],
      },
      { h2: "Meie kliinikus" },
      {
        p: "Tallinna Endokrinoloogia Kliinikus teostame silmapõhjade pildistamise kohapeal vastuvõtu ajal. Pakume seda teenust nii Tervisekassaga (saatekirjaga, paketis) kui ka tasuliselt (50 €/silm või paketis 110 €: visiit + HbA1c + silmapõhjade pildistamine). Kui leiame muutusi, suuname sind koheselt silmaarsti juurde.",
      },
    ],
    translations: {
      ru: {
        category: "Осложнения диабета",
        title: "Диабетическая ретинопатия",
        imageCaption: "На глазном дне появляются кровоизлияния",
        summary:
          "Ретинопатия — это поражение сосудов сетчатки глаза, возникающее под действием длительно повышенного уровня сахара в крови. В развитых странах это самая частая причина слепоты у людей трудоспособного возраста. Однако заболевание можно предотвратить и лечить — многое зависит от самого пациента.",
        author: "Д-р Вернер Фогель, эндокринолог",
        sections: [
          { h2: "Что такое диабетическая ретинопатия?" },
          { p: "Сетчатка глаза (ретина) — это слои светочувствительных клеток, которые передают зрительный сигнал в мозг. Кровоснабжение сетчатки осуществляется через мелкие сосуды. Длительно повышенный уровень сахара повреждает стенки этих сосудов — они становятся проницаемыми, сужаются и могут разрываться. В результате возникает помутнение зрения, появляются мелкие тёмные точки («плавающие мушки») в поле зрения, а в долгосрочной перспективе — полная потеря зрения." },
          { p: "Статистика: у всех, кто болеет диабетом более 20 лет, выявляется ретинопатия. У пациентов с СД1 первые изменения обычно появляются через 5 лет после постановки диагноза, а при СД2 изменения могут присутствовать уже на момент диагностики." },
          { h2: "Стадии" },
          { p: "Ретинопатия развивается поэтапно:" },
          {
            ol: [
              "Непролиферативная ретинопатия (NPDR) — ранние изменения. В стенках сосудов появляются небольшие расширения (микроаневризмы), могут возникать мелкие кровоизлияния и жёлтые пятна (экссудаты). Зрение часто остаётся нормальным.",
              "Умеренная NPDR — больше микроаневризм и кровоизлияний, некоторые сосуды закупориваются.",
              "Тяжёлая NPDR — закупорено множество сосудов. Часть сетчатки остаётся без кровоснабжения (ишемия). Зрение может ещё быть нормальным, но риск его потери высок.",
              "Пролиферативная ретинопатия (PDR) — ишемия стимулирует рост новых, аномальных сосудов (неоваскуляризация). Эти новые сосуды хрупкие и могут разрываться, вызывая кровоизлияние в стекловидное тело и в конечном итоге отслойку сетчатки (сетчатка отделяется от глазного дна).",
              "Диабетический макулярный отёк (DME) — скопление жидкости в центральной части сетчатки (макуле), отвечающей за чёткое зрение. Может развиться на любой стадии ретинопатии и является самой частой причиной потери зрения.",
            ],
          },
          { h2: "Симптомы" },
          { p: "При ретинопатии важно понимать, что ранние стадии полностью бессимптомны. Пациент ничего не ощущает и не видит ничего необычного, хотя сетчатка уже повреждена. При появлении симптомов они таковы:" },
          {
            ul: [
              "Размытое или искажённое центральное зрение (признак DME)",
              "Тёмные точки или «плавающие мушки» в поле зрения (кровь, попавшая в стекловидное тело)",
              "Тёмные участки в поле зрения",
              "Снижение ночного зрения",
              "Изменение восприятия цветов",
              "Внезапная частичная или полная потеря зрения (тяжёлое кровоизлияние или отслойка сетчатки — требует немедленного обращения)",
            ],
          },
          {
            callout: {
              type: "warning",
              title: "Внимание!",
              body:
                "К тому моменту, когда появляются первые зрительные симптомы, ретинопатия уже далеко зашла. Поэтому регулярный осмотр глазного дна жизненно важен для каждого пациента с диабетом.",
            },
          },
          { h2: "Диагностика" },
          { p: "Для диагностики ретинопатии используются два основных метода:" },
          {
            ul: [
              "Фотография глазного дна (фундоскопия) — снимаем сетчатку цифровой камерой и оцениваем микроаневризмы, кровоизлияния, экссудаты и новообразованные сосуды. Метод хорошо подходит для скрининга и проводится у нас на месте во время приёма.",
              "Оптическая когерентная томография (OCT) — сканирование светом для получения послойного изображения сетчатки. Особенно хороша для диагностики макулярного отёка. Проводится у офтальмолога.",
            ],
          },
          { p: "В зависимости от типа и длительности диабета рекомендуем:" },
          {
            ul: [
              "СД 1 типа: первый осмотр глазного дна через 5 лет после постановки диагноза, затем раз в год.",
              "СД 2 типа: осмотр сразу при постановке диагноза, затем раз в год (или чаще, если выявлены изменения).",
              "Гестационный диабет или беременность у пациентки с диабетом: каждые 3 месяца.",
            ],
          },
          { h2: "Лечение" },
          { p: "Лечение зависит от стадии:" },
          {
            ul: [
              "Ранние стадии: снижение уровня сахара, артериального давления и холестерина. Чаще всего другого лечения не требуется, только регулярное наблюдение.",
              "Тяжёлая NPDR и PDR: лазерная фотокоагуляция (при лазерной коагуляции «прижигаются» ишемические участки сетчатки, чтобы замедлить или остановить рост новых сосудов). Проводится у офтальмолога.",
              "Диабетический макулярный отёк: интравитреальные инъекции анти-VEGF (ранибизумаб, афлиберцепт, бролуцизумаб) — уменьшают отёк и улучшают зрение. Проводятся у офтальмолога.",
              "Кровоизлияние в стекловидное тело или отслойка сетчатки: витрэктомия — операция, при которой стекловидное тело удаляется, а сетчатка восстанавливается.",
            ],
          },
          { h2: "Как предотвратить?" },
          { p: "Профилактика ретинопатии проста, но требует постоянного внимания:" },
          {
            ul: [
              "Поддерживайте HbA1c в целевом диапазоне (< 7%, идеально 6,5%)",
              "Контролируйте артериальное давление — должно быть ниже 130/80 mmHg",
              "Удерживайте ЛПНП-холестерин < 2,6 ммоль/л (ниже, если уже есть осложнения)",
              "Откажитесь от курения — курение ускоряет развитие ретинопатии",
              "Регулярно проходите осмотр глазного дна.",
            ],
          },
          { h2: "В нашей клинике" },
          { p: "В Таллиннской клинике эндокринологии мы выполняем фото глазного дна на месте во время приёма. Услугу предлагаем как с медицинской страховкой (по направлению, в составе пакета), так и платно (50 € за оба глаза) или в пакете 110 € (визит + HbA1c + фото глазного дна). Если обнаруживаем изменения, сразу направляем вас к офтальмологу." },
        ],
      },
      en: {
        category: "Diabetes complications",
        title: "Diabetic retinopathy",
        imageCaption: "Haemorrhages appear on the retina",
        summary:
          "Retinopathy is damage to the blood vessels of the retina caused by long-term high blood glucose. In developed countries, it is the most common cause of blindness in working-age adults. However, it is preventable and treatable — and a great deal depends on the patient.",
        author: "Dr. Verner Fogel, endocrinologist",
        sections: [
          { h2: "What is diabetic retinopathy?" },
          { p: "The retina is a layer of light-sensitive cells that carries the visual signal to the brain. The retina is supplied by tiny blood vessels. Long-term high blood glucose damages the walls of these vessels — they become leaky, narrow and may rupture. The result is blurred vision, small dark spots (\"floaters\") in the visual field and, over time, complete vision loss." },
          { p: "Statistics: everyone who has had diabetes for 20+ years has detectable retinopathy. In patients with type 1 diabetes the first changes typically appear 5 years after diagnosis, while in type 2 diabetes changes may already be present at the time of diagnosis." },
          { h2: "Stages" },
          { p: "Retinopathy develops step by step:" },
          {
            ol: [
              "Non-proliferative retinopathy (NPDR) — early changes. Tiny bulges form in vessel walls (microaneurysms), small haemorrhages and yellow spots (exudates) may appear. Vision is often normal.",
              "Moderate NPDR — more microaneurysms and haemorrhages, some vessels are occluded.",
              "Severe NPDR — many vessels are blocked. Parts of the retina lose their blood supply (ischaemia). Vision may still be normal but the risk of vision loss is high.",
              "Proliferative retinopathy (PDR) — ischaemia stimulates the growth of new, abnormal vessels (neovascularisation). These new vessels are fragile and can rupture, causing vitreous haemorrhage and, eventually, retinal detachment (the retina separates from the back of the eye).",
              "Diabetic macular oedema (DME) — accumulation of fluid in the central part of the retina (macula), which is responsible for sharp vision. It can occur at any stage of retinopathy and is the most common cause of vision loss.",
            ],
          },
          { h2: "Symptoms" },
          { p: "An important point about retinopathy: the early stages are completely symptom-free. The patient feels and sees nothing unusual, even though the retina is already damaged. When symptoms do appear, they are:" },
          {
            ul: [
              "Blurred or distorted central vision (a sign of DME)",
              "Black spots or \"floaters\" in the visual field (blood that has leaked into the vitreous)",
              "Dark patches in the visual field",
              "Reduced night vision",
              "Changes in colour perception",
              "Sudden partial or total vision loss (severe haemorrhage or retinal detachment — requires immediate medical attention)",
            ],
          },
          {
            callout: {
              type: "warning",
              title: "Attention!",
              body:
                "By the time the first visual symptoms appear, retinopathy is already advanced. That is why regular retinal screening is vital for every patient with diabetes.",
            },
          },
          { h2: "Diagnosis" },
          { p: "There are two main methods for diagnosing retinopathy:" },
          {
            ul: [
              "Retinal photography (fundoscopy) — we photograph the retina with a digital camera and assess microaneurysms, haemorrhages, exudates and neovascular vessels. The method is well-suited for screening and we offer it on-site during your appointment.",
              "Optical coherence tomography (OCT) — uses light scanning to produce a cross-sectional image of the retina. Especially useful for diagnosing macular oedema. Performed by an ophthalmologist.",
            ],
          },
          { p: "Depending on the type and duration of diabetes, we recommend:" },
          {
            ul: [
              "Type 1 diabetes: first retinal exam 5 years after diagnosis, then once a year.",
              "Type 2 diabetes: exam immediately at diagnosis and then once a year (or more often if changes are found).",
              "Gestational diabetes or pregnancy in a diabetic patient: every 3 months.",
            ],
          },
          { h2: "Treatment" },
          { p: "Treatment depends on the stage:" },
          {
            ul: [
              "Early stages: lower blood glucose, blood pressure and cholesterol. Often no other treatment is needed, just regular monitoring.",
              "Severe NPDR and PDR: laser photocoagulation (laser coagulation \"burns\" ischaemic areas of the retina to slow or stop the growth of new vessels). Performed by an ophthalmologist.",
              "Diabetic macular oedema: intravitreal anti-VEGF injections (ranibizumab, aflibercept, brolucizumab) — reduce swelling and improve vision. Performed by an ophthalmologist.",
              "Vitreous haemorrhage or retinal detachment: vitrectomy — surgery in which the vitreous is removed and the retina is reattached.",
            ],
          },
          { h2: "How to prevent it?" },
          { p: "Preventing retinopathy is simple — but requires constant attention:" },
          {
            ul: [
              "Keep HbA1c within target (< 7%, ideally 6.5%)",
              "Control blood pressure — should stay below 130/80 mmHg",
              "Keep LDL cholesterol < 2.6 mmol/L (lower if complications are already present)",
              "Stop smoking — smoking accelerates retinopathy",
              "Attend regular retinal screening.",
            ],
          },
          { h2: "At our clinic" },
          { p: "At Tallinn Endocrinology Clinic we perform retinal photography on-site during the appointment. The service is available both via Estonian Health Insurance (with referral, as part of a package) and as a paid service (€50 for both eyes) or in a package for €110 (visit + HbA1c + retinal photography). If we find changes, we refer you to an ophthalmologist immediately." },
        ],
      },
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "diabeetiline-neuropaatia",
    slug: "diabeetiline-neuropaatia",
    category: "Diabeedi tüsistused",
    title: "Diabeetiline neuropaatia",
    image: "/api/static/ai-images/articles/diabeetiline-neuropaatia.webp",
    summary:
      "Diabeetiline neuropaatia on närvide kahjustus, mis tekib pikaajaliselt kõrge veresuhkru toimel. See on kõige sagedasem diabeedi tüsistus — vähemalt pooled diabeedi all kannatajad kogevad neuropaatiat oma elu jooksul. Sümptomid varieeruvad jalgade kipitusest ja valust kuni seedeprobleemideni.",
    readingMinutes: 8,
    updated: "2026-06-10",
    author: DR_AUTHOR,
    sections: [
      { h2: "Mis on diabeetiline neuropaatia?" },
      {
        p: "Närvid edastavad signaale aju ja keha ülejäänud osade vahel — annavad edasi signaale lihaste kokkutõmbeks ja lõõgastumiseks, edastavad tundlikkust nahalt, reguleerivad siseorganite tööd. Diabeedi puhul kahjustab kõrge veresuhkur närve kahel viisil: 1) otseselt rikub närvirakkude ainevahetust ja 2) kahjustab närve toitvaid väikseid veresooni. Tulemuseks on närvide \"isolatsiooni\" (müeliini) kahjustus ja signaalide ülekande häired.",
      },
      {
        p: "Neuropaatia võib avalduda erinevatel viisidel sõltuvalt sellest, milliseid närve on kahjustatud:",
      },
      {
        ol: [
          "Perifeerne neuropaatia — kõige sagedasem. Kahjustatud on käte ja jalgade närvid.",
          "Autonoomne neuropaatia — kahjustatud on siseorganeid juhtivad närvid (süda, magu, sooled, põis, suguelundid).",
          "Fokaalne neuropaatia — üks konkreetne närv on järsku kahjustatud (näiteks näonärv, randmenärv).",
          "Proksimaalne neuropaatia (diabeetiline amüotroofia) — harvem, mõjutab puusa- ja reielihaseid.",
        ],
      },
      { h2: "Perifeerne neuropaatia: kõige sagedasem vorm" },
      {
        p: "Algab tavaliselt varvastest ja jalataldadest, levib aja jooksul üles säärte poole, hiljem ka kätele. Sümptomid on:",
      },
      {
        ul: [
          "Kipitus, torkimine või põletustunne",
          "Tuimus või tundlikkuse vähenemine",
          "Tugev valu, eriti öösel (võib segada und)",
          "Liigne tundlikkus puudutusele (voodilina võib tunduda valusana)",
          "Jalgades nõrkus, koordinatsioonihäired",
          "Lihaste atrofeerumine",
        ],
      },
      {
        callout: {
          type: "warning",
          title: "Miks neuropaatia on ohtlik?",
          body:
            "Tundlikkuse kaotus tähendab, et patsient ei pruugi tunda väikest haava või villi jalal. Neuropaatia koos halva verevarustusega (jalad ei parane hästi) viib diabeetilise jala tekkeni — haavandid, mis võivad vajada amputatsiooni. Eestis tehakse igal aastal sadu diabeediga seotud jala-amputatsioone, suurem osa neist on välditavad korras veresuhkrute ja regulaarse kontrolliga.",
        },
      },
      { h2: "Autonoomne neuropaatia" },
      {
        p: "Autonoomne närvisüsteem juhib kõike, mille üle me teadlikult ei mõtle: südamerütm, vererõhk, seedimine, urineerimine, higistamine. Autonoomse neuropaatia sümptomid sõltuvad sellest, milliseid närve on kahjustatud:",
      },
      {
        ul: [
          "Süda ja veresooned: pulsi tõusu puudumine pingutusel, vererõhu järsk langus püsti tõustes (ortostaatiline hüpotensioon), \"vaikne\" südameinfarkt ilma rinnavaluta",
          "Seedetrakt: gastroparees (mao aeglane tühjenemine — täiskõhutunne, iiveldus pärast väikseid söögikordi), kõhukinnisus või -lahtisus.",
          "Põis: võimetus tunda täis põit, halvab tühjenemine, sagedased kuseteede infektsioonid.",
          "Suguelundid: erektsioonihäired meestel, kuiv tupp ja libiido langus naistel.",
          "Higistamine: liigne higistamine söömise ajal (gustatoorne higistamine) või vastupidi, higistamise vähenemine alajäsemetes",
          "Hüpoglükeemia tunde kadumine — kaod hoiatussümptomid (värin, higistamine) madalast veresuhkrust",
        ],
      },
      { h2: "Diagnoosimine" },
      {
        p: "Neuropaatia diagnoos põhineb sümptomite, kliinilise läbivaatuse ja täiendavate uuringute kombinatsioonist:",
      },
      {
        ul: [
          "Monofilamenttest — 10g monofilamenti puudutame jalataldu erinevates kohtades. Kui patsient ei tunne, on tundlikkus oluliselt vähenenud.",
          "Vibratsioonitundlikkuse hindamine.",
          "Refleksid — Achilleuse kõõluse refleks väheneb sageli esimesena.",
          "ABI-indeks — hindab, kas tundlikkuse häire on seotud halva verevarustusega.",
          "Elektroneuromüograafia (ENMG) — täpsem närvifunktsiooni mõõtmine, tehakse neuroloogi juures.",
          "Autonoomse neuropaatia hindamiseks: ortostaatiline test, südamerütmi varieerumine, mao tühjenemise hindamine vajaduse korral.",
        ],
      },
      { h2: "Ravi" },
      {
        p: "Neuropaatia on raskesti tagasipööratav, kuid sümptomeid saab leevendada ning haiguse süvenemist peatada. Ravi koosneb kolmest tasandist:",
      },
      { h2: "1. Põhihaiguse ravi" },
      {
        p: "Mida paremini on veresuhkur kontrolli all, seda aeglasemalt neuropaatia areneb. Kõigi seonduvate riskitegurite (vererõhk, kolesterool, suitsetamine, alkohol) ravi on samuti oluline.",
      },
      { h2: "2. Sümptomite leevendamine" },
      {
        ul: [
          "Neuropaatiline valu: pregabaliin, gabapentiin, duloksetiin, amitriptüliin. Tavalised valuvaigistid (ibuprofeen, paratsetamool) üldjuhul ei aita.",
          "Lokaalsed kreemid: kapsaitsiin, lidokaiinplaastrid",
          "Autonoomne neuropaatia: vererõhuravimid, gastropareesi ravi (metoklopramiid, dieedimuutused — väikesed sagedased toidukorrad), erektsioonihäirete ravi (PDE5 inhibiitorid)",
          "Hüpoglükeemia tundlikkuse taastamiseks võib aidata pidev glükoosimonitooring (CGM).",
        ],
      },
      { h2: "3. Jalgade hooldus" },
      {
        p: "See on kõige tähtsam osa neuropaatia ravis — ennetab amputatsiooni:",
      },
      {
        ul: [
          "Kontrolli oma jalgu IGAL päeval — vaata, kas on uusi haavu, ville, punetust, kallust või küünte muutusi.",
          "Pese jalad iga päev leige veega, kuivata hoolega, eriti varvastevahed.",
          "Ära käi paljajalu, ka kodus.",
          "Kanna pehmeid, hingavaid sokke ja mugavaid jalanõusid. Lõika küüsi otse, mitte ümaralt.",
          "Käi regulaarselt jalaõe juures ravipediküüris — meie kliinikus 40 € (tasuline) või tasuta Tervisekassa saatekirjaga.",
          "Iga haavandi puhul pöördu KOHE jalaõe poole.",
        ],
      },
      { h2: "Meie kliinikus" },
      {
        p: "Tallinna Endokrinoloogia Kliinikus pakume diabeetilise neuropaatia ennetuseks ja jälgimiseks:",
      },
      {
        ul: [
          "Endokrinoloogi konsultatsioon ravi optimeerimiseks – Tervisekassa ja saatekiri 20€/5€",
          "Diabeediõe nõustamine — kuidas hoolitseda jalgade eest ja parandada veresuhkrut. Tervisekassa ja saatekiri – tasuta",
          "Jalaõe vastuvõtt ravipediküüri ja jalahaavandite raviks (Liina Sildnik Tallinnas, Kersti Saar Pärnus).",
          "ABI-indeksi mõõtmine — 35 € (mõlemad jalad).",
        ],
      },
    ],
    translations: {
      ru: {
        category: "Осложнения диабета",
        title: "Диабетическая нейропатия",
        summary:
          "Диабетическая нейропатия — это поражение нервов, возникающее при длительно повышенном уровне сахара в крови. Это самое частое осложнение диабета — по меньшей мере половина пациентов с диабетом сталкивается с нейропатией в течение жизни. Симптомы варьируются от покалывания и боли в ногах до проблем с пищеварением.",
        author: "Д-р Вернер Фогель, эндокринолог",
        sections: [
          { h2: "Что такое диабетическая нейропатия?" },
          { p: "Нервы передают сигналы между мозгом и остальными частями тела — отвечают за сокращение и расслабление мышц, передают чувствительность от кожи, регулируют работу внутренних органов. При диабете высокий уровень сахара повреждает нервы двумя путями: 1) напрямую нарушает обмен веществ в нервных клетках и 2) повреждает мелкие сосуды, питающие нервы. В результате нарушается «изоляция» нервов (миелин) и передача сигналов." },
          { p: "Нейропатия может проявляться по-разному в зависимости от того, какие нервы повреждены:" },
          {
            ul: [
              "Периферическая нейропатия — самая частая. Поражены нервы рук и ног.",
              "Автономная нейропатия — поражены нервы, управляющие внутренними органами (сердце, желудок, кишечник, мочевой пузырь, половые органы).",
              "Фокальная нейропатия — один конкретный нерв внезапно повреждён (например, лицевой нерв, нерв запястья).",
              "Проксимальная нейропатия (диабетическая амиотрофия) — встречается реже, поражает мышцы бедра и таза.",
            ],
          },
          { h2: "Периферическая нейропатия: самая частая форма" },
          { p: "Обычно начинается с пальцев ног и стоп, со временем поднимается вверх по голеням, позже распространяется и на руки. Симптомы:" },
          {
            ul: [
              "Покалывание, пощипывание или жжение",
              "Онемение или снижение чувствительности",
              "Сильная боль, особенно ночью (может мешать сну)",
              "Повышенная чувствительность к прикосновению (простыня может казаться болезненной)",
              "Слабость в ногах, нарушение координации",
              "Атрофия мышц",
            ],
          },
          {
            callout: {
              type: "warning",
              title: "Почему нейропатия опасна?",
              body:
                "Потеря чувствительности означает, что пациент может не почувствовать небольшую рану или волдырь на стопе. Нейропатия в сочетании с плохим кровоснабжением приводит к развитию диабетической стопы. Возникают язвы, которые не заживают и могут привести к ампутации. В Эстонии ежегодно выполняются сотни связанных с диабетом ампутаций нижних конечностей, большую часть из которых можно предотвратить контролем сахара и регулярными осмотрами.",
            },
          },
          { h2: "Автономная нейропатия" },
          { p: "Автономная нервная система управляет всем, о чём мы не думаем сознательно: сердечный ритм, давление, пищеварение, мочеиспускание, потоотделение. Симптомы автономной нейропатии зависят от того, какие нервы повреждены:" },
          {
            ul: [
              "Сердце и сосуды: отсутствие повышения пульса при нагрузке, резкое падение давления при вставании (ортостатическая гипотензия), «немой» инфаркт без боли в груди",
              "Желудочно-кишечный тракт: гастропарез (замедленное опорожнение желудка — чувство переполнения, тошнота после небольших приёмов пищи), запоры или диарея.",
              "Мочевой пузырь: невозможность ощутить наполненный пузырь, нарушение опорожнения, частые инфекции мочевыводящих путей.",
              "Половые органы: эректильная дисфункция у мужчин, сухость влагалища и снижение либидо у женщин.",
              "Потоотделение: повышенная потливость во время еды (густаторное потоотделение) или, наоборот, снижение потоотделения в нижних конечностях",
              "Утрата ощущения гипогликемии — исчезают предупреждающие симптомы (дрожь, потливость) при низком сахаре в крови",
            ],
          },
          { h2: "Диагностика" },
          { p: "Диагноз нейропатии основывается на сочетании симптомов, клинического осмотра и дополнительных исследований:" },
          {
            ul: [
              "Тест монофиламентом — 10-граммовым монофиламентом касаемся стоп в разных точках. Если пациент не чувствует — чувствительность значительно снижена.",
              "Оценка вибрационной чувствительности.",
              "Рефлексы — ахиллов рефлекс часто снижается первым.",
              "ABI-индекс — связано ли нарушение чувствительности с плохим кровоснабжением.",
              "Электронейромиография (ЭНМГ) — более точное измерение функции нервов, проводится у невролога.",
              "Для оценки автономной нейропатии: ортостатическая проба, вариабельность сердечного ритма, при необходимости — оценка опорожнения желудка.",
            ],
          },
          { h2: "Лечение" },
          { p: "Нейропатия трудно обратима, но симптомы можно облегчить, а прогрессирование болезни — остановить. Лечение состоит из трёх уровней:" },
          { h2: "1. Лечение основного заболевания" },
          { p: "Чем лучше контроль сахара, тем медленнее развивается нейропатия. Также важно лечить все сопутствующие факторы риска (давление, холестерин, курение, алкоголь)." },
          { h2: "2. Облегчение симптомов" },
          {
            ul: [
              "Нейропатическая боль: прегабалин, габапентин, дулоксетин, амитриптилин. Обычные обезболивающие (ибупрофен, парацетамол) обычно не помогают.",
              "Местные кремы: капсаицин, лидокаиновые пластыри",
              "Автономная нейропатия: препараты от давления, лечение гастропареза (метоклопрамид, изменение диеты — небольшие частые приёмы пищи), лечение эректильной дисфункции (ингибиторы ФДЭ-5)",
              "Для восстановления чувствительности к гипогликемии может помочь непрерывный мониторинг глюкозы (CGM).",
            ],
          },
          { h2: "3. Уход за стопами" },
          { p: "Это самая важная часть лечения нейропатии для предотвращения ампутации:" },
          {
            ul: [
              "Осматривайте свои стопы КАЖДЫЙ день — нет ли новых ран, волдырей, покраснений, мозолей или изменений ногтей.",
              "Мойте стопы каждый день тёплой водой, тщательно вытирайте, особенно между пальцами.",
              "Не ходите босиком, даже дома.",
              "Носите мягкие, дышащие носки и удобную обувь. Стригите ногти прямо, не округляя.",
              "Регулярно посещайте медсестру по стопе для лечебного педикюра — в нашей клинике 40 € (платно) или бесплатно по направлению медицинской страховки.",
              "При любой ране немедленно обращайтесь к медсестре по стопе.",
            ],
          },
          { h2: "В нашей клинике" },
          { p: "В Таллиннской клинике эндокринологии мы предлагаем для профилактики и наблюдения диабетической нейропатии:" },
          {
            ul: [
              "Консультация эндокринолога для оптимизации лечения — медицинская страховка и направление 20€/5€",
              "Консультация медсестры по диабету — как ухаживать за стопами и улучшить уровень сахара. Медицинская страховка и направление — бесплатно",
              "Приём медсестры по стопе для лечебного педикюра и лечения язв (Лийна Сильдник в Таллинне, Керсти Саар в Пярну).",
              "Измерение ABI-индекса — 35 € (обе ноги).",
            ],
          },
        ],
      },
      en: {
        category: "Diabetes complications",
        title: "Diabetic neuropathy",
        summary:
          "Diabetic neuropathy is nerve damage that develops when blood sugar has been high for a long time. It is the most common complication of diabetes — at least half of people with diabetes experience neuropathy at some point in their lives. Symptoms range from tingling and pain in the feet to digestive problems.",
        author: "Dr. Verner Fogel, endocrinologist",
        sections: [
          { h2: "What is diabetic neuropathy?" },
          { p: "Nerves transmit signals between the brain and the rest of the body — they trigger muscle contraction and relaxation, carry sensation from the skin and regulate the internal organs. In diabetes, high blood glucose damages the nerves in two ways: 1) it directly disrupts the metabolism of nerve cells and 2) it damages the tiny blood vessels that supply the nerves. The result is damage to the nerve's \"insulation\" (myelin) and impaired signal transmission." },
          { p: "Neuropathy can manifest in several different ways depending on which nerves are affected:" },
          {
            ul: [
              "Peripheral neuropathy — the most common. The nerves of the hands and feet are affected.",
              "Autonomic neuropathy — the nerves controlling internal organs (heart, stomach, intestines, bladder, sex organs) are affected.",
              "Focal neuropathy — a single specific nerve is suddenly damaged (e.g. facial nerve, wrist nerve).",
              "Proximal neuropathy (diabetic amyotrophy) — less common, affects hip and thigh muscles.",
            ],
          },
          { h2: "Peripheral neuropathy: the most common form" },
          { p: "Usually starts in the toes and soles, gradually moves up the lower legs and later affects the hands. Symptoms are:" },
          {
            ul: [
              "Tingling, prickling or burning",
              "Numbness or reduced sensation",
              "Severe pain, especially at night (can disturb sleep)",
              "Hypersensitivity to touch (a bedsheet may feel painful)",
              "Weakness in the legs, coordination problems",
              "Muscle atrophy",
            ],
          },
          {
            callout: {
              type: "warning",
              title: "Why is neuropathy dangerous?",
              body:
                "Loss of sensation means the patient may not feel a small wound or blister on the foot. Neuropathy combined with poor blood supply leads to the diabetic foot. Ulcers develop that do not heal and can lead to amputation. Hundreds of diabetes-related lower-limb amputations are performed each year in Estonia, most of which are preventable with good blood sugar control and regular check-ups.",
            },
          },
          { h2: "Autonomic neuropathy" },
          { p: "The autonomic nervous system controls everything we don't consciously think about: heart rate, blood pressure, digestion, urination, sweating. Symptoms of autonomic neuropathy depend on which nerves are affected:" },
          {
            ul: [
              "Heart and vessels: lack of heart rate rise during exertion, sudden drop in blood pressure when standing up (orthostatic hypotension), \"silent\" heart attack without chest pain",
              "Digestive tract: gastroparesis (slow gastric emptying — feeling of fullness, nausea after small meals), constipation or diarrhoea.",
              "Bladder: inability to feel a full bladder, impaired emptying, frequent urinary tract infections.",
              "Sex organs: erectile dysfunction in men, vaginal dryness and decreased libido in women.",
              "Sweating: excessive sweating during meals (gustatory sweating) or, conversely, reduced sweating in the lower limbs",
              "Loss of hypoglycaemia awareness — the warning symptoms (trembling, sweating) of low blood sugar disappear",
            ],
          },
          { h2: "Diagnosis" },
          { p: "The diagnosis of neuropathy is based on a combination of symptoms, clinical examination and additional tests:" },
          {
            ul: [
              "Monofilament test — we touch the soles of the feet at different points with a 10 g monofilament. If the patient cannot feel it, sensation is significantly reduced.",
              "Vibration sensation assessment.",
              "Reflexes — the Achilles reflex is often the first to be reduced.",
              "ABI index — to check whether the sensory disturbance is related to poor blood supply.",
              "Electroneuromyography (ENMG) — a more accurate measurement of nerve function, performed by a neurologist.",
              "For autonomic neuropathy: orthostatic test, heart rate variability, gastric emptying assessment when needed.",
            ],
          },
          { h2: "Treatment" },
          { p: "Neuropathy is difficult to reverse, but symptoms can be relieved and disease progression halted. Treatment consists of three levels:" },
          { h2: "1. Treating the underlying disease" },
          { p: "The better blood sugar is controlled, the more slowly neuropathy develops. Treating all related risk factors (blood pressure, cholesterol, smoking, alcohol) is also important." },
          { h2: "2. Symptom relief" },
          {
            ul: [
              "Neuropathic pain: pregabalin, gabapentin, duloxetine, amitriptyline. Ordinary painkillers (ibuprofen, paracetamol) usually do not help.",
              "Topical creams: capsaicin, lidocaine patches",
              "Autonomic neuropathy: blood pressure drugs, treatment of gastroparesis (metoclopramide, dietary changes — small frequent meals), treatment of erectile dysfunction (PDE5 inhibitors)",
              "Continuous glucose monitoring (CGM) can help restore awareness of hypoglycaemia.",
            ],
          },
          { h2: "3. Foot care" },
          { p: "This is the most important part of neuropathy management for preventing amputation:" },
          {
            ul: [
              "Check your feet EVERY day — look for new wounds, blisters, redness, calluses or nail changes.",
              "Wash your feet daily with lukewarm water, dry carefully, especially between the toes.",
              "Don't walk barefoot, not even at home.",
              "Wear soft, breathable socks and comfortable shoes. Cut nails straight across, not rounded.",
              "Visit the foot nurse regularly for medical pedicure — at our clinic €40 (paid) or free with a referral from your health insurance.",
              "Contact the foot nurse IMMEDIATELY for any ulcer.",
            ],
          },
          { h2: "At our clinic" },
          { p: "At Tallinn Endocrinology Clinic we offer for the prevention and monitoring of diabetic neuropathy:" },
          {
            ul: [
              "Endocrinologist consultation for treatment optimisation — Estonian Health Insurance and a referral 20€/5€",
              "Diabetes nurse counselling — how to care for your feet and improve blood sugar. Estonian Health Insurance and a referral — free of charge",
              "Foot nurse appointment for medical pedicure and treatment of foot ulcers (Liina Sildnik in Tallinn, Kersti Saar in Pärnu).",
              "ABI index measurement — €35 (both legs).",
            ],
          },
        ],
      },
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "diabeetiline-nefropaatia",
    slug: "diabeetiline-nefropaatia",
    category: "Diabeedi tüsistused",
    title: "Diabeetiline nefropaatia",
    image: "/api/static/ai-images/articles/diabeetiline-nefropaatia.webp",
    imageCaption: "Neerupäsmakeste kahjustus kõrgest veresuhkrust",
    summary:
      "Diabeetiline nefropaatia on diabeedi põhjustatud neerukahjustus — kõige sagedasem kroonilise neeruhaiguse põhjus arenenud riikides ning peamine põhjus, miks inimesed jõuavad neeruasendusravini (dialüüs või neerusiirdamine). Õigeaegse avastamise ja ravi korral saab seda aga edukalt aeglustada.",
    readingMinutes: 7,
    updated: "2026-06-13",
    author: DR_AUTHOR,
    sections: [
      { h2: "Mis on diabeetiline nefropaatia?" },
      {
        p: "Neerud on keha looduslik filter — nad puhastavad iga päev 180 liitrit verd, eemaldades jääkaineid ja liigset vedelikku. Filtreerimise teeb miljonid väikesed üksused, mida kutsutakse neerupäsmakeseks. Iga neerupäsmake sisaldab glomeruli — pisikest veresoonte kogumikku, kus filtreerimine tegelikult toimub.",
      },
      {
        p: "Pikaajaliselt kõrge veresuhkur kahjustab glomerulite veresoonte seinu — need paksenevad ja hakkavad lekkima. Esimeseks märgiks on albumiini esinemine uriinis, mida tavaliselt ei tohiks seal olla. Aja jooksul väheneb neerude filtreerimisvõime ning lõpuks vajab patsient dialüüsi või neerusiirdamist.",
      },
      { h2: "Nefropaatia etapid" },
      {
        ol: [
          "Hüperfiltreerimine — varases staadiumis filtreerivad neerud isegi rohkem kui tavaliselt. Sümptomeid pole.",
          "Mikroalbuminuuria (varajane nefropaatia) — uriiniga eritub väike kogus albumiini (30–300 mg/24 h või albumiin/kreatiniin suhe 3–30 mg/mmol). Sümptomeid endiselt pole.",
          "Makroalbuminuuria (avalduv nefropaatia) — uriiniga eritub suurem kogus albumiini (> 300 mg/24 h). Vererõhk hakkab tõusma.",
          "Krooniline neerupuudulikkus — eGFR (hinnanguline glomerulaarne filtratsioonimäär) langeb alla 60 ml/min. Hakkavad kogunema jääkained, tekib aneemia, luuhaigus.",
          "Lõppstaadium (eGFR < 15) — vajalik on dialüüs või neerusiirdamine.",
        ],
      },
      { h2: "Sümptomid" },
      {
        p: "Nagu retinopaatia, on ka nefropaatia varajased etapid sümptomivabad. Kui sümptomid ilmuvad, on haigus juba kaugele arenenud. Need on:",
      },
      {
        ul: [
          "Vahutav uriin (proteiinuuria tunnus)",
          "Tursed (silmade ümber, jalalabades, säärtes)",
          "Vererõhu tõus või kontrollimatu vererõhk",
          "Väsimus, jõuetus (aneemia)",
          "Söögiisu langus, iiveldus",
          "Sügelev nahk",
          "Lihaskrambid",
          "Unehäired",
          "Kontsentratsioonihäired",
        ],
      },
      { h2: "Diagnoosimine ja jälgimine" },
      {
        p: "Kuna varased etapid on sümptomivabad, on regulaarne jälgimine ainus võimalus haigust õigel ajal avastada. Iga diabeedihaige peaks kord aastas läbima:",
      },
      {
        ul: [
          "Albumiini/kreatiniini suhe (UACR) ühekordsest uriiniproovist — kõige tundlikum analüüs varajase nefropaatia avastamiseks",
          "Seerumi kreatiniin ja eGFR — neerude üldfunktsiooni hindamine",
          "Vererõhu mõõtmine",
        ],
      },
      {
        p: "Kui UACR on > 3 mg/mmol, korratakse analüüsi 2–3 kuu pärast. Kahe positiivse tulemuse korral kinnitatakse mikroalbuminuuria diagnoos.",
      },
      { h2: "Ravi" },
      {
        p: "Nefropaatia ravi on kompleksne:",
      },
      { h2: "1. Veresuhkru kontroll" },
      { p: "HbA1c eesmärk on tavaliselt < 7%. Liiga karm kontroll vanemaealistel patsientidel võib suurendada hüpoglükeemia riski." },
      { h2: "2. Vererõhu kontroll" },
      {
        p: "Vererõhu eesmärk on < 130/80 mmHg (mõnel patsiendil isegi madalam). Esmavalik on AKE-inhibiitorid (näiteks ramipriil) või ARB-d (näiteks losartaan) — need vähendavad survet glomerulites ning aeglustavad nefropaatia progresseerumist sõltumata vererõhu tasemest. Need on \"kohustuslikud\" ravimid kõigil mikroalbuminuuriaga patsientidel.",
      },
      { h2: "3. SGLT2-inhibiitorid" },
      {
        p: "Empagliflosiin ja dapagliflosiin on viimase 10 aasta üks suurimaid edusamme. Need ravimid eemaldavad glükoosi neerude kaudu, kuid lisaks sellele on neil tugev neerukaitsev toime — vähendavad oluliselt nefropaatia progresseerumist ja kardiovaskulaarsete sündmuste riski. Soovitame neid kõigile 2. tüüpi diabeedi ja nefropaatiaga patsientidele, kui pole vastunäidustusi.",
      },
      { h2: "4. Mineraalokortikoidi retseptori antagonistid (MRA)" },
      {
        p: "Finerenoon on uus ravim, mis on näidustatud nefropaatiaga 2. tüüpi diabeetikutele. Vähendab proteinuuriat ja kaitseb neere.",
      },
      { h2: "5. Toitumis- ja elustiilimuutused" },
      {
        ul: [
          "Soolaga vähem — alla 5 g päevas",
          "Valku mõõdukalt (0,8 g/kg kehakaalu kohta)",
          "Loobu suitsetamisest",
          "Hoia kehakaal kontrolli all",
          "Liigu regulaarselt",
          "Väldi nefrotoksilisi ravimeid (NSAID-d nagu ibuprofeen pikaajaliselt)",
        ],
      },
      { h2: "Hilisem ravi" },
      {
        p: "Kui nefropaatia jõuab krooniliseks neerupuudulikkuseks (eGFR < 60), suunatakse patsient nefroloogi juurde. Lõppstaadiumis (eGFR < 15) on vajalik:",
      },
      {
        ul: [
          "Hemodialüüs (kunstneer) — 3 korda nädalas, 4 tundi seansis",
          "Peritoneaaldialüüs — kõhukoopas tehtav dialüüs, mis võimaldab kodust ravi",
          "Neerusiirdamine — kõige parem pikaajaline lahendus, kuid eeldab sobiva doonori leidmist",
        ],
      },
      { h2: "Meie kliinikus" },
      {
        p: "Tallinna Endokrinoloogia Kliinikus jälgime regulaarselt neerufunktsiooni kõigil diabeedihaigetel. Albumiini/kreatiniini suhe ja seerumi kreatiniin on osa meie standardsest diabeedipaketist. Kui leiame mikroalbuminuuria, kohandame ravi (lisame AKE-inhibiitori või SGLT2-inhibiitori) ning kutsume sind sagedasematele kordusvisiitidele. Vajadusel suuname nefroloogi juurde.",
      },
    ],
    translations: {
      ru: {
        category: "Осложнения диабета",
        title: "Диабетическая нефропатия",
        imageCaption: "Повреждение почечных клубочков из-за высокого уровня сахара",
        summary:
          "Диабетическая нефропатия — это поражение почек, вызванное диабетом, — самая частая причина хронической болезни почек в развитых странах и основная причина, по которой пациенты нуждаются в заместительной почечной терапии (диализ или пересадка почки). При своевременном выявлении и лечении болезнь можно успешно замедлить.",
        author: "Д-р Вернер Фогель, эндокринолог",
        sections: [
          { h2: "Что такое диабетическая нефропатия?" },
          { p: "Почки — это естественный фильтр организма: каждый день они очищают 180 литров крови, удаляя продукты обмена и избыток жидкости. Фильтрацию выполняют миллионы крошечных единиц — почечных клубочков. Каждый клубочек содержит гломерулу — маленький пучок сосудов, в котором собственно и происходит фильтрация." },
          { p: "Длительно повышенный уровень сахара повреждает стенки сосудов клубочков — они утолщаются и начинают пропускать белок. Первым признаком является появление альбумина в моче, которого там обычно быть не должно. Со временем фильтрационная способность почек снижается, и в конечном итоге пациенту требуется диализ или пересадка почки." },
          { h2: "Стадии нефропатии" },
          {
            ol: [
              "Гиперфильтрация — на ранней стадии почки фильтруют даже больше обычного. Симптомов нет.",
              "Микроальбуминурия (ранняя нефропатия) — с мочой выделяется небольшое количество альбумина (30–300 мг/24 ч или соотношение альбумин/креатинин 3–30 мг/ммоль). Симптомов всё ещё нет.",
              "Макроальбуминурия (явная нефропатия) — с мочой выделяется большее количество альбумина (> 300 мг/24 ч). Начинает повышаться артериальное давление.",
              "Хроническая почечная недостаточность — eGFR (расчётная скорость клубочковой фильтрации) падает ниже 60 мл/мин. Начинают накапливаться продукты обмена, развивается анемия, поражение костей.",
              "Терминальная стадия (eGFR < 15) — требуется диализ или пересадка почки.",
            ],
          },
          { h2: "Симптомы" },
          { p: "Как и ретинопатия, нефропатия на ранних стадиях протекает без симптомов. Когда симптомы появляются, болезнь уже далеко зашла. Это:" },
          {
            ul: [
              "Пенящаяся моча (признак протеинурии)",
              "Отёки (вокруг глаз, на стопах, голенях)",
              "Повышение артериального давления или неконтролируемое давление",
              "Усталость, слабость (анемия)",
              "Снижение аппетита, тошнота",
              "Кожный зуд",
              "Мышечные судороги",
              "Нарушения сна",
              "Снижение концентрации внимания",
            ],
          },
          { h2: "Диагностика и наблюдение" },
          { p: "Поскольку ранние стадии бессимптомны, регулярное наблюдение — единственный способ обнаружить болезнь вовремя. Каждый пациент с диабетом раз в год должен сдавать анализы:" },
          {
            ul: [
              "Соотношение альбумин/креатинин (UACR) из разовой пробы мочи — самый чувствительный анализ для выявления ранней нефропатии",
              "Креатинин сыворотки и eGFR — общая оценка функции почек",
              "А также измерение артериального давления дома",
            ],
          },
          { p: "Если UACR > 3 мг/ммоль, анализ повторяют через 2–3 месяца. При двух положительных результатах диагноз микроальбуминурии подтверждается." },
          { h2: "Лечение" },
          { p: "Лечение нефропатии комплексное:" },
          { h2: "1. Контроль уровня сахара" },
          { p: "Целевой HbA1c обычно < 7%. Слишком жёсткий контроль у пожилых пациентов может повысить риск гипогликемии." },
          { h2: "2. Контроль артериального давления" },
          { p: "Целевое давление < 130/80 mmHg (у некоторых пациентов даже ниже). Препараты первого выбора — ингибиторы АПФ (например, рамиприл) или БРА (например, лозартан) — они снижают давление в клубочках и замедляют прогрессирование нефропатии независимо от уровня давления. Это «обязательные» препараты у всех пациентов с микроальбуминурией." },
          { h2: "3. Ингибиторы SGLT2" },
          { p: "Эмпаглифлозин и дапаглифлозин — это самое большое достижение последних 10 лет. Эти препараты выводят глюкозу через почки, но также обладают мощным нефропротекторным действием — значительно снижают прогрессирование нефропатии и риск сердечно-сосудистых событий. Рекомендуем их всем пациентам с СД2 и нефропатией при отсутствии противопоказаний." },
          { h2: "4. Антагонисты минералокортикоидных рецепторов (MRA)" },
          { p: "Финеренон — новый препарат, показанный пациентам с СД2 и нефропатией. Снижает протеинурию и защищает почки." },
          { h2: "5. Изменения питания и образа жизни" },
          {
            ul: [
              "Снизить потребление соли — менее 5 г в день",
              "Белок — умеренно (0,8 г/кг массы тела)",
              "Отказ от курения",
              "Поддерживать массу тела под контролем",
              "Регулярная физическая активность",
              "Избегать нефротоксичных лекарств (НПВС, например, ибупрофен длительно)",
            ],
          },
          { h2: "Более поздняя терапия" },
          { p: "Если нефропатия достигает стадии хронической почечной недостаточности (eGFR < 60), пациент направляется к нефрологу. В терминальной стадии (eGFR < 15) необходим:" },
          {
            ul: [
              "Гемодиализ (искусственная почка) — 3 раза в неделю, по 4 часа за сеанс",
              "Перитонеальный диализ — диализ через брюшную полость, позволяет лечение на дому",
              "Пересадка почки — лучшее долгосрочное решение, но требует подходящего донора",
            ],
          },
          { h2: "В нашей клинике" },
          { p: "В Таллиннской клинике эндокринологии мы регулярно контролируем функцию почек у всех пациентов с диабетом. Соотношение альбумин/креатинин и креатинин сыворотки входят в наш стандартный диабетический пакет. Если обнаруживаем микроальбуминурию, корректируем терапию (добавляем ингибитор АПФ или SGLT2) и приглашаем вас на более частые контрольные визиты. При необходимости направляем к нефрологу." },
        ],
      },
      en: {
        category: "Diabetes complications",
        title: "Diabetic nephropathy",
        imageCaption: "Glomerular damage caused by high blood glucose",
        summary:
          "Diabetic nephropathy is kidney damage caused by diabetes — the most common cause of chronic kidney disease in developed countries and the leading reason people end up needing renal replacement therapy (dialysis or kidney transplant). With timely detection and treatment, however, its progression can be successfully slowed.",
        author: "Dr. Verner Fogel, endocrinologist",
        sections: [
          { h2: "What is diabetic nephropathy?" },
          { p: "The kidneys are the body's natural filter — every day they clean 180 litres of blood, removing waste products and excess fluid. Filtration is performed by millions of tiny units called nephrons. Each nephron contains a glomerulus — a tiny ball of blood vessels where filtration actually takes place." },
          { p: "Long-term high blood glucose damages the walls of the glomerular vessels — they thicken and start to leak. The first sign is the appearance of albumin in the urine, where it should normally not be. Over time the kidneys' filtration capacity decreases and eventually the patient needs dialysis or a kidney transplant." },
          { h2: "Stages of nephropathy" },
          {
            ol: [
              "Hyperfiltration — at an early stage the kidneys filter even more than normal. No symptoms.",
              "Microalbuminuria (early nephropathy) — a small amount of albumin is excreted in the urine (30–300 mg/24 h or albumin/creatinine ratio 3–30 mg/mmol). Still no symptoms.",
              "Macroalbuminuria (overt nephropathy) — a larger amount of albumin is excreted (> 300 mg/24 h). Blood pressure starts to rise.",
              "Chronic kidney failure — eGFR (estimated glomerular filtration rate) drops below 60 mL/min. Waste products start to accumulate, anaemia and bone disease develop.",
              "End stage (eGFR < 15) — dialysis or a kidney transplant is required.",
            ],
          },
          { h2: "Symptoms" },
          { p: "Like retinopathy, the early stages of nephropathy are symptom-free. By the time symptoms appear, the disease is already advanced. Symptoms are:" },
          {
            ul: [
              "Foamy urine (a sign of proteinuria)",
              "Swelling (around the eyes, in the feet, lower legs)",
              "Rising blood pressure or uncontrolled blood pressure",
              "Fatigue, weakness (anaemia)",
              "Loss of appetite, nausea",
              "Itchy skin",
              "Muscle cramps",
              "Sleep disturbances",
              "Concentration problems",
            ],
          },
          { h2: "Diagnosis and monitoring" },
          { p: "Because the early stages are symptom-free, regular monitoring is the only way to detect the disease in time. Every patient with diabetes should have annual tests:" },
          {
            ul: [
              "Albumin/creatinine ratio (UACR) from a single urine sample — the most sensitive test for detecting early nephropathy",
              "Serum creatinine and eGFR — overall assessment of kidney function",
              "Home blood pressure measurement",
            ],
          },
          { p: "If UACR is > 3 mg/mmol, the test is repeated in 2–3 months. Two positive results confirm the diagnosis of microalbuminuria." },
          { h2: "Treatment" },
          { p: "Nephropathy treatment is multifaceted:" },
          { h2: "1. Blood glucose control" },
          { p: "HbA1c target is usually < 7%. Overly strict control in elderly patients can increase the risk of hypoglycaemia." },
          { h2: "2. Blood pressure control" },
          { p: "The blood pressure target is < 130/80 mmHg (even lower in some patients). First-line drugs are ACE inhibitors (e.g. ramipril) or ARBs (e.g. losartan) — they reduce pressure in the glomeruli and slow the progression of nephropathy regardless of blood pressure level. These are \"mandatory\" drugs in all patients with microalbuminuria." },
          { h2: "3. SGLT2 inhibitors" },
          { p: "Empagliflozin and dapagliflozin represent the greatest progress of the past decade. These drugs remove glucose via the kidneys but also have a strong kidney-protective effect — they significantly reduce the progression of nephropathy and the risk of cardiovascular events. We recommend them to all patients with type 2 diabetes and nephropathy in the absence of contraindications." },
          { h2: "4. Mineralocorticoid receptor antagonists (MRA)" },
          { p: "Finerenone is a new drug indicated for type 2 diabetic patients with nephropathy. It reduces proteinuria and protects the kidneys." },
          { h2: "5. Diet and lifestyle changes" },
          {
            ul: [
              "Reduce salt — less than 5 g per day",
              "Protein in moderation (0.8 g/kg of body weight)",
              "Stop smoking",
              "Keep body weight under control",
              "Exercise regularly",
              "Avoid nephrotoxic medications (NSAIDs such as ibuprofen, long-term)",
            ],
          },
          { h2: "Later treatment" },
          { p: "When nephropathy progresses to chronic kidney failure (eGFR < 60), the patient is referred to a nephrologist. In the end stage (eGFR < 15), the following is required:" },
          {
            ul: [
              "Haemodialysis (artificial kidney) — 3 times a week, 4-hour sessions",
              "Peritoneal dialysis — dialysis performed in the abdominal cavity, allowing home treatment",
              "Kidney transplant — the best long-term solution but requires finding a matching donor",
            ],
          },
          { h2: "At our clinic" },
          { p: "At Tallinn Endocrinology Clinic we regularly monitor kidney function in all diabetes patients. Albumin/creatinine ratio and serum creatinine are part of our standard diabetes panel. If we find microalbuminuria, we adjust treatment (adding an ACE inhibitor or SGLT2 inhibitor) and invite you to more frequent follow-up visits. When needed, we refer you to a nephrologist." },
        ],
      },
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "diabeedi-uuringud",
    slug: "diabeedi-uuringud",
    category: "Diagnoos",
    title: "Diabeedi uuringud — A1c, albumiini/kreatiniini suhe ja teised",
    image: "/api/static/ai-images/articles/diabeedi-uuringud.webp",
    summary:
      "Diabeedi hea juhtimine põhineb regulaarsetel analüüsidel. Käesolev artikkel selgitab põhjalikult, mida tähendavad olulisemad diabeedi uuringud — HbA1c, albumiini/kreatiniini suhe, lipiidide profiil, kreatiniin — ja milleks neid tehakse.",
    readingMinutes: 9,
    updated: "2026-06-13",
    author: DR_AUTHOR,
    sections: [
      { h2: "Miks on analüüsid olulised?" },
      {
        p: "Diabeet on \"vaikne\" haigus — patsient võib end tunda hästi, kuigi veresuhkur või vererõhk on aastate jooksul kõrge ja kahjustab veresooni ja organeid. Sümptomid (nägemise halvenemine, jalahaavandid, neerupuudulikkus) ilmuvad alles siis, kui tüsistused on juba kaugele arenenud. Sellepärast on regulaarne jälgimine analüüside kaudu ainus võimalus seisundit objektiivselt hinnata ja ravi õigeaegselt kohandada.",
      },
      { h2: "HbA1c (glükohemoglobiin)" },
      {
        p: "HbA1c on kõige tähtsam diabeedi jälgimise analüüs. See näitab, milline on olnud sinu keskmine veresuhkur viimase 2–3 kuu jooksul. Põhimõte: hemoglobiin (vere punaste rakkude valk) on pidevas kontaktis vere glükoosiga. Mida kõrgem on veresuhkur, seda rohkem glükoosi \"kleepub\" hemoglobiinile, moodustades HbA1c. Kuna punaste vererakkude eluiga on umbes 120 päeva, peegeldab HbA1c keskmist veresuhkrut viimaste kuude jooksul.",
      },
      { h2: "HbA1c väärtused ja nende tähendus" },
      {
        ul: [
          "< 6,0 % — normaalne",
          "6,0–6,4% — prediabeet",
          "≥ 6,5% — diabeet (diagnoos)",
          "6,5–7,0% — eesmärk enamikule diabeedihaigetest",
          "< 6,5% — rangem eesmärk noorematele, varases haiguse staadiumis",
          "< 8,0% — leebem eesmärk vanematele, paljude tüsistustega patsientidele",
        ],
      },
      {
        p: "HbA1c-d soovitame mõõta iga 3–6 kuu tagant. Hea ravisoostumuse korral piisab kord poole aasta tagant; ravi muutmise ajal või halvasti kontrollitud diabeedi puhul iga 3 kuu tagant.",
      },
      {
        callout: {
          type: "info",
          title: "Mida HbA1c veresuhkrutes tähendab?",
          body:
            "HbA1c 6% ≈ keskmine veresuhkur 7 mmol/L. HbA1c 7% ≈ 8,6 mmol/L. HbA1c 8% ≈ 10,2 mmol/L. HbA1c 9% ≈ 11,8 mmol/L. HbA1c 10% ≈ 13,4 mmol/L. Iga 1% suurenemine HbA1c-s vastab umbes 1,5 mmol/L kõrgemale keskmisele veresuhkrule.",
        },
      },
      { h2: "Piirangud" },
      {
        p: "HbA1c ei ole alati täiesti usaldusväärne. See võib olla ebatäpne järgmistel juhtudel:",
      },
      {
        ul: [
          "Aneemia — kõige sagedasem põhjus, langetab HbA1c-d",
          "Hemoglobinopaatiad (sirprakuline aneemia, talasseemia)",
          "Hiljutine verejooks, vereülekanne",
          "Krooniline neerupuudulikkus",
          "Rasedus — eelistame teisi analüüse (tühja kõhu veresuhkur, OGTT)",
        ],
      },
      {
        p: "Sellepärast hindame HbA1c-d alati koos teiste analüüsidega ning vajadusel mõõdame veresuhkrut ka kodus glükomeetri või CGM-iga.",
      },
      { h2: "Albumiini/kreatiniini suhe (UACR)" },
      {
        p: "UACR on kõige tundlikum analüüs varajase diabeetilise nefropaatia (neerukahjustuse) avastamiseks. Tervel neerul on glomeerulid \"piisavalt tihedad\", et hoida valgud (näiteks albumiini) verevoolus. Kui veresuhkur kahjustab glomeerulite seinu, hakkab albumiin uriiniga välja erituma — see on esimene märk diabeetilisest nefropaatiast.",
      },
      { h2: "UACR väärtused" },
      {
        ul: [
          "< 3 mg/mmol (< 30 mg/g) — normaalne",
          "3–30 mg/mmol (30–300 mg/g) — mikroalbuminuuria (varajane nefropaatia)",
          "> 30 mg/mmol (> 300 mg/g) — makroalbuminuuria (avalduv nefropaatia)",
        ],
      },
      {
        p: "Analüüs tehakse ühekordsest uriiniproovist (eelistatult hommikune esimene uriin). Üks positiivne tulemus ei ole diagnoosiks piisav — kinnituseks on vaja 2 positiivset analüüsi 3–6 kuu jooksul. UACR-i soovitame teha kõigil diabeedihaigetel kord aastas.",
      },
      { h2: "Seerumi kreatiniin ja eGFR" },
      {
        p: "Kreatiniin on lihaste ainevahetuse jääkaine, mille neerud filtreerivad verest. Kreatiniini tasemest verest arvutame eGFR-i (hinnanguline glomerulaarne filtratsioonimäär), mis näitab, kui hästi neerud filtreerivad:",
      },
      {
        ul: [
          "eGFR > 90 ml/min — normaalne",
          "eGFR 60–89 — kerge langus",
          "eGFR 45–59 — mõõdukas neerupuudulikkus (staadium 3a)",
          "eGFR 30–44 — raskem neerupuudulikkus (staadium 3b)",
          "eGFR 15–29 — raske neerupuudulikkus (staadium 4)",
          "eGFR < 15 — lõppstaadium (vajab dialüüsi)",
        ],
      },
      { h2: "Lipiidide profiil" },
      {
        p: "Diabeedihaigetel on sageli düslipideemia — kõrged kolesterooli tasemed. See on üks suurimaid südameinfarkti riskifaktoreid. Mõõdame kord aastas:",
      },
      {
        ul: [
          "Kogu-kolesterool — < 4,5 mmol/L",
          "LDL-kolesterool (\"halb\") — < 2,6 mmol/L diabeedihaigetel (< 1,8 mmol/L, kui on juba tüsistused)",
          "HDL-kolesterool (\"hea\") — > 1,0 mmol/L meestel, > 1,3 mmol/L naistel",
          "Triglütseriidid — < 1,7 mmol/L",
        ],
      },
      {
        p: "Kõrge LDL-i puhul on enamasti vaja statiini (atorvastatiin, rosuvastatiin) — see vähendab oluliselt südameinfarkti ja insuldi riski.",
      },
      { h2: "Veresuhkur tühja kõhuga ja juhuslik veresuhkur" },
      {
        ul: [
          "Tühja kõhu veresuhkur — pärast 8-tunnist paastu. Norm < 5,6 mmol/L; prediabeet 5,6–6,9; diabeet ≥ 7,0.",
          "Juhuslik veresuhkur — ükskõik millal mõõdetud. Diabeedi diagnoosiks ≥ 11,1 mmol/L koos sümptomitega.",
        ],
      },
      { h2: "Glükoosi tolerantsustest (OGTT)" },
      {
        p: "OGTT on \"kullastandard\" diabeedi diagnoosimiseks. Patsient joob 75 g glükoosi ja seejärel mõõdetakse veresuhkrut tühja kõhuga ja 2 tunni pärast. Diabeet, kui 2-tunni väärtus ≥ 11,1 mmol/L. Kasutame ka rasedusdiabeedi diagnoosimiseks (raseduse 24.–28. nädalal).",
      },
      { h2: "Teised olulised analüüsid" },
      {
        ul: [
          "TSH (kilpnäärme funktsioon) — kord aastas, sest kilpnäärmehaigused on diabeedihaigetel sagedasemad",
          "Vitamiin B12 — eriti metformiini kasutajatele, kuna see ravim võib põhjustada B12 vaegust",
          "Maksaensüümid (ALAT, ASAT) — rasvmaksa hindamiseks",
          "Vere üldanalüüs — aneemia välistamiseks",
        ],
      },
      { h2: "Meie kliinikus" },
      {
        p: "Tallinna Endokrinoloogia Kliinikus pakume kõiki neid analüüse.",
      },
      {
        p: "Tervisekassa saatekirjaga on analüüsid tasuta.",
      },
      {
        p: "Tasulised paketid:",
      },
      {
        ul: [
          "Visiit + 4 analüüsi (HbA1c, LDL, HDL, kreatiniin) — 105 €",
          "Visiit + HbA1c + silmapõhjade pildistamine — 110 €",
          "Üksikud analüüsid (biokeemiline / hormonaalne) — 15 € tk",
          "Glükoosi tolerantsustest (sh testvedelik) — 40 €",
        ],
      },
    ],
    translations: {
      ru: {
        category: "Диагностика",
        title: "Анализы при диабете — A1c, соотношение альбумин/креатинин и другие",
        summary:
          "Хорошее управление диабетом основано на регулярных анализах. В этой статье подробно объясняется, что означают важнейшие анализы при диабете — HbA1c, соотношение альбумин/креатинин, липидный профиль, креатинин — и для чего они нужны.",
        author: "Д-р Вернер Фогель, эндокринолог",
        sections: [
          { h2: "Почему важны анализы?" },
          { p: "Диабет — это «тихое» заболевание: пациент может чувствовать себя хорошо, в то время как уровень сахара или артериальное давление годами повышены и повреждают сосуды и органы. Симптомы (ухудшение зрения, язвы стоп, почечная недостаточность) появляются лишь тогда, когда осложнения уже далеко зашли. Поэтому регулярный контроль анализов — единственный способ объективно оценить состояние и своевременно скорректировать лечение." },
          { h2: "HbA1c (гликированный гемоглобин)" },
          { p: "HbA1c — самый важный анализ для мониторинга диабета. Он показывает, каким был ваш средний уровень сахара за последние 2–3 месяца. Принцип: гемоглобин (белок эритроцитов) постоянно контактирует с глюкозой крови. Чем выше сахар, тем больше глюкозы «прилипает» к гемоглобину, образуя HbA1c. Поскольку срок жизни эритроцитов около 120 дней, HbA1c отражает средний уровень сахара за последние месяцы." },
          { h2: "Значения HbA1c и их интерпретация" },
          {
            ul: [
              "< 6,0% — норма",
              "6,0–6,4% — преддиабет",
              "≥ 6,5% — диабет (диагноз)",
              "< 7,0% — цель для большинства пациентов с диабетом",
              "< 6,5% — более строгая цель для молодых, на ранней стадии заболевания",
              "< 8,0% — более мягкая цель для пожилых пациентов с множеством осложнений",
            ],
          },
          { p: "HbA1c рекомендуем измерять каждые 3–6 месяцев. При хорошей приверженности лечению достаточно раз в полгода; во время коррекции терапии или при плохо контролируемом диабете — каждые 3 месяца." },
          { h2: "Что HbA1c означает в значениях глюкозы крови?" },
          {
            ul: [
              "HbA1c 6% ≈ глюкоза в среднем 7 ммоль/л",
              "HbA1c 7% ≈ 8,6 ммоль/л",
              "HbA1c 8% ≈ 10,2 ммоль/л",
              "HbA1c 9% ≈ 11,8 ммоль/л",
              "HbA1c 10% ≈ 13,4 ммоль/л",
            ],
          },
          { p: "Каждое повышение HbA1c на 1% соответствует примерно 1,5 ммоль/л более высокому среднему уровню сахара." },
          { h2: "Ограничения" },
          { p: "HbA1c не всегда полностью надёжен. Он может быть неточным в следующих случаях:" },
          {
            ul: [
              "Анемия — самая частая причина, занижает HbA1c",
              "Гемоглобинопатии (серповидноклеточная анемия, талассемия)",
              "Недавнее кровотечение, переливание крови",
              "Хроническая почечная недостаточность",
              "Беременность — предпочитаем другие анализы (глюкоза натощак, OGTT)",
            ],
          },
          { p: "Поэтому HbA1c всегда оцениваем вместе с другими анализами, при необходимости измеряем глюкозу также дома глюкометром или с помощью CGM." },
          { h2: "Соотношение альбумин/креатинин (UACR)" },
          { p: "UACR — самый чувствительный анализ для выявления ранней диабетической нефропатии (поражения почек). В здоровых почках клубочки «достаточно плотные», чтобы удерживать белки (например, альбумин) в кровотоке. Когда высокий сахар повреждает стенки клубочков, альбумин начинает выводиться с мочой — это первый признак диабетической нефропатии." },
          { h2: "Значения UACR" },
          {
            ul: [
              "< 3 мг/ммоль (< 30 мг/г) — норма",
              "3–30 мг/ммоль (30–300 мг/г) — микроальбуминурия (ранняя нефропатия)",
              "> 30 мг/ммоль (> 300 мг/г) — макроальбуминурия (явная нефропатия)",
            ],
          },
          { p: "Анализ выполняется из разовой пробы мочи (предпочтительно утренняя первая моча). Один положительный результат не достаточен для диагноза — для подтверждения нужны 2 положительных анализа в течение 3–6 месяцев. UACR рекомендуем делать всем пациентам с диабетом раз в год." },
          { h2: "Креатинин сыворотки и eGFR" },
          { p: "Креатинин — продукт обмена мышц, который фильтруется почками из крови. На основе уровня креатинина рассчитывают eGFR (расчётную скорость клубочковой фильтрации), показывающую, насколько хорошо почки фильтруют:" },
          {
            ul: [
              "eGFR > 90 мл/мин — норма",
              "eGFR 60–89 — лёгкое снижение",
              "eGFR 45–59 — умеренная почечная недостаточность (стадия 3a)",
              "eGFR 30–44 — более тяжёлая почечная недостаточность (стадия 3b)",
              "eGFR 15–29 — тяжёлая почечная недостаточность (стадия 4)",
              "eGFR < 15 — терминальная стадия (требуется диализ)",
            ],
          },
          { h2: "Липидный профиль" },
          { p: "У пациентов с диабетом часто встречается дислипидемия — повышенный уровень холестерина. Это один из главных факторов риска инфаркта. Раз в год измеряем:" },
          {
            ul: [
              "Общий холестерин — < 4,5 ммоль/л",
              "ЛПНП-холестерин («плохой») — < 2,6 ммоль/л у пациентов с диабетом (< 1,8 ммоль/л при наличии осложнений)",
              "ЛПВП-холестерин («хороший») — > 1,0 ммоль/л у мужчин, > 1,3 ммоль/л у женщин",
              "Триглицериды — < 1,7 ммоль/л",
            ],
          },
          { p: "При высоком ЛПНП обычно требуется статин (аторвастатин, розувастатин) — он значительно снижает риск инфаркта и инсульта." },
          { h2: "Глюкоза натощак и случайная глюкоза" },
          {
            ul: [
              "Глюкоза натощак — после 8-часового голодания. Норма < 5,6 ммоль/л; преддиабет 5,6–6,9; диабет ≥ 7,0.",
              "Случайная глюкоза — измеренная в любое время. Для диагноза диабета ≥ 11,1 ммоль/л в сочетании с симптомами.",
            ],
          },
          { h2: "Тест толерантности к глюкозе (OGTT)" },
          { p: "OGTT — «золотой стандарт» диагностики диабета. Сначала измеряют глюкозу натощак (из венозной крови), затем пациент выпивает 75 г глюкозы и через 2 часа уровень глюкозы измеряется снова из венозной крови. Диабет диагностируется, если значение через 2 часа ≥ 11,1 ммоль/л. Также используется для диагностики гестационного диабета (на 24–28 неделе беременности)." },
          { h2: "Другие важные анализы" },
          {
            ul: [
              "TSH (функция щитовидной железы) — раз в год, так как заболевания щитовидной железы встречаются чаще у пациентов с диабетом",
              "Витамин B12 — особенно для пациентов, принимающих метформин, так как этот препарат может вызывать дефицит B12",
              "Печёночные ферменты (АЛТ, АСТ) — для оценки жирового гепатоза",
              "Общий анализ крови — для исключения анемии",
            ],
          },
          { h2: "В нашей клинике" },
          { p: "В Таллиннской клинике эндокринологии мы предлагаем все эти анализы. С направлением медицинской страховки анализы бесплатны." },
          { p: "Платные пакеты:" },
          {
            ul: [
              "Визит + 4 анализа (HbA1c, ЛПНП, ЛПВП, креатинин) — 105 €",
              "Визит + HbA1c + фото глазного дна — 110 €",
              "Отдельные анализы (биохимический / гормональный) — 15 € за единицу",
              "Тест толерантности к глюкозе (с тестовой жидкостью) — 40 €",
            ],
          },
        ],
      },
      en: {
        category: "Diagnostics",
        title: "Diabetes tests — A1c, albumin/creatinine ratio and other tests",
        summary:
          "Good diabetes management is based on regular tests. This article thoroughly explains the most important diabetes tests — HbA1c, albumin/creatinine ratio, lipid profile, creatinine — and what they are used for.",
        author: "Dr. Verner Fogel, endocrinologist",
        sections: [
          { h2: "Why are tests important?" },
          { p: "Diabetes is a \"silent\" disease — the patient may feel well even though blood sugar or blood pressure has been elevated for years and is damaging blood vessels and organs. Symptoms (deteriorating vision, foot ulcers, kidney failure) only appear when complications are already advanced. That is why regular monitoring through tests is the only way to objectively assess your condition and adjust treatment in time." },
          { h2: "HbA1c (glycated haemoglobin)" },
          { p: "HbA1c is the most important diabetes monitoring test. It shows your average blood sugar over the past 2–3 months. The principle: haemoglobin (a protein in red blood cells) is in constant contact with blood glucose. The higher the glucose, the more it \"sticks\" to haemoglobin, forming HbA1c. Since red cells live for about 120 days, HbA1c reflects average blood sugar over the past few months." },
          { h2: "HbA1c values and their meaning" },
          {
            ul: [
              "< 6.0% — normal",
              "6.0–6.4% — prediabetes",
              "≥ 6.5% — diabetes (diagnosis)",
              "< 7.0% — target for most diabetes patients",
              "< 6.5% — stricter target for younger patients in early disease",
              "< 8.0% — more lenient target for older patients with multiple complications",
            ],
          },
          { p: "We recommend measuring HbA1c every 3–6 months. With good treatment adherence, every six months is enough; during treatment changes or poorly controlled diabetes — every 3 months." },
          { h2: "What does HbA1c mean in blood sugar values?" },
          {
            ul: [
              "HbA1c 6% ≈ average glucose 7 mmol/L",
              "HbA1c 7% ≈ 8.6 mmol/L",
              "HbA1c 8% ≈ 10.2 mmol/L",
              "HbA1c 9% ≈ 11.8 mmol/L",
              "HbA1c 10% ≈ 13.4 mmol/L",
            ],
          },
          { p: "Every 1% rise in HbA1c corresponds to roughly 1.5 mmol/L higher average blood sugar." },
          { h2: "Limitations" },
          { p: "HbA1c is not always fully reliable. It can be inaccurate in the following cases:" },
          {
            ul: [
              "Anaemia — the most common reason, lowers HbA1c",
              "Haemoglobinopathies (sickle cell anaemia, thalassaemia)",
              "Recent bleeding, blood transfusion",
              "Chronic kidney failure",
              "Pregnancy — we prefer other tests (fasting glucose, OGTT)",
            ],
          },
          { p: "That is why we always evaluate HbA1c together with other tests and, when needed, also measure blood sugar at home with a glucometer or CGM." },
          { h2: "Albumin/creatinine ratio (UACR)" },
          { p: "UACR is the most sensitive test for detecting early diabetic nephropathy (kidney damage). In healthy kidneys the glomeruli are \"tight enough\" to keep proteins (such as albumin) in the bloodstream. When high blood sugar damages the glomerular walls, albumin starts leaking into the urine — the first sign of diabetic nephropathy." },
          { h2: "UACR values" },
          {
            ul: [
              "< 3 mg/mmol (< 30 mg/g) — normal",
              "3–30 mg/mmol (30–300 mg/g) — microalbuminuria (early nephropathy)",
              "> 30 mg/mmol (> 300 mg/g) — macroalbuminuria (overt nephropathy)",
            ],
          },
          { p: "The test is performed on a single urine sample (preferably the first morning urine). One positive result is not enough for diagnosis — confirmation requires 2 positive tests within 3–6 months. We recommend UACR for all diabetes patients once a year." },
          { h2: "Serum creatinine and eGFR" },
          { p: "Creatinine is a waste product of muscle metabolism filtered out of the blood by the kidneys. From the serum creatinine level we calculate the eGFR (estimated glomerular filtration rate), which shows how well the kidneys are filtering:" },
          {
            ul: [
              "eGFR > 90 mL/min — normal",
              "eGFR 60–89 — mild decline",
              "eGFR 45–59 — moderate kidney failure (stage 3a)",
              "eGFR 30–44 — more severe kidney failure (stage 3b)",
              "eGFR 15–29 — severe kidney failure (stage 4)",
              "eGFR < 15 — end stage (requires dialysis)",
            ],
          },
          { h2: "Lipid profile" },
          { p: "Patients with diabetes often have dyslipidaemia — elevated cholesterol levels. This is one of the biggest risk factors for heart attack. We measure once a year:" },
          {
            ul: [
              "Total cholesterol — < 4.5 mmol/L",
              "LDL cholesterol (\"bad\") — < 2.6 mmol/L in diabetes patients (< 1.8 mmol/L if complications are already present)",
              "HDL cholesterol (\"good\") — > 1.0 mmol/L in men, > 1.3 mmol/L in women",
              "Triglycerides — < 1.7 mmol/L",
            ],
          },
          { p: "If LDL is high, a statin (atorvastatin, rosuvastatin) is usually needed — it significantly reduces the risk of heart attack and stroke." },
          { h2: "Fasting and random blood glucose" },
          {
            ul: [
              "Fasting glucose — after 8 hours of fasting. Normal < 5.6 mmol/L; prediabetes 5.6–6.9; diabetes ≥ 7.0.",
              "Random glucose — measured at any time. For diabetes diagnosis ≥ 11.1 mmol/L together with symptoms.",
            ],
          },
          { h2: "Oral glucose tolerance test (OGTT)" },
          { p: "OGTT is the \"gold standard\" for diagnosing diabetes. First, fasting glucose is measured (from venous blood), then the patient drinks 75 g of glucose and 2 hours later venous blood glucose is measured again. Diabetes is diagnosed when the 2-hour value is ≥ 11.1 mmol/L. It is also used to diagnose gestational diabetes (at 24–28 weeks of pregnancy)." },
          { h2: "Other important tests" },
          {
            ul: [
              "TSH (thyroid function) — once a year, as thyroid disease is more common in diabetes patients",
              "Vitamin B12 — especially in patients taking metformin, since this drug can cause B12 deficiency",
              "Liver enzymes (ALT, AST) — to assess fatty liver",
              "Complete blood count — to rule out anaemia",
            ],
          },
          { h2: "At our clinic" },
          { p: "At Tallinn Endocrinology Clinic we offer all of these tests. With a referral from your health insurance the tests are free of charge." },
          { p: "Paid packages:" },
          {
            ul: [
              "Visit + 4 tests (HbA1c, LDL, HDL, creatinine) — €105",
              "Visit + HbA1c + retinal photography — €110",
              "Individual tests (biochemistry / hormones) — €15 each",
              "Oral glucose tolerance test (incl. test drink) — €40",
            ],
          },
        ],
      },
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "diabeedi-ravimid",
    slug: "diabeedi-ravimid",
    category: "Ravi",
    title: "Erinevad diabeedi ravimid ja kuidas nad toimivad",
    image: "/api/static/ai-images/articles/diabeedi-ravimid.webp",
    summary:
      "Diabeedi raviks on tänapäeval saadaval rohkem kui 50 erinevat ravimit, mis kuuluvad 10 erinevasse ravimirühma. Iga ravim toimib veresuhkrule erineval viisil — sellepärast on tihti vaja kombineerida mitut ravimit. Käesolev artikkel selgitab põhjalikult, kuidas iga ravimirühm toimib ja kellele see sobib.",
    readingMinutes: 10,
    updated: "2026-06-13",
    author: DR_AUTHOR,
    sections: [
      { h2: "Sissejuhatus" },
      {
        p: "Diabeedi ravi on viimase 15 aasta jooksul dramaatiliselt muutunud. Veel hiljuti olid valikus peamiselt metformiin, sulfonüüluuread ja insuliin. Tänapäeval on saadaval uued ravimirühmad, mis lisaks veresuhkru kontrollile kaitsevad ka südant ja neere ning aitavad kehakaalu langetada.",
      },
      {
        p: "Iga ravimi valik sõltub patsiendi profiilist: vanus, kehakaal, tüsistused, kaasuvad haigused, hüpoglükeemia risk ja patsiendi eelistused. Sellepärast on raviplaani koostamine alati individuaalne.",
      },
      { h2: "1. Metformiin" },
      {
        p: "Esmavalik 2. tüüpi diabeedi ravis. Vähendab maksa glükoosi tootmist ja parandab insuliinitundlikkust.",
      },
      {
        ul: [
          "Plussid: odav, ei põhjusta hüpoglükeemiat, kaitseb südant, kaalu suhtes neutraalne või isegi langetab kaalu",
          "Miinused: seedetrakti vaevused (kõhulahtisus, iiveldus) algul, võib põhjustada B12 vaegust pikaaegsel kasutamisel",
          "Annus: 1500–2000 mg päevas, jagatuna 1–3 annuseks",
          "Vastunäidustused: raske neerupuudulikkus (eGFR < 30), maksapuudulikkus",
        ],
      },
      { h2: "2. SGLT2-inhibiitorid" },
      {
        p: "Empagliflosiin (Jardiance), dapagliflosiin (Forxiga), kanagliflosiin (Invokana). Toimivad neerudes, blokeerides glükoosi tagasiimendumist — glükoos väljub uriiniga.",
      },
      {
        ul: [
          "Plussid: vähendavad veresuhkrut, langetavad kehakaalu (2–4 kg), langetavad vererõhku, vähendavad südamepuudulikkuse ja kroonilise neeruhaiguse progresseerumist. Tugev südame- ja neerukaitsev toime.",
          "Miinused: kuseteede ja suguelundite infektsioonide risk, harv risk diabeetilise ketoatsidoosi tekkeks",
          "Sobib eriti: patsiendile, kel on südamepuudulikkus, nefropaatia või ülekaal",
          "Vastunäidustused: 1. tüüpi diabeet (sageli), raske neerupuudulikkus",
        ],
      },
      { h2: "3. GLP-1 retseptori agonistid" },
      {
        p: "Liraglutiid (Victoza), semaglutiid (Ozempic, Rybelsus), dulaglutiid (Trulicity), tirzepatiid (Mounjaro — kombinatsioon GLP-1 ja GIP). Süstitavad (üks ravim, semaglutiid Rybelsus, on ka tabletina) hormoonid, mis matkivad keha enda GLP-1 hormooni.",
      },
      {
        ul: [
          "Toimemehhanism: stimuleerivad insuliini tootmist, kui veresuhkur on kõrge; vähendavad glükagooni taset; aeglustavad mao tühjenemist; vähendavad söögiisu",
          "Plussid: tugev veresuhkrut langetav toime, oluline kehakaalu langus (5–15%), südant kaitsev toime.",
          "Miinused: kallid, iiveldus algul, harvad seedeprobleemid, võimalik pankreatiidi risk",
          "Sobib: kõrge HbA1c-ga patsientidele, ülekaalulistele, südameinfarkti riskigrupi patsientidele",
        ],
      },
      {
        callout: {
          type: "warning",
          title: "Tallinna Endokrinoloogia Kliiniku reegel",
          body:
            "Me ei kirjuta Ozempicu, Rybelsuse ega Mounjaro retsepte kaalulangetuse eesmärgil. Lisaks ei tegele me ülekaalu raviga, kui patsient pole iseseisvalt langetanud vähemalt 12 kg kehakaalu, pole muutnud söömisharjumusi ja ei käi trennis vähemalt kolm korda nädalas.",
        },
      },
      { h2: "4. DPP-4 inhibiitorid" },
      {
        p: "Sitagliptiin (Januvia), linagliptiin (Trajenta), vildagliptiin (Galvus). Blokeerivad ensüümi DPP-4, mis lagundab keha enda GLP-1 hormooni — seega on tegemist nõrgema versiooniga GLP-1 agonistidest.",
      },
      {
        ul: [
          "Plussid: tabletina, hästi talutav, kaalu suhtes neutraalne, ei põhjusta hüpoglükeemiat",
          "Miinused: nõrgem toime kui GLP-1 agonistid, ei langeta kaalu, ei kaitse aktiivselt südant",
          "Sobib: kerge kuni mõõduka diabeedi puhul, eakatele patsientidele",
        ],
      },
      { h2: "5. Sulfonüüluuread" },
      {
        p: "Gliklasiid (Diaprel MR), glimepiriid (Amaryl). Vanimad ravimid 2. tüüpi diabeedi raviks. Stimuleerib kõhunääret rohkem insuliini tootma.",
      },
      {
        ul: [
          "Plussid: odav, tugev veresuhkrut langetav toime",
          "Miinused: hüpoglükeemia risk, kehakaalu tõus 2–5 kg, ravimi efektiivsus väheneb aja jooksul (beeta-rakkude kurnatus)",
          "Sobib: piiratud eelarvega patsientidele, kui muud ravimid pole saadaval",
        ],
      },
      { h2: "6. Tiasolidiindioonid (TZD)" },
      {
        p: "Pioglitasoon (Actos). Parandavad insuliinitundlikkust lihastes ja maksas.",
      },
      {
        ul: [
          "Plussid: tugev insuliinitundlikkuse parandamine, võib parandada rasvmaksa",
          "Miinused: kaalu tõus, vedelikupeetus, südamepuudulikkuse risk, suurenenud osteoporoosi risk",
          "Kasutatakse harvemini tänapäeval",
        ],
      },
      { h2: "7. Insuliin" },
      {
        p: "Insuliin on kõige võimsam ravim — sobib kõigile diabeedi tüüpidele. Tüübid:",
      },
      {
        ul: [
          "Kiiretoimeline (lispro, aspart, glulisiin) — toime 5–15 min, kestab 3–5 h. Söögikorra eel.",
          "Pikatoimeline (glargiin, detemir, degludec) — toime 1–2 h, kestab 24–42 h. Baasinsuliin, 1–2 süsti päevas.",
          "Eelnevalt segatud (mixed) — kombinatsioon kesk- ja kiiretoimelisest. Lihtsam režiim, kuid vähem paindlik.",
        ],
      },
      {
        ul: [
          "Plussid: alati toimib, võimas, vajalik 1. tüüpi diabeedi puhul",
          "Miinused: süstitav, hüpoglükeemia risk, kehakaalu tõus",
        ],
      },
      { h2: "8. Kombineeritud ravim — kuidas seda planeeritakse?" },
      {
        p: "Enamik 2. tüüpi diabeedi patsiente vajab aja jooksul kahte või rohkemat ravimit. Tüüpiline kombinatsioon võib olla:",
      },
      {
        ul: [
          "Metformiin + SGLT2-inhibiitor (näiteks 2. tüüpi diabeet ülekaalulisel patsiendil)",
          "Metformiin + GLP-1 agonist (näiteks 2. tüüpi diabeet kõrge kardiovaskulaarse riskiga patsiendil)",
          "Metformiin + DPP-4 inhibiitor (näiteks eakatele lihtsa režiimi puhul)",
          "Metformiin + SGLT2-inhibiitor + baasinsuliin (kui beeta-rakkude funktsioon väheneb)",
        ],
      },
      { h2: "9. Uued ravimid horisonail" },
      {
        p: "Diabeedi ravim on kiiresti arenev valdkond. Lähitulevikus tulevad turule:",
      },
      {
        ul: [
          "Triagoonid (GLP-1 + GIP + glükagooni — retatrutiid) — võimsam kehakaalu langus kui Mounjaro",
          "Oraalsed GLP-1 agonistid — Rybelsus on juba olemas, lisanduvad uued tabletid",
          "Pidev glükoosimonitooring (CGM) ja suletud-süsteemse \"tehispankrease\" pumbad — muudavad 1. tüüpi diabeedi haldamise oluliselt lihtsamaks",
        ],
      },
      { h2: "Meie kliinikus" },
      {
        p: "Tallinna Endokrinoloogia Kliinikus valime sulle just need ravimid, mis sobivad sinu profiilile parimini — võttes arvesse kõik kõrvalmõjud, kaasuvad haigused ja sinu enda eelistused. Endokrinoloog hindab sinu seisundit terviklikult ning planeerib raviplaani, mis sind kõige paremini aitab. Broneeri vastuvõtt — saame koos sinu jaoks parima lahenduse leida.",
      },
    ],
    translations: {
      ru: {
        category: "Лечение",
        title: "Различные препараты при диабете и как они работают",
        summary:
          "Сегодня для лечения диабета доступно более 50 различных препаратов, относящихся к 10 группам. Каждый препарат действует на сахар крови по-своему — поэтому часто требуется комбинация нескольких. В этой статье подробно объясняется, как работает каждая группа препаратов и кому она подходит.",
        author: "Д-р Вернер Фогель, эндокринолог",
        sections: [
          { h2: "Введение" },
          { p: "Лечение диабета за последние 15 лет значительно изменилось. Ещё недавно выбор сводился в основном к метформину, производным сульфонилмочевины и инсулину. Сегодня доступны новые группы препаратов, которые помимо контроля сахара также защищают сердце и почки и помогают снижать вес." },
          { p: "Выбор препарата зависит от профиля пациента: возраст, вес, осложнения, сопутствующие заболевания, риск гипогликемии и предпочтения самого пациента. Поэтому план лечения всегда индивидуален." },
          { h2: "1. Метформин" },
          { p: "Препарат первого выбора при СД2. Снижает выработку глюкозы в печени и улучшает чувствительность к инсулину." },
          {
            ul: [
              "Плюсы: дешёвый, не вызывает гипогликемии, защищает сердце, нейтрален к весу или даже снижает его",
              "Минусы: в начале лечения — желудочно-кишечные расстройства (диарея, тошнота), при длительном приёме может вызывать дефицит B12",
              "Доза: 1500–2000 мг в день, разделённая на 1–3 приёма",
              "Противопоказания: тяжёлая почечная недостаточность (eGFR < 30), печёночная недостаточность",
            ],
          },
          { h2: "2. Ингибиторы SGLT2" },
          { p: "Эмпаглифлозин (Jardiance), дапаглифлозин (Forxiga), канаглифлозин (Invokana). Действуют в почках, блокируя обратное всасывание глюкозы — глюкоза выводится с мочой." },
          {
            ul: [
              "Плюсы: снижают сахар, уменьшают вес (2–4 кг) и артериальное давление, замедляют прогрессирование сердечной недостаточности и хронической болезни почек. Мощное кардио- и нефропротекторное действие.",
              "Минусы: риск инфекций мочевыводящих путей и половых органов, редкий риск диабетического кетоацидоза",
              "Подходят: особенно пациентам с сердечной недостаточностью, нефропатией или избыточным весом",
              "Противопоказания: СД1, тяжёлая почечная недостаточность",
            ],
          },
          { h2: "3. Агонисты рецепторов GLP-1" },
          { p: "Лираглутид (Victoza), семаглутид (Ozempic, Rybelsus), дулаглутид (Trulicity), тирзепатид (Mounjaro — комбинация GLP-1 и GIP). Инъекционные (один препарат, семаглутид Rybelsus, также в форме таблеток) гормоны, имитирующие собственный GLP-1 организма." },
          {
            ul: [
              "Механизм действия: стимулируют выработку инсулина при повышенном сахаре; снижают уровень глюкагона; замедляют опорожнение желудка; снижают аппетит",
              "Плюсы: мощное сахароснижающее действие, значительное снижение веса (5–15%), кардиопротекторное действие",
              "Минусы: дорого, побочные эффекты — тошнота в начале лечения, иногда ЖКТ-расстройства, возможный риск панкреатита",
              "Подходят: пациентам с высоким HbA1c, с избыточным весом, с высоким риском инфаркта",
            ],
          },
          {
            callout: {
              type: "warning",
              title: "Правило Таллиннской клиники эндокринологии",
              body:
                "Мы не выписываем рецепты на Ozempic, Rybelsus или Mounjaro с целью снижения веса. Кроме того, мы не занимаемся лечением избыточного веса, если пациент не снизил вес хотя бы на 12 кг самостоятельно, не изменил привычки питания и не ходит на тренировки минимум 3 раза в неделю.",
            },
          },
          { h2: "4. Ингибиторы DPP-4" },
          { p: "Ситаглиптин (Januvia), линаглиптин (Trajenta), вилдаглиптин (Galvus). Блокируют фермент DPP-4, который расщепляет собственный GLP-1 организма — таким образом представляют собой более слабую версию агонистов GLP-1." },
          {
            ul: [
              "Плюсы: в форме таблеток, хорошо переносятся, нейтральны к весу, не вызывают гипогликемии",
              "Минусы: эффект слабее, чем у агонистов GLP-1, не снижают вес, активно не защищают сердце",
              "Подходят: при лёгком и умеренном диабете, пожилым пациентам",
            ],
          },
          { h2: "5. Производные сульфонилмочевины" },
          { p: "Гликлазид (Diaprel MR), глимепирид (Amaryl). Самые старые препараты для лечения СД2. Стимулируют поджелудочную железу вырабатывать больше инсулина." },
          {
            ul: [
              "Плюсы: дешёвые, сильное сахароснижающее действие",
              "Минусы: риск гипогликемии, прибавка в весе 2–5 кг, со временем эффективность снижается (истощение бета-клеток)",
              "Подходят: пациентам с ограниченным бюджетом, когда другие препараты недоступны",
            ],
          },
          { h2: "6. Тиазолидиндионы (TZD)" },
          { p: "Пиоглитазон (Actos). Улучшает чувствительность к инсулину в мышцах и печени." },
          {
            ul: [
              "Плюсы: мощное улучшение чувствительности к инсулину, может улучшать состояние при жировом гепатозе",
              "Минусы: прибавка в весе, задержка жидкости, риск сердечной недостаточности, повышенный риск остеопороза",
              "Сегодня используются реже",
            ],
          },
          { h2: "7. Инсулин" },
          { p: "Инсулин — самый мощный препарат, подходит для всех типов диабета." },
          { p: "Типы инсулина:" },
          {
            ul: [
              "Быстродействующие (лизпро, аспарт, глулизин) — действие через 5–15 мин, длится 3–5 часов. Вводят перед приёмом пищи.",
              "Длительного действия (гларгин, детемир, деглудек) — действие через 1–2 часа, длится 24–42 часа. Это базальные инсулины, 1–2 инъекции в день.",
              "Предварительно смешанные (микс-инсулины) — комбинация средне- и быстродействующего инсулина. Более простой режим, но менее гибкий.",
            ],
          },
          {
            ul: [
              "Плюсы: всегда работает, мощный, необходим при СД1",
              "Минусы: инъекционный, риск гипогликемии, прибавка в весе",
            ],
          },
          { h2: "8. Комбинированная терапия — как её планируют?" },
          { p: "Большинству пациентов с СД2 со временем требуется два или более препаратов. Типичные комбинации:" },
          {
            ul: [
              "Метформин + ингибитор SGLT2 (например, СД2 у пациента с избыточным весом)",
              "Метформин + агонист GLP-1 (например, СД2 у пациента с высоким сердечно-сосудистым риском)",
              "Метформин + ингибитор DPP-4 (например, пожилым для простого режима)",
              "Метформин + ингибитор SGLT2 + базальный инсулин (при снижении функции бета-клеток)",
            ],
          },
          { h2: "9. Новые препараты на подходе" },
          { p: "Лечение диабета — быстро развивающаяся область. В ближайшем будущем на рынок выйдут:" },
          {
            ul: [
              "Тригонисты (GLP-1 + GIP + глюкагон — ретатрутид) — более мощное снижение веса, чем у Mounjaro",
              "Пероральные агонисты GLP-1 — Rybelsus уже доступен, в разработке новые таблетки",
              "Непрерывный мониторинг глюкозы (CGM) и инсулиновые помпы — значительно упрощают управление СД1",
            ],
          },
          { h2: "В нашей клинике" },
          { p: "В Таллиннской клинике эндокринологии мы подбираем именно те препараты, которые лучше всего подходят вашему профилю — учитывая все побочные эффекты, сопутствующие заболевания и ваши собственные предпочтения. Эндокринолог комплексно оценит ваше состояние и составит план лечения, который поможет вам наилучшим образом. Запишитесь на приём — вместе мы найдём оптимальное решение." },
        ],
      },
      en: {
        category: "Treatment",
        title: "Different diabetes drugs and how they work",
        summary:
          "Today, more than 50 different medications are available for diabetes treatment, belonging to 10 different drug classes. Each drug acts on blood sugar in a different way — that is why several drugs are often combined. This article thoroughly explains how each class works and who it suits.",
        author: "Dr. Verner Fogel, endocrinologist",
        sections: [
          { h2: "Introduction" },
          { p: "Diabetes treatment has changed remarkably over the past 15 years. Until recently the choice was mainly metformin, sulfonylureas and insulin. Today new drug classes are available that, in addition to blood sugar control, also protect the heart and kidneys and help with weight loss." },
          { p: "The choice of drug depends on the patient's profile: age, weight, complications, comorbidities, hypoglycaemia risk and patient preference. That is why a treatment plan is always individual." },
          { h2: "1. Metformin" },
          { p: "The first-line choice for type 2 diabetes. Reduces glucose production in the liver and improves insulin sensitivity." },
          {
            ul: [
              "Pros: inexpensive, does not cause hypoglycaemia, protects the heart, weight-neutral or even reduces weight",
              "Cons: initial gastrointestinal side effects (diarrhoea, nausea), may cause B12 deficiency in long-term use",
              "Dose: 1500–2000 mg per day, in 1–3 divided doses",
              "Contraindications: severe kidney failure (eGFR < 30), liver failure",
            ],
          },
          { h2: "2. SGLT2 inhibitors" },
          { p: "Empagliflozin (Jardiance), dapagliflozin (Forxiga), canagliflozin (Invokana). They act in the kidneys, blocking the reabsorption of glucose — glucose is excreted in the urine." },
          {
            ul: [
              "Pros: lower blood sugar, reduce body weight (2–4 kg) and blood pressure, slow the progression of heart failure and chronic kidney disease. Strong cardio- and renal-protective effect.",
              "Cons: risk of urinary and genital infections, rare risk of diabetic ketoacidosis",
              "Suitable for: especially patients with heart failure, nephropathy or excess weight",
              "Contraindications: type 1 diabetes, severe kidney failure",
            ],
          },
          { h2: "3. GLP-1 receptor agonists" },
          { p: "Liraglutide (Victoza), semaglutide (Ozempic, Rybelsus), dulaglutide (Trulicity), tirzepatide (Mounjaro — a GLP-1 and GIP combination). Injectable (one drug, semaglutide Rybelsus, is also available as a tablet) hormones that mimic the body's own GLP-1 hormone." },
          {
            ul: [
              "Mechanism: stimulate insulin production when blood sugar is high; reduce glucagon levels; slow gastric emptying; reduce appetite",
              "Pros: powerful glucose-lowering effect, significant weight loss (5–15%), heart-protective effect",
              "Cons: expensive, side effects — nausea when starting treatment, occasionally gastrointestinal problems, potential pancreatitis risk",
              "Suitable for: patients with high HbA1c, overweight patients, patients at high risk of heart attack",
            ],
          },
          {
            callout: {
              type: "warning",
              title: "Tallinn Endocrinology Clinic rule",
              body:
                "We do not write prescriptions for Ozempic, Rybelsus or Mounjaro for the purpose of weight loss. We also do not treat excess weight unless the patient has first lost at least 12 kg on their own, changed their eating habits and is exercising at least three times a week.",
            },
          },
          { h2: "4. DPP-4 inhibitors" },
          { p: "Sitagliptin (Januvia), linagliptin (Trajenta), vildagliptin (Galvus). They block the DPP-4 enzyme that breaks down the body's own GLP-1 hormone — so they are effectively a weaker version of GLP-1 agonists." },
          {
            ul: [
              "Pros: tablet form, well tolerated, weight-neutral, do not cause hypoglycaemia",
              "Cons: weaker effect than GLP-1 agonists, do not reduce weight, do not actively protect the heart",
              "Suitable for: mild to moderate diabetes, elderly patients",
            ],
          },
          { h2: "5. Sulfonylureas" },
          { p: "Gliclazide (Diaprel MR), glimepiride (Amaryl). The oldest drugs for type 2 diabetes. They stimulate the pancreas to produce more insulin." },
          {
            ul: [
              "Pros: inexpensive, strong glucose-lowering effect",
              "Cons: risk of hypoglycaemia, 2–5 kg weight gain, effectiveness wanes over time (beta-cell exhaustion)",
              "Suitable for: patients with a limited budget when other drugs are not available",
            ],
          },
          { h2: "6. Thiazolidinediones (TZD)" },
          { p: "Pioglitazone (Actos). Improves insulin sensitivity in muscles and the liver." },
          {
            ul: [
              "Pros: strong improvement of insulin sensitivity, can improve fatty liver",
              "Cons: weight gain, fluid retention, risk of heart failure, increased osteoporosis risk",
              "Used less commonly today",
            ],
          },
          { h2: "7. Insulin" },
          { p: "Insulin is the most powerful drug — suitable for all types of diabetes." },
          { p: "Types of insulin:" },
          {
            ul: [
              "Rapid-acting (lispro, aspart, glulisine) — onset 5–15 min, duration 3–5 hours. Injected before meals.",
              "Long-acting (glargine, detemir, degludec) — onset 1–2 hours, duration 24–42 hours. These are basal insulins, 1–2 injections per day.",
              "Pre-mixed insulins — a combination of intermediate- and rapid-acting insulin. A simpler regimen but less flexible.",
            ],
          },
          {
            ul: [
              "Pros: always works, powerful, mandatory in type 1 diabetes",
              "Cons: injectable, risk of hypoglycaemia, weight gain",
            ],
          },
          { h2: "8. Combination therapy — how it is planned" },
          { p: "Most patients with type 2 diabetes need two or more drugs over time. Typical combinations include:" },
          {
            ul: [
              "Metformin + SGLT2 inhibitor (e.g. type 2 diabetes in an overweight patient)",
              "Metformin + GLP-1 agonist (e.g. type 2 diabetes in a patient at high cardiovascular risk)",
              "Metformin + DPP-4 inhibitor (e.g. for the elderly when a simple regimen is needed)",
              "Metformin + SGLT2 inhibitor + basal insulin (when beta-cell function declines)",
            ],
          },
          { h2: "9. New drugs coming" },
          { p: "Diabetes treatment is a rapidly developing field. Coming to market in the near future:" },
          {
            ul: [
              "Triple agonists (GLP-1 + GIP + glucagon — retatrutide) — even greater weight loss than with Mounjaro",
              "Oral GLP-1 agonists — Rybelsus is already available, more tablets are coming",
              "Continuous glucose monitoring (CGM) and insulin pumps — significantly easier management of type 1 diabetes",
            ],
          },
          { h2: "At our clinic" },
          { p: "At Tallinn Endocrinology Clinic we select exactly the drugs that suit your profile best — taking into account all side effects, comorbidities and your own preferences. The endocrinologist will assess your status holistically and plan a treatment programme that helps you the most. Book an appointment — together we will find the best solution for you." },
        ],
      },
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "soomine-liikumine-kehakaalu-langetamine",
    slug: "soomine-liikumine-kehakaalu-langetamine",
    category: "Elustiil",
    title: "Söömine, liikumine ja kehakaalu langetamine",
    image: "/api/static/ai-images/articles/soomine-liikumine-kehakaalu-langetamine.webp",
    summary:
      "Elustiilimuutused on diabeedi ravi alus. Õige toitumine, regulaarne liikumine ja sobiv kehakaal vähendavad oluliselt veresuhkrut, vererõhku ja kolesterooli — sageli rohkem kui ükski tablett. Käesolev artikkel pakub praktilisi soovitusi diabeedihaigele või prediabeediga inimesele.",
    readingMinutes: 11,
    updated: "2026-06-13",
    author: DR_AUTHOR,
    sections: [
      { h2: "Miks elustiil on tähtsam kui tabletid?" },
      {
        p: "Mitmed uuringud on näidanud, et elustiilimuutus on kõige tugevam diabeedi ennetusvahend. Diabetes Prevention Program (DPP) uuringus vähendas 7% kehakaalu langus ja 150 min liikumist nädalas 2. tüüpi diabeedi tekkeriski 58%, võrreldes 31%-ga metformiini grupis. DiRECT uuring (UK) näitas, et oluline kehakaalu langus (15+ kg) viib peaaegu pooltel patsientidel 2. tüüpi diabeedi remissiooni — ilma ravimiteta.",
      },
      {
        p: "Sellepärast on iga ravi alus elustiilimuutus — see toimib lihtsalt, odavalt ja efektiivselt. Tabletid ja insuliin on mõnikord vajalikud, kuid need ei asenda elustiilimuutust.",
      },
      { h2: "I. Toitumine" },
      {
        p: "Diabeedihaige toitumine ei ole \"eridieet\" — see on lihtsalt tervislik toitumine, mida soovitatakse kõigile. Põhiprintsiibid on:",
      },
      { h2: "1. Süsivesikute jälgimine" },
      {
        p: "Süsivesikud tõstavad veresuhkrut kõige tugevamalt. Pole vaja neid täielikult vältida — keha vajab neid energiana. Kuid oluline on:",
      },
      {
        ul: [
          "Eelista täisteratooteid ja jälgida soovituslikku tarbimise kogust (rukkileib, kaerahelbed, pruun riis, tatra) valgele leivale, saiakestele, valgele riisile. NB! Säilita kaine mõistus: kui veresuhkur oli juba enne söömist kõrge, siis pole näiteks leiba vaja juurde süüa, et veresuhkru läheks veelgi kõrgemaks!",
          "Väldi rafineeritud suhkrut ja siirupit — magusad joogid (Coca-Cola, mahl), maiustused, kondiitritooted.",
          "Söö puuvilju mõõdukalt — eelista terveid puuvilju (mitte mahla). 1–2 portsjonit päevas. Marjad on eelistatud (madalam glükeemiline indeks).",
          "Köögiviljad on praktiliselt piiramatud — kapsad, kurgid, tomatid, lehtsalatid, brokoli, lillkapsas, paprika. Kartulit ja maguskartulit mõõdukalt.",
          "Kaunviljad (oad, läätsed, kikerherned) on suurepärased — palju kiudaineid, valku, madala glükeemilise indeksiga.",
        ],
      },
      { h2: "2. Taldrikureegel" },
      {
        p: "Lihtne reegel: kujutle oma taldrik jagatuna kolmeks osaks:",
      },
      {
        ul: [
          "1/2 taldrikut: köögiviljad ja lehtsalatid (tomat, kurk, brokoli, lillkapsas, paprika, kapsad)",
          "1/4 taldrikut: valguallikas (kala, kana, lahja liha, muna, juust, tofu, kaunviljad)",
          "1/4 taldrikut: täisteratooted (rukkileib, kvinoa, tatra) või kartulit (jahutatult)",
          "Lisand: 1 portsjon piimatoodet (jogurt, keefir, juust)",
          "Magustoidu asemel värsked marjad või väike kogus pähkleid",
        ],
      },
      { h2: "3. Söögikorrad ja -ajad" },
      {
        ul: [
          "Söö 3 põhisööki päevas (hommikusöök, lõuna, õhtusöök) — pikemad söögivahed aitavad insuliinitundlikkust parandada.",
          "Väldi vahepalasid, eriti magusaid. Kui vahepala vaja, eelista pähkleid, juustu või köögivilja.",
          "Söö rahulikult — ajule kulub 20 minutit, et tunda küllastust.",
          "Õhtul söö 3 tundi enne magamaminekut.",
        ],
      },
      { h2: "4. Joogid" },
      {
        ul: [
          "Vesi on parim valik — joo 1,5–2 liitrit päevas",
          "Tee ja kohv ilma suhkruta on lubatud (kohv mõõdukalt)",
          "Mahlad ja gaseeritud joogid — VÄLDI, sisaldavad palju suhkrut",
          "Alkohol — mõõdukalt (max 1 jook/päev naistele, 2 meestele). Tähelepanu: alkohol võib põhjustada hüpoglükeemiat, eriti insuliinravil olevatel patsientidel.",
        ],
      },
      { h2: "5. Vahemere dieet" },
      {
        p: "Tugevaim teaduslik tõend toetab Vahemere stiilis toitumist diabeedi ennetuses ja ravis:",
      },
      {
        ul: [
          "Palju köögivilja ja puuvilja",
          "Täisteratooted",
          "Oliiviõli põhilise rasvana",
          "Kala (eriti rasvased kalad — lõhe, makrell, sardiinid) 2–3 korda nädalas",
          "Pähklid (4–5 korda nädalas, peotäis)",
          "Kaunviljad (4–5 korda nädalas)",
          "Mõõdukalt piimatooteid ja kana",
          "Vähe punase liha (1 kord nädalas või vähem)",
          "Vein mõõdukalt söögi kõrvale (kui üldse)",
        ],
      },
      { h2: "II. Liikumine" },
      {
        p: "Liikumine on diabeedi ravis sama tähtis kui toitumine. Selle mõjud on:",
      },
      {
        ul: [
          "Lihased võtavad veresuhkrut kasutusse ilma insuliinita — veresuhkur langeb juba paari tunni jooksul pärast trenni",
          "Pikaajaliselt suurendab insuliinitundlikkust (üks treening parandab insuliinitundlikkust 24–72 tundi)",
          "Langetab vererõhku ja kolesterooli",
          "Aitab kehakaalu langetada ja säilitada",
          "Vähendab depressiooni ja parandab und",
          "Vähendab südameinfarkti riski",
        ],
      },
      { h2: "Aeroobne treening" },
      {
        p: "Soovitus on 150 minutit mõõdukat aeroobset koormust nädalas, jagatuna vähemalt 3 päevale. Mõõdukas tähendab: südamerütm tõuseb, sa hingad sügavamalt, kuid saad veel rääkida. Näiteks:",
      },
      {
        ul: [
          "Kiirkõnd (5–6 km/h) — 30 min × 5 päeva nädalas",
          "Rattasõit",
          "Ujumine",
          "Tantsimine",
          "Suusatamine, uisutamine",
        ],
      },
      { h2: "Jõutreening" },
      {
        p: "Lisaks aeroobsele treeningule on soovituslik 2–3 korda nädalas jõutreening, mis hoiab lihasmassi. Lihaskude on metaboolselt aktiivne — see kasutab veresuhkrut isegi puhkeolekus. Näiteks:",
      },
      {
        ul: [
          "Kummiga harjutused kodus",
          "Hantliga ja sangpommiga harjutused kodus või jõusaalis",
          "Jõumasinad jõusaalis",
          "Funktsionaalsed harjutused — kükid, kätekõrverdused, lõuatõmbed, plank",
        ],
      },
      {
        callout: {
          type: "warning",
          title: "Veresuhkur ja treening",
          body:
            "Insuliini süstivad patsiendid peaksid mõõtma veresuhkrut enne ja pärast treeningut. Kui veresuhkur enne treeningut on alla 5 mmol/L, söö enne väike snäkk (15g süsivesikuid). Pikem treening (üle 1 h) võib veresuhkrut langetada ka 24 h jooksul pärast treeningut — eriti öösel. Kohanda insuliini doosi vastavalt — diabeediõde aitab seda planeerida.",
        },
      },
      { h2: "Igapäevane aktiivsus" },
      {
        p: "Lisaks ametlikele treeningutele on tähtis ka igapäevane aktiivsus. Eesmärk on 7000–10 000 sammu päevas. Lihtsad viisid:",
      },
      {
        ul: [
          "Mine trepist üles, mitte liftiga",
          "Pargi auto kaugemale",
          "Tee \"jalutuskoosolekuid\" — räägi telefoniga kõndides",
          "Aiatöö, koristamine, lemmikloomaga jalutamine",
          "Püsti tööd — ära istu järjest üle 30 minuti",
        ],
      },
      { h2: "III. Kehakaalu langetamine" },
      {
        p: "Ülekaal ja rasvumine on 2. tüüpi diabeedi peamine põhjus. 5–10% kehakaalu langus võib oluliselt vähendada veresuhkru taset, vererõhku ja kolesterooli. 15+ kg langus võib viia diabeedi remissiooni.",
      },
      { h2: "Põhiprintsiip: kaloridefitsiit" },
      {
        p: "Kehakaalu langetab ainult üks asi — kui kulutad rohkem kaloreid kui sööd. 0,5–1 kg kaalu langus nädalas on jätkusuutlik ja tervislik. See nõuab ~500 kcal päevas defitsiiti (söömist vähem või liikumist rohkem).",
      },
      { h2: "Praktilised soovitused" },
      {
        ul: [
          "Pea toidupäevikut — vähemalt esimese kuu jooksul. Inimesed tihti alahindavad, kui palju nad söövad.",
          "Vähenda portsjonisuurusi 20–25% — kasuta väiksemat taldrikut.",
          "Söö rohkem valku (1–1,5 g/kg kehakaalu kohta) — valk küllastab paremini kui süsivesikud ja säilitab lihasmassi kaalulangetamisel.",
          "Joo vett — sageli aetakse janu segi näljaga.",
          "Maga piisavalt (7–8 h ööpäevas) — vähene uni suurendab näljahormoonide tootmist.",
          "Halda stressi — kortisool tõstab kõhupiirkonna rasva.",
        ],
      },
      { h2: "Kui elustiil ei tööta üksinda?" },
      {
        p: "Kahjuks on enamiku ülekaaluliste inimeste jaoks elustiilimuutus tõhus, kuid raske säilitada. Kaalulangetuse ravim võib aidata, kuid Tallinna Endokrinoloogia Kliinikus on meil selge reegel: Me ei tegele ülekaalu raviga, kui patsient pole eelnevalt iseseisvalt langetanud vähemalt 12 kg kehakaalu, muutnud söömisharjumusi ja ei käi trennis vähemalt kolm korda nädalas. Me ei kirjuta välja Ozempicu, Rybelsuse ega Mounjaro retsepte. See pole karistus — see on kvaliteetse ravi tagamise reegel. Ravimid ilma elustiilimuutuseta annavad lühiajalisi tulemusi; kombinatsioonis õige eluviisiga aga püsivaid.",
      },
      { h2: "Meie kliinikus" },
      {
        p: "Diabeediõed Riina Vello (Tallinn), Liina Sildnik (Tallinn) ja Kersti Saar (Pärnu) pakuvad personaalset nõustamist toitumise, süsivesikute arvestamise ja kehakaalu langetamise teemadel. Tallinna Endokrinoloogia Kliinikus saab teha:",
      },
      {
        ul: [
          "Kehakoostise analüüsi — 20 €",
          "Diabeediõe iseseisev vastuvõtt — 40 €",
          "Tervisekassa saatekirjaga on need teenused tasuta",
        ],
      },
      {
        p: "Koos teeme sinu jaoks praktilise, isikupärase ja jätkusuutliku plaani. Edu diabeedi juhtimisel ei ole imepall — see on harjumus, mida iga päev veidi paremaks teeme.",
      },
    ],
    translations: {
      ru: {
        category: "Образ жизни",
        title: "Питание, движение и снижение веса",
        summary:
          "Изменения образа жизни — основа лечения диабета. Правильное питание, регулярная физическая активность и подходящий вес значительно снижают уровень сахара в крови, артериальное давление и холестерин — часто эффективнее любой таблетки. Эта статья даёт практические рекомендации пациентам с диабетом или преддиабетом.",
        author: "Д-р Вернер Фогель, эндокринолог",
        sections: [
          { h2: "Почему образ жизни важнее таблеток?" },
          { p: "Многочисленные исследования показали, что изменение образа жизни — самое сильное средство профилактики диабета. В исследовании Diabetes Prevention Program (DPP) снижение веса на 7% и 150 минут активности в неделю снизили риск развития СД2 на 58%, по сравнению с 31% в группе метформина. Исследование DiRECT (Великобритания) показало, что значительное снижение веса (15+ кг) приводит почти у половины пациентов к ремиссии СД2 — без лекарств." },
          { p: "Поэтому основа любого лечения — изменение образа жизни. Оно работает просто, дёшево и эффективно. Таблетки и инсулин иногда необходимы, но они не заменяют изменения образа жизни." },
          { h2: "I. Питание" },
          { p: "Питание пациента с диабетом — это не «специальная диета», а просто здоровое питание, которое рекомендуется всем. Основные принципы:" },
          { h2: "1. Контроль углеводов" },
          { p: "Углеводы сильнее всего повышают уровень сахара. Полностью исключать их не нужно — организм нуждается в них как в источнике энергии. Но важно:" },
          {
            ul: [
              "Предпочитайте цельнозерновые продукты и следите за рекомендуемым количеством потребления (ржаной хлеб, овсянка, бурый рис, гречка) вместо белого хлеба, булочек, белого риса. Сохраняйте здравый смысл: если сахар уже до еды был высоким, не стоит съесть ещё хлеба, чтобы он стал ещё выше!",
              "Избегайте рафинированного сахара и сиропов — сладкие напитки (Coca-Cola, сок), сладости, кондитерские изделия.",
              "Ешьте фрукты умеренно — предпочитайте целые фрукты (а не сок или смузи). 1–2 порции в день. Ягоды предпочтительнее (низкий гликемический индекс).",
              "Овощи практически без ограничений — капуста, огурцы, помидоры, листовые салаты, брокколи, цветная капуста, перец. Картофель и сладкий картофель — умеренно.",
              "Бобовые (фасоль, чечевица, нут) — отличный выбор: много клетчатки, белка, низкий гликемический индекс.",
            ],
          },
          { h2: "2. Правило тарелки" },
          { p: "Простое правило: представьте тарелку, разделённую на три части:" },
          {
            ul: [
              "1/2 тарелки: овощи и листовые салаты (помидор, огурец, брокколи, цветная капуста, перец, капуста)",
              "1/4 тарелки: источник белка (рыба, курица, нежирное мясо, яйцо, сыр, тофу, бобовые)",
              "1/4 тарелки: цельнозерновые (ржаной хлеб, киноа, гречка) или картофель (охлаждённый)",
              "Дополнительно: 1 порция молочного продукта (йогурт, кефир, сыр)",
              "На десерт — свежие ягоды или небольшая порция орехов",
            ],
          },
          { h2: "3. Приёмы пищи и время" },
          {
            ul: [
              "Ешьте 3 основных приёма пищи в день (завтрак, обед, ужин) — более длинные интервалы между приёмами помогают улучшить чувствительность к инсулину.",
              "Избегайте перекусов, особенно сладких. Если перекус необходим — предпочитайте орехи, сыр или овощи.",
              "Ешьте спокойно — мозгу нужно 20 минут, чтобы почувствовать насыщение.",
              "Вечером — максимум за 3 часа до сна.",
            ],
          },
          { h2: "4. Напитки" },
          {
            ul: [
              "Вода — лучший выбор, пейте 1,5–2 литра в день",
              "Чай и кофе без сахара разрешены (кофе умеренно)",
              "Соки и газированные напитки — ИЗБЕГАЙТЕ, содержат много сахара",
              "Алкоголь — умеренно (макс. 1 порция/день для женщин, 2 для мужчин). Внимание: алкоголь может вызвать гипогликемию, особенно у пациентов на инсулинотерапии.",
            ],
          },
          { h2: "5. Средиземноморская диета" },
          { p: "Самые убедительные научные данные подтверждают эффективность средиземноморского стиля питания для профилактики и лечения диабета:" },
          {
            ul: [
              "Много овощей и фруктов",
              "Цельнозерновые продукты",
              "Оливковое масло как основной источник жира",
              "Рыба (особенно жирная — лосось, скумбрия, сардины) 2–3 раза в неделю",
              "Орехи (4–5 раз в неделю, горсть)",
              "Бобовые (4–5 раз в неделю)",
              "Умеренно — молочные продукты и курица",
              "Мало красного мяса (раз в неделю или реже)",
              "Вино умеренно к еде (если вообще)",
            ],
          },
          { h2: "II. Движение" },
          { p: "Движение в лечении диабета так же важно, как и питание. Его эффекты:" },
          {
            ul: [
              "Мышцы используют глюкозу без инсулина — уровень сахара падает уже в течение пары часов после тренировки",
              "Длительно повышает чувствительность к инсулину (одна тренировка улучшает её на 24–72 часа)",
              "Снижает артериальное давление и холестерин",
              "Помогает снизить и удержать вес",
              "Снижает депрессию и улучшает сон",
              "Снижает риск инфаркта",
            ],
          },
          { h2: "Аэробная тренировка" },
          { p: "Рекомендация — 150 минут умеренной аэробной нагрузки в неделю, распределённых минимум на 3 дня. Умеренная означает: сердечный ритм повышается, вы дышите глубже, но всё ещё можете говорить. Например:" },
          {
            ul: [
              "Быстрая ходьба (5–6 км/ч) — 30 мин × 5 дней в неделю",
              "Велосипед",
              "Плавание",
              "Танцы",
              "Лыжи, коньки",
            ],
          },
          { h2: "Силовая тренировка" },
          { p: "Помимо аэробной нагрузки рекомендуется 2–3 раза в неделю силовая тренировка для сохранения мышечной массы. Мышечная ткань метаболически активна — она использует глюкозу даже в состоянии покоя. Например:" },
          {
            ul: [
              "Упражнения с резинкой дома",
              "С гантелями и гирями дома или в зале",
              "На силовых тренажёрах в зале",
              "Функциональные упражнения — приседания, отжимания, подтягивания, планка",
            ],
          },
          { h2: "Сахар крови и тренировка" },
          { p: "Пациенты на инсулинотерапии должны измерять сахар до и после тренировки. Если сахар перед тренировкой ниже 5 ммоль/л, съешьте небольшой перекус (15 г углеводов). Длительная тренировка (более 1 ч) может снижать сахар и в течение 24 часов после — особенно ночью. Корректируйте дозу инсулина соответственно — медсестра по диабету поможет это спланировать." },
          { h2: "Повседневная активность" },
          { p: "Помимо официальных тренировок важна и повседневная активность. Цель — 7000–10 000 шагов в день. Простые способы:" },
          {
            ul: [
              "Поднимайтесь по лестнице, а не на лифте",
              "Паркуйте машину подальше",
              "Делайте «прогулочные совещания» — разговаривайте по телефону, гуляя",
              "Работа в саду, уборка, прогулки с питомцем",
              "Работайте стоя — не сидите подряд более 30 минут",
            ],
          },
          { h2: "III. Снижение веса" },
          { p: "Избыточный вес и ожирение — главная причина СД2. Снижение веса на 5–10% значительно уменьшает уровень сахара, давления и холестерина. Снижение на 15+ кг может привести к ремиссии диабета." },
          { h2: "Основной принцип: дефицит калорий" },
          { p: "Вес снижает только одно — если тратишь больше калорий, чем съедаешь. 0,5–1 кг в неделю — устойчивая и здоровая скорость снижения веса. Это требует дефицита ~500 ккал в день (есть меньше или больше двигаться)." },
          { h2: "Практические рекомендации" },
          {
            ul: [
              "Ведите пищевой дневник — хотя бы первый месяц. Люди часто недооценивают, сколько съедают.",
              "Уменьшите размер порции на 20–25% — используйте тарелку меньшего размера.",
              "Ешьте больше белка (1–1,5 г/кг массы тела) — белок лучше насыщает, чем углеводы, и сохраняет мышечную массу при похудении.",
              "Пейте воду — жажду часто путают с голодом.",
              "Спите достаточно (7–8 ч в сутки) — недосыпание повышает гормоны голода.",
              "Управляйте стрессом — кортизол увеличивает жир в области живота.",
            ],
          },
          { h2: "Если образ жизни не работает в одиночку?" },
          { p: "К сожалению, у большинства людей с избыточным весом изменение образа жизни эффективно, но трудно поддерживать. Препарат для снижения веса может помочь, но в Таллиннской клинике эндокринологии у нас чёткое правило: мы не занимаемся лечением избыточного веса, если пациент не снизил вес хотя бы на 12 кг самостоятельно, не изменил привычки питания и не ходит на тренировки минимум 3 раза в неделю. Мы не выписываем рецепты на Ozempic, Rybelsus или Mounjaro. Это не наказание — это правило обеспечения качества лечения. Лекарства без изменения образа жизни дают краткосрочные результаты; в сочетании с правильным образом жизни — устойчивые." },
          { h2: "В нашей клинике" },
          { p: "Медсёстры по диабету Рийна Велло (Таллинн), Лийна Сильдник (Таллинн) и Керсти Саар (Пярну) предлагают персональные консультации по диабету — по питанию, подсчёту углеводов и снижению веса. В Таллиннской клинике эндокринологии можно сделать:" },
          {
            ul: [
              "Анализ состава тела — 20 €",
              "Самостоятельный приём медсестры по диабету — 40 €",
              "По направлению медицинской страховки эти услуги бесплатны",
            ],
          },
          { p: "Вместе мы составим для вас практичный, индивидуальный и устойчивый план. Успех в управлении диабетом — это не волшебная таблетка, а привычка, которую мы каждый день делаем чуть лучше." },
        ],
      },
      en: {
        category: "Lifestyle",
        title: "Diet, exercise and weight loss",
        summary:
          "Lifestyle changes are the foundation of diabetes treatment. Proper nutrition, regular exercise and an appropriate body weight significantly reduce blood sugar, blood pressure and cholesterol — often more than any pill. This article gives practical recommendations for people with diabetes or prediabetes.",
        author: "Dr. Verner Fogel, endocrinologist",
        sections: [
          { h2: "Why is lifestyle more important than pills?" },
          { p: "Many studies have shown that lifestyle change is the most powerful tool for preventing diabetes. In the Diabetes Prevention Program (DPP) study, 7% weight loss and 150 minutes of activity per week reduced the risk of developing type 2 diabetes by 58%, compared with 31% in the metformin group. The DiRECT trial (UK) showed that significant weight loss (15+ kg) leads to type 2 diabetes remission in nearly half of patients — without medication." },
          { p: "That is why lifestyle change is the foundation of every treatment — it works simply, cheaply and effectively. Pills and insulin are sometimes necessary, but they do not replace lifestyle change." },
          { h2: "I. Nutrition" },
          { p: "Nutrition for a person with diabetes is not a \"special diet\" — it is simply healthy eating recommended for everyone. The main principles are:" },
          { h2: "1. Watching carbohydrates" },
          { p: "Carbohydrates raise blood sugar the most. You don't need to avoid them entirely — the body needs them for energy. But it is important to:" },
          {
            ul: [
              "Prefer whole-grain products and watch the recommended portion size (rye bread, oats, brown rice, buckwheat) over white bread, pastries and white rice. Use common sense: if blood sugar was already high before the meal, you don't need extra bread to push it even higher!",
              "Avoid refined sugar and syrups — sugary drinks (Coca-Cola, juice), sweets, pastries.",
              "Eat fruit in moderation — prefer whole fruit (not juice or smoothies). 1–2 portions a day. Berries are preferred (lower glycaemic index).",
              "Vegetables are practically unlimited — cabbage, cucumbers, tomatoes, leafy salads, broccoli, cauliflower, peppers. Eat potatoes and sweet potatoes in moderation.",
              "Pulses (beans, lentils, chickpeas) are excellent — high fibre, protein, low glycaemic index.",
            ],
          },
          { h2: "2. The plate rule" },
          { p: "A simple rule: imagine your plate divided into three parts:" },
          {
            ul: [
              "1/2 of the plate: vegetables and leafy salads (tomato, cucumber, broccoli, cauliflower, peppers, cabbage)",
              "1/4 of the plate: protein (fish, chicken, lean meat, egg, cheese, tofu, pulses)",
              "1/4 of the plate: whole grains (rye bread, quinoa, buckwheat) or potato (cooled)",
              "Side: 1 portion of dairy (yoghurt, kefir, cheese)",
              "For dessert — fresh berries or a small amount of nuts",
            ],
          },
          { h2: "3. Meals and timing" },
          {
            ul: [
              "Eat 3 main meals a day (breakfast, lunch, dinner) — longer intervals between meals help improve insulin sensitivity.",
              "Avoid snacking, especially sweet snacks. If you need a snack, prefer nuts, cheese or vegetables.",
              "Eat slowly — the brain needs 20 minutes to feel full.",
              "Have your last meal no later than 3 hours before bedtime.",
            ],
          },
          { h2: "4. Drinks" },
          {
            ul: [
              "Water is the best choice — drink 1.5–2 litres a day",
              "Tea and coffee without sugar are allowed (coffee in moderation)",
              "Juices and fizzy drinks — AVOID, they contain a lot of sugar",
              "Alcohol — in moderation (max. 1 drink/day for women, 2 for men). Note: alcohol can cause hypoglycaemia, especially in patients on insulin.",
            ],
          },
          { h2: "5. The Mediterranean diet" },
          { p: "The strongest scientific evidence supports the Mediterranean style of eating for diabetes prevention and treatment:" },
          {
            ul: [
              "Plenty of vegetables and fruit",
              "Whole grains",
              "Olive oil as the main source of fat",
              "Fish (especially fatty fish — salmon, mackerel, sardines) 2–3 times a week",
              "Nuts (4–5 times a week, a handful)",
              "Pulses (4–5 times a week)",
              "Dairy and chicken in moderation",
              "Little red meat (once a week or less)",
              "Wine with meals in moderation (if at all)",
            ],
          },
          { h2: "II. Movement" },
          { p: "Movement is as important in diabetes treatment as nutrition. Its effects are:" },
          {
            ul: [
              "Muscles use blood glucose without insulin — blood sugar drops within hours after exercise",
              "Long term, it increases insulin sensitivity (one workout improves it for 24–72 hours)",
              "Lowers blood pressure and cholesterol",
              "Helps lose and maintain body weight",
              "Reduces depression and improves sleep",
              "Reduces heart attack risk",
            ],
          },
          { h2: "Aerobic exercise" },
          { p: "The recommendation is 150 minutes of moderate aerobic activity per week, spread over at least 3 days. Moderate means: heart rate rises, you breathe deeper but can still talk. For example:" },
          {
            ul: [
              "Brisk walking (5–6 km/h) — 30 min × 5 days a week",
              "Cycling",
              "Swimming",
              "Dancing",
              "Skiing, skating",
            ],
          },
          { h2: "Strength training" },
          { p: "In addition to aerobic exercise, 2–3 strength sessions per week are recommended to maintain muscle mass. Muscle tissue is metabolically active — it uses glucose even at rest. For example:" },
          {
            ul: [
              "Resistance band exercises at home",
              "Dumbbell and kettlebell exercises at home or in the gym",
              "Machine-based strength exercises at the gym",
              "Functional exercises — squats, push-ups, pull-ups, plank",
            ],
          },
          { h2: "Blood sugar and exercise" },
          { p: "Patients on insulin should measure blood sugar before and after exercise. If pre-workout glucose is below 5 mmol/L, eat a small snack (15 g of carbs) first. A long workout (over 1 h) can lower blood sugar for up to 24 hours afterwards — especially at night. Adjust your insulin dose accordingly — the diabetes nurse will help plan this." },
          { h2: "Everyday activity" },
          { p: "In addition to formal workouts, everyday activity is also important. The goal is 7,000–10,000 steps per day. Simple ways:" },
          {
            ul: [
              "Take the stairs, not the lift",
              "Park further away",
              "Have \"walking meetings\" — talk on the phone while walking",
              "Garden, clean, walk the dog",
              "Stand while working — don't sit for more than 30 minutes at a time",
            ],
          },
          { h2: "III. Weight loss" },
          { p: "Excess weight and obesity are the main cause of type 2 diabetes. A 5–10% weight loss can significantly reduce blood sugar, blood pressure and cholesterol. A 15+ kg loss can lead to diabetes remission." },
          { h2: "The main principle: calorie deficit" },
          { p: "Only one thing causes weight loss — burning more calories than you eat. 0.5–1 kg of weight loss per week is sustainable and healthy. This requires a deficit of about 500 kcal per day (eat less or move more)." },
          { h2: "Practical recommendations" },
          {
            ul: [
              "Keep a food diary — at least for the first month. People often underestimate how much they eat.",
              "Reduce portion size by 20–25% — use a smaller plate.",
              "Eat more protein (1–1.5 g/kg of body weight) — protein satiates better than carbs and preserves muscle mass during weight loss.",
              "Drink water — thirst is often mistaken for hunger.",
              "Sleep enough (7–8 h per night) — too little sleep increases hunger hormones.",
              "Manage stress — cortisol increases abdominal fat.",
            ],
          },
          { h2: "What if lifestyle alone doesn't work?" },
          { p: "Unfortunately, for most overweight people, lifestyle change is effective but hard to maintain. Weight-loss medication can help, but at Tallinn Endocrinology Clinic we have a clear rule: we do not treat excess weight unless the patient has first lost at least 12 kg on their own, changed their eating habits and is exercising at least three times a week. We do not write prescriptions for Ozempic, Rybelsus or Mounjaro. This is not a punishment — it is our way of guaranteeing high-quality care. Medication without lifestyle change produces short-term results; combined with the right lifestyle, the results are lasting." },
          { h2: "At our clinic" },
          { p: "Our diabetes nurses Riina Vello (Tallinn), Liina Sildnik (Tallinn) and Kersti Saar (Pärnu) offer personal diabetes counselling on nutrition, carbohydrate counting and weight loss. At Tallinn Endocrinology Clinic you can have:" },
          {
            ul: [
              "Body composition analysis — €20",
              "Stand-alone diabetes nurse appointment — €40",
              "With a referral from your health insurance, these services are free of charge",
            ],
          },
          { p: "Together we will create a practical, personal and sustainable plan for you. Success in managing diabetes is not a magic pill — it is a habit that we improve a little every day." },
        ],
      },
    },
  },
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "kondimine-peale-sooki",
    slug: "kondimine-peale-sooki",
    category: "Elustiil",
    title: "Kõndimine peale sööki ja 10 000 sammu päevas",
    image: "/api/static/ai-images/articles/kondimine-peale-sooki.webp",
    imageCaption: "Lühike jalutuskäik peale sööki ja 10 000 sammu päevas",
    summary:
      "Lihtne, teaduslikult tõestatud viis veresuhkru ja kehakaalu langetamiseks on kõndida 10–20 minutit kohe pärast iga söögikorda ning saavutada päevas 10 000 või rohkem sammu. Käesolev artikkel selgitab, miks see toimib, kuidas alustada ja millised on konkreetsed numbrid sinu tervise jaoks.",
    readingMinutes: 8,
    updated: "2026-06-13",
    author: DR_AUTHOR,
    sections: [
      { h2: "Miks just kõndimine?" },
      {
        p: "Kõndimine on inimliigi kõige loomulikum liikumisviis. Vastupidiselt jõusaalile, ratta- või jooksuspordile ei vaja kõndimine spetsiaalset varustust, eelnevat ettevalmistust ega head füüsilist vormi. Igaüks saab kõndida — ka eakad, rasedad, taastusravil olijad ja need, kes pole kunagi sporti teinud. Just sellepärast on kõndimine üks vähestest asjadest, mis päriselt töötab kogu rahvastiku tasemel.",
      },
      {
        p: "Diabeedi, ülekaalu ja südameveresoonkonna haiguste seisukohast on kõndimine eriti väärtuslik kahel viisil:",
      },
      {
        ol: [
          "Kõndimine vahetult pärast söögikorda — vähendab veresuhkru järsku tõusu ja parandab insuliinitundlikkust",
          "Päevane suurem aktiivsus (sammud kokku) — vähendab pikaajalist kõhupiirkonna rasvumist, langetab vererõhku ja kolesterooli",
        ],
      },
      { h2: "1. Kõndimine peale sööki" },
      {
        p: "Pärast söömist tõuseb vere glükoosisisaldus, sest süsivesikud lagunevad seedimisel glükoosiks. Kõhunääre vabastab insuliini, et see glükoos rakkudesse viia. Diabeeti põdevatel inimestel või insuliiniresistentsusega inimestel on see häirunud — veresuhkur jääb pikaks ajaks kõrgeks.",
      },
      {
        p: "Siin tuleb mängu väike imeravim: kõndimine. Lihastöö ajal hakkavad lihaskoed glükoosi tarbima ka ilma insuliinita — eraldi mehhanismi kaudu, mida nimetatakse \"insuliini-sõltumatuks glükoosi omastamiseks\". See vähendab veresuhkrut vahetult pärast söögikorda 30–50%.",
      },
      {
        callout: {
          type: "info",
          title: "Numbrid teadusuuringutest",
          body:
            "2016. aastal avaldatud Otago Ülikooli uuring leidis, et 10-minutiline kõnd kohe pärast söögikorda langetas 2. tüüpi diabeediga patsientidel veresuhkrut keskmiselt 12% rohkem kui 30-minutiline kõnd suvalisel ajal päevas. 2022. aasta meta-analüüs (Sports Medicine) jõudis sarnasele järeldusele — isegi 2–5 minutit kerget liikumist iga 20–30 minuti tagant parandas glükoosi taset oluliselt.",
        },
      },
      { h2: "Praktiline soovitus" },
      {
        ul: [
          "Kõnni 10–20 minutit kohe pärast iga söögikorda (hommikusöök, lõuna, õhtusöök)",
          "Tempo on vabalt valitav — isegi tavaline rahulik kõnd toimib.",
          "Kõige tähtsam on ajatus — esimene 15 min pärast sööki annab suurima efekti, kuna just sellel ajal hakkab veresuhkur tõusma.",
          "Kombineeri seda igapäevase rutiiniga: lõunat süües juba mõtle, kuhu sa pärast kõndima lähed.",
        ],
      },
      { h2: "Tüüpilised tulemused 4–8 nädalat" },
      {
        ul: [
          "HbA1c langeb 0,3–0,5% (eriti kerge kuni mõõduka diabeediga patsientidel)",
          "Söögijärgne veresuhkur (1–2 h pärast sööki) langeb 1,5–2,5 mmol/L",
          "Insuliinivajadus väheneb 5–15% — paljud insuliini kasutavad patsiendid saavad doosi langetada/vähendada",
          "Keskmiselt triglütseriidid langevad 10–20%",
          "Unekvaliteet paraneb (vähem öiseid hüpoglükeemiaid)",
        ],
      },
      { h2: "2. 10 000 sammu päevas — pikaajaline eesmärk" },
      {
        p: "\"10 000 sammu päevas\" on tuntud rahvatervise sõnum, mis pärineb 1965. aasta Jaapanist (kõndija-pedomeeter \"manpo-kei\" — \"10 000 sammu mõõtja\"). Tänapäeval teame teadusuuringutest täpsemalt, mida sammude arv keha jaoks tähendab.",
      },
      { h2: "Mida ütleb teadus?" },
      {
        ul: [
          "4 000 sammu päevas — kõige madalam künnis igapäevaseks mõjuks; juba alates sellest tasemest hakkab suremus langema",
          "7 000 sammu päevas — surmariski oluline vähenemine (~50% versus 4 000 sammu)",
          "10 000 sammu päevas — diabeedi, südameinfarkti ja insuldi riski oluline vähenemine; kehakaalu kontroll oluliselt parem",
          "12 000–15 000 sammu päevas — täiendav kasu, kuid kasu protsent on väiksem.",
        ],
      },
      {
        p: "Suurim kasu on liikumisel inaktiivse inimese jaoks — 2 000-lt 5 000-ni samme päevas annab suurema tervisemõju kui 8 000-lt 12 000-ni. Sellepärast ära pelga, kui sa ei saa esimesel päeval 10 000 — iga lisasamm on kasulik.",
      },
      { h2: "Kuidas saavutada 10 000 sammu" },
      {
        ul: [
          "Hommikune kõnd 30 min = umbes 3 500 sammu",
          "Söögijärgsed 3 kõndi á 10–15 min = umbes 3 000 sammu",
          "Tavaline päev kontoris/kodus = umbes 3 000–4 000 sammu",
          "Kokku ≈ 9 500–10 500 sammu päevas",
        ],
      },
      { p: "Lihtsamad nipid sammude lisamiseks:" },
      {
        ul: [
          "Mine trepist üles, mitte liftiga",
          "Pargi auto kaugemale või tule bussist üks peatus varem maha",
          "Tee \"jalutuskoosolekuid\" — räägi telefoniga jalutades, mitte istudes",
          "Lemmikloomaga jalutamine 30+ minutit, mitte 10 minutit",
          "Õhtused jalutuskäigud perega — sotsiaalne lisaboonus",
          "Kasuta seisukohta või treadmilli tööl",
          "Pane telefonis või kellal sammude eesmärk ja jälgi seda iga päev",
        ],
      },
      { h2: "Pikaajalised tulemused 6–12 kuud" },
      {
        ul: [
          "Kehakaalu langus 3–8 kg (ilma muude muudatusteta toitumises)",
          "Vööümbermõõt väheneb 4–7 cm",
          "Süstoolne vererõhk langeb 5–10 mmHg",
          "LDL-kolesterool langeb 10–15%",
          "HDL-kolesterool (\"hea\") tõuseb 5–10%",
          "Stress, ärevus ja depressiooni sümptomid vähenevad",
          "Unekvaliteet paraneb",
          "Liigeste valu (eriti põlvedes ja seljas) väheneb",
        ],
      },
      { h2: "Mõõtmine ja jälgimine" },
      { p: "Sammude mõõtmiseks on tänapäeval kõige lihtsam telefon või nutikell:" },
      {
        ul: [
          "iPhone: Apple Health äpp loendab sammud automaatselt",
          "Android: Google Fit või tootja-spetsiifiline äpp (Samsung Health, Xiaomi Mi Fit)",
          "Nutikell (Garmin, Fitbit, Apple Watch, Polar) — täpsem ja motiveerivam",
          "Lihtne pedomeeter (10–30 €) — kõige odavam",
        ],
      },
      {
        p: "Jälgi mitte ainult kogusummat, vaid ka **igapäevast järjepidevust**. Parem on 8 000 sammu iga päev kui 0 sammu tööpäevadel + 70 000 sammu nädalavahetusel.",
      },
      { h2: "Soovitused diabeedihaigetele" },
      {
        callout: {
          type: "warning",
          title: "Veresuhkur ja kõndimine",
          body:
            "Insuliinravi puhul võib pikem kõnd langetada järsult veresuhkru tase. Eriti pärast pikemat (üle 30 min) kõndi või enne magamaminekut. Soovitused: 1) Mõõda veresuhkrut enne ja pärast pikemat kõndi, 2) Võta kaasa glükoositabletid või banaan, 3) Kui veresuhkur enne kõndi on alla 5 mmol/L, söö enne väike snäkk (15 g süsivesikut), 4) Arvuta insuliinidoosi nii, et see arvestaks pärast sööki toimuva kõnni veresuhkrut langetava toimega — diabeediõde aitab seda planeerida.",
        },
      },
      { h2: "Mõni vastunäidustus" },
      { p: "Kuigi kõndimine sobib peaaegu kõigile, on teatud olukorras vaja arsti konsultatsiooni:" },
      {
        ul: [
          "Diabeetilise jalahaava korral — pikaaegne kõndimine võib haava ärritada",
          "Mõõduka kuni raske retinopaatia korral — vältida suurt tõusu/laskumist (võrkkesta verejooksu risk)",
          "Pärast värsket südameoperatsiooni — vaja kardioloogi heakskiitu",
          "Tugev neuropaatia jalgades — vaja sobivaid jalanõusid ja sagedasi jalakontrolle",
          "Tugev tasakaaluhäire — võib vajada keppi või kepikõndi (Nordic walking)",
        ],
      },
      { h2: "Kõndimisplaan algajatele" },
      {
        p: "Kui sa pole varem regulaarselt kõndinud, ära alusta 10 000 sammuga päevas — see võib põhjustada lihasvalu ja motivatsiooni langust. Soovitan järgnevat 8-nädalast plaani:",
      },
      {
        ul: [
          "Nädalad 1–2: lisa 1 söögijärgne 10-minutiline kõnd päevas + 3 000–4 000 sammu kokku",
          "Nädalad 3–4: 2 söögijärgset kõndi + 5 000–6 000 sammu kokku",
          "Nädalad 5–6: 3 söögijärgset kõndi + 7 000 sammu kokku",
          "Nädalad 7–8: 3 söögijärgset kõndi + 9 000–10 000 sammu kokku",
        ],
      },
      { p: "Kui mõni päev ei õnnestu — ära muretse. Jätkamine homme on tähtsam kui täiuslikkus." },
      { h2: "Meie kliinikus" },
      {
        p: "Tallinna Endokrinoloogia Kliinikus aitame sind koostada isikupärase kõndimisplaani sõltuvalt sinu praegusest seisundist, diabeedi tüübist ja insuliinravist. Diabeediõed Riina Vello (Tallinn), Liina Sildnik (Tallinn) ja Kersti Saar (Pärnu) annavad konkreetseid soovitusi:",
      },
      {
        ul: [
          "Kuidas kohandada insuliinidoosi kõndimist arvestades",
          "Millised sammude eesmärgid sobivad sinu tervise jaoks",
          "Kuidas kasutada CGM-sensorit kõndi mõju jälgimiseks",
          "Jalgade hooldus ja sobivate jalanõude valik (eriti neuropaatia korral)",
        ],
      },
      {
        callout: {
          type: "info",
          title: "Üks lihtne harjumus, suur mõju",
          body:
            "Kõndimine peale sööki ja 10 000 sammu päevas on tasuta, ohutu ja teaduslikult tõestatud sekkumine, mis võib langetada HbA1c-d sama palju kui üks ravim. Alusta täna — sõbralik 10-minutiline jalutuskäik pärast lõunat võib olla see, mis muudab kõik.",
        },
      },
    ],
    translations: {
      ru: {
        category: "Образ жизни",
        title: "Ходьба после еды и 10 000 шагов в день",
        imageCaption: "Короткая прогулка после еды и 10 000 шагов в день",
        summary:
          "Простой, научно доказанный способ снизить уровень сахара в крови и вес — ходить 10–20 минут сразу после каждого приёма пищи и достигать 10 000 или более шагов в день. В этой статье объясняется, почему это работает, как начать и какие конкретные числа важны для вашего здоровья.",
        author: "Д-р Вернер Фогель, эндокринолог",
        sections: [
          { h2: "Почему именно ходьба?" },
          { p: "Ходьба — самый естественный способ передвижения для человека. В отличие от тренажёрного зала, езды на велосипеде или бега, ходьба не требует специального оборудования, предварительной подготовки или хорошей физической формы. Ходить может каждый — пожилые, беременные, проходящие реабилитацию и те, кто никогда не занимался спортом. Именно поэтому ходьба — одно из немногих средств, которое реально работает на уровне всего населения." },
          { p: "С точки зрения диабета, избыточного веса и сердечно-сосудистых заболеваний ходьба особенно ценна по двум причинам:" },
          {
            ul: [
              "Ходьба сразу после еды — замедляет резкий рост сахара в крови и улучшает чувствительность к инсулину",
              "Повышение дневной активности (общее количество шагов) — уменьшает абдоминальное ожирение в долгосрочной перспективе, снижает давление и холестерин",
            ],
          },
          { h2: "1. Ходьба после еды" },
          { p: "После еды уровень сахара в крови повышается, так как углеводы при пищеварении расщепляются до глюкозы. Поджелудочная железа выделяет инсулин, чтобы доставить эту глюкозу в клетки. У людей с диабетом или инсулинорезистентностью этот процесс нарушен — сахар остаётся высоким на длительное время." },
          { p: "Здесь в игру вступает маленькое чудо-средство: ходьба. Во время мышечной работы мышечная ткань начинает потреблять глюкозу даже без инсулина — через отдельный механизм, называемый «инсулинонезависимое поглощение глюкозы». Это снижает уровень сахара сразу после еды на 30–50%." },
          { h2: "Цифры из научных исследований" },
          { p: "Исследование Университета Отаго, опубликованное в 2016 году, показало, что 10-минутная прогулка сразу после еды снижала уровень сахара у пациентов с СД2 в среднем на 12% больше, чем 30-минутная прогулка в произвольное время дня. Мета-анализ 2022 года (Sports Medicine) пришёл к аналогичному выводу — даже 2–5 минут лёгкой активности каждые 20–30 минут значительно улучшали уровень глюкозы." },
          { h2: "Практическая рекомендация" },
          {
            ul: [
              "Ходите 10–20 минут сразу после каждого приёма пищи (завтрак, обед, ужин)",
              "Темп — на ваш выбор, даже спокойная ходьба работает",
              "Самое важное — время начала: первые 15 минут после еды дают наибольший эффект, ведь именно тогда сахар начинает расти",
              "Совместите это с повседневной рутиной: уже во время обеда подумайте, куда вы пойдёте гулять после",
            ],
          },
          { h2: "Типичные результаты через 4–8 недель" },
          {
            ul: [
              "HbA1c снижается на 0,3–0,5% (особенно у пациентов с лёгким и умеренным диабетом)",
              "Уровень сахара после еды (через 1–2 ч) снижается на 1,5–2,5 ммоль/л",
              "Потребность в инсулине снижается на 5–15% — многие пациенты могут уменьшить дозу",
              "Триглицериды в среднем снижаются на 10–20%",
              "Качество сна улучшается (меньше ночных гипогликемий)",
            ],
          },
          { h2: "2. 10 000 шагов в день — долгосрочная цель" },
          { p: "«10 000 шагов в день» — известный месседж общественного здравоохранения, пришедший из Японии 1965 года (шагомер «manpo-kei», что означает «измеритель 10 000 шагов»). Сегодня научные исследования точнее показывают, что означает количество шагов для организма." },
          { h2: "Что говорит наука?" },
          {
            ul: [
              "4 000 шагов в день — самый низкий порог для ежедневного эффекта; смертность начинает снижаться уже с этого уровня",
              "7 000 шагов в день — значительное снижение риска смерти (~50% против 4 000 шагов)",
              "10 000 шагов в день — значительное снижение риска диабета, инфаркта и инсульта; гораздо лучший контроль веса",
              "12 000–15 000 шагов в день — дополнительная польза, но прирост меньше",
            ],
          },
          { p: "Наибольшая польза — для малоподвижного человека, переходящего к движению: с 2 000 до 5 000 шагов в день даёт больший эффект для здоровья, чем с 8 000 до 12 000. Поэтому не отчаивайтесь, если в первый день не получается 10 000 — каждый дополнительный шаг полезен." },
          { h2: "Как набрать 10 000 шагов" },
          {
            ul: [
              "Утренняя прогулка 30 минут = примерно 3 500 шагов",
              "Три прогулки после еды по 10–15 минут = примерно 3 000 шагов",
              "Обычный день в офисе/дома = примерно 3 000–4 000 шагов",
              "Итого ≈ 9 500–10 500 шагов в день",
            ],
          },
          { p: "Простые способы добавить шаги:" },
          {
            ul: [
              "Поднимайтесь по лестнице, а не лифтом",
              "Паркуйте машину дальше или выйдите из автобуса на одну остановку раньше",
              "Делайте «прогулочные совещания» — разговаривайте по телефону на ходу, а не сидя",
              "Гуляйте с питомцем 30+ минут, а не 10",
              "Вечерние прогулки с семьёй — социальный бонус",
              "Используйте стол стоя или беговую дорожку на работе",
              "Установите цель шагов в телефоне или часах и следите за ней каждый день",
            ],
          },
          { h2: "Долгосрочные результаты через 6–12 месяцев" },
          {
            ul: [
              "Снижение веса на 3–8 кг (без других изменений в питании)",
              "Окружность талии уменьшается на 4–7 см",
              "Систолическое давление снижается на 5–10 мм рт. ст.",
              "ЛПНП-холестерин снижается на 10–15%",
              "ЛПВП-холестерин («хороший») повышается на 5–10%",
              "Стресс, тревожность и симптомы депрессии снижаются",
              "Качество сна улучшается",
              "Боль в суставах (особенно в коленях и спине) уменьшается",
            ],
          },
          { h2: "Измерение и отслеживание" },
          { p: "Самый простой способ измерения шагов сегодня — телефон или умные часы:" },
          {
            ul: [
              "iPhone: приложение Apple Health считает шаги автоматически",
              "Android: Google Fit или приложение производителя (Samsung Health, Xiaomi Mi Fit)",
              "Умные часы (Garmin, Fitbit, Apple Watch, Polar) — точнее и мотивирует сильнее",
              "Простой шагомер (10–30 €) — самый дешёвый вариант",
            ],
          },
          { p: "Следите не только за общим количеством, но и за «ежедневной последовательностью». Лучше 8 000 шагов каждый день, чем 0 шагов в будни + 70 000 в выходные." },
          { h2: "Рекомендации пациентам с диабетом" },
          { h2: "Сахар крови и ходьба" },
          { p: "При инсулинотерапии длительная ходьба может резко снизить уровень сахара. Особенно после прогулки длиннее 30 минут или перед сном. Рекомендации: 1) Измеряйте сахар до и после длительной ходьбы, 2) Берите с собой таблетки глюкозы или банан, 3) Если сахар перед прогулкой ниже 5 ммоль/л, съешьте небольшой перекус (15 г углеводов), 4) Рассчитайте дозу инсулина так, чтобы учитывать снижающий эффект послеобеденной ходьбы — медсестра по диабету поможет это спланировать." },
          { h2: "Несколько противопоказаний" },
          { p: "Хотя ходьба подходит почти всем, в некоторых ситуациях нужна консультация врача:" },
          {
            ul: [
              "При язве диабетической стопы — длительная ходьба может травмировать рану",
              "При умеренной или тяжёлой ретинопатии — избегать больших подъёмов/спусков (риск кровоизлияния в сетчатку)",
              "После недавней операции на сердце — нужно одобрение кардиолога",
              "При выраженной нейропатии в ногах — нужны подходящая обувь и частые осмотры стоп",
              "При сильном нарушении равновесия — использовать палки (скандинавская ходьба)",
            ],
          },
          { h2: "План ходьбы для начинающих" },
          { p: "Если вы раньше регулярно не ходили, не начинайте сразу с 10 000 шагов в день — это может вызвать мышечную боль и снижение мотивации. Рекомендуем следующий 8-недельный план:" },
          {
            ul: [
              "1–2 неделя: добавьте 1 прогулку после еды по 10 минут в день + 3 000–4 000 шагов в сумме",
              "3–4 неделя: 2 прогулки после еды + 5 000–6 000 шагов в сумме",
              "5–6 неделя: 3 прогулки после еды + 7 000 шагов в сумме",
              "7–8 неделя: 3 прогулки после еды + 9 000–10 000 шагов в сумме",
            ],
          },
          { p: "Если какой-то день не получился — не расстраивайтесь. Продолжать завтра важнее, чем быть идеальным." },
          { h2: "В нашей клинике" },
          { p: "В Таллиннской клинике эндокринологии мы поможем составить индивидуальный план ходьбы в зависимости от вашего текущего состояния, типа диабета и инсулинотерапии. Медсёстры по диабету Рийна Велло (Таллинн), Лийна Сильдник (Таллинн) и Керсти Саар (Пярну) дают конкретные рекомендации:" },
          {
            ul: [
              "Как скорректировать дозу инсулина с учётом ходьбы",
              "Какие цели по шагам подходят вашему здоровью",
              "Как использовать CGM-сенсор для отслеживания влияния ходьбы",
              "Уход за стопами и подбор обуви (особенно при нейропатии)",
            ],
          },
          {
            callout: {
              type: "info",
              title: "Одна простая привычка, большое влияние",
              body:
                "Ходьба после еды и 10 000 шагов в день — это бесплатное, безопасное и научно доказанное вмешательство, способное снизить HbA1c так же, как одно лекарство. Начните сегодня — дружеская 10-минутная прогулка после обеда может стать тем, что изменит всё.",
            },
          },
        ],
      },
      en: {
        category: "Lifestyle",
        title: "Walking after meals and 10,000 steps a day",
        imageCaption: "A short walk after a meal and 10,000 steps a day",
        summary:
          "A simple, scientifically proven way to lower blood sugar and body weight is to walk for 10–20 minutes immediately after every meal and reach 10,000 or more steps a day. This article explains why it works, how to start and what the specific numbers mean for your health.",
        author: "Dr. Verner Fogel, endocrinologist",
        sections: [
          { h2: "Why walking?" },
          { p: "Walking is the most natural form of movement for the human species. Unlike the gym, cycling or running, walking requires no special equipment, prior preparation or good physical fitness. Anyone can walk — the elderly, pregnant women, people in rehabilitation and those who have never done sports. That is why walking is one of the few things that actually works at population level." },
          { p: "From the perspective of diabetes, excess weight and cardiovascular disease, walking is especially valuable for two reasons:" },
          {
            ul: [
              "Walking immediately after a meal — slows the sharp rise in blood sugar and improves insulin sensitivity",
              "Higher daily activity (total steps) — reduces long-term abdominal obesity, lowers blood pressure and cholesterol",
            ],
          },
          { h2: "1. Walking after meals" },
          { p: "After eating, blood glucose rises because carbohydrates are broken down into glucose during digestion. The pancreas releases insulin to move that glucose into the cells. In people with diabetes or insulin resistance this is disrupted — blood sugar stays high for a long time." },
          { p: "Here a little miracle comes into play: walking. During muscle work, muscle tissue starts taking up glucose even without insulin — through a separate mechanism called \"insulin-independent glucose uptake\". This reduces blood sugar right after a meal by 30–50%." },
          { h2: "The numbers from scientific studies" },
          { p: "A 2016 study from the University of Otago found that a 10-minute walk right after a meal lowered blood sugar in patients with type 2 diabetes by an average of 12% more than a 30-minute walk at any other time of day. A 2022 meta-analysis (Sports Medicine) reached a similar conclusion — even 2–5 minutes of light movement every 20–30 minutes significantly improved glucose levels." },
          { h2: "Practical recommendation" },
          {
            ul: [
              "Walk for 10–20 minutes immediately after every meal (breakfast, lunch, dinner)",
              "Pace is up to you — even a calm walk works",
              "Timing is the most important — the first 15 minutes after eating give the biggest effect, because that is when blood sugar starts to rise",
              "Combine it with your daily routine: while you eat lunch, already think about where you will walk afterwards",
            ],
          },
          { h2: "Typical results after 4–8 weeks" },
          {
            ul: [
              "HbA1c drops by 0.3–0.5% (especially in mild to moderate diabetes)",
              "Post-meal blood sugar (1–2 h after eating) drops by 1.5–2.5 mmol/L",
              "Insulin requirements decrease by 5–15% — many insulin users can reduce their dose",
              "Triglycerides decrease by 10–20% on average",
              "Sleep quality improves (fewer night-time hypoglycaemia events)",
            ],
          },
          { h2: "2. 10,000 steps a day — the long-term goal" },
          { p: "\"10,000 steps a day\" is a well-known public health message originating in 1965 in Japan (the pedometer \"manpo-kei\" meaning \"10,000 step meter\"). Today, scientific research tells us more precisely what step count means for the body." },
          { h2: "What does the science say?" },
          {
            ul: [
              "4,000 steps a day — the lowest threshold for a daily effect; mortality already starts to drop from this level",
              "7,000 steps a day — a significant reduction in mortality risk (~50% vs. 4,000 steps)",
              "10,000 steps a day — significantly reduced risk of diabetes, heart attack and stroke; much better weight control",
              "12,000–15,000 steps a day — additional benefit, but the marginal gain is smaller",
            ],
          },
          { p: "The biggest gain is for an inactive person becoming active — moving from 2,000 to 5,000 steps a day has a greater health effect than moving from 8,000 to 12,000. So don't worry if you can't hit 10,000 on day one — every extra step is useful." },
          { h2: "How to reach 10,000 steps" },
          {
            ul: [
              "Morning walk 30 min = about 3,500 steps",
              "Three post-meal walks of 10–15 min = about 3,000 steps",
              "An ordinary day at the office/at home = about 3,000–4,000 steps",
              "Total ≈ 9,500–10,500 steps a day",
            ],
          },
          { p: "Easy tricks to add steps:" },
          {
            ul: [
              "Take the stairs, not the lift",
              "Park further away or get off the bus one stop earlier",
              "Have \"walking meetings\" — take phone calls while walking, not sitting",
              "Walk the dog for 30+ minutes, not 10",
              "Evening walks with family — a social bonus",
              "Use a standing desk or treadmill at work",
              "Set a step goal on your phone or watch and track it daily",
            ],
          },
          { h2: "Long-term results over 6–12 months" },
          {
            ul: [
              "Weight loss of 3–8 kg (without other dietary changes)",
              "Waist circumference reduced by 4–7 cm",
              "Systolic blood pressure drops by 5–10 mmHg",
              "LDL cholesterol drops by 10–15%",
              "HDL (\"good\") cholesterol rises by 5–10%",
              "Stress, anxiety and depressive symptoms decrease",
              "Sleep quality improves",
              "Joint pain (especially knees and back) decreases",
            ],
          },
          { h2: "Measurement and tracking" },
          { p: "The easiest way to measure steps today is your phone or smartwatch:" },
          {
            ul: [
              "iPhone: the Apple Health app counts steps automatically",
              "Android: Google Fit or the manufacturer's app (Samsung Health, Xiaomi Mi Fit)",
              "Smartwatch (Garmin, Fitbit, Apple Watch, Polar) — more accurate and more motivating",
              "A simple pedometer (€10–30) — the cheapest option",
            ],
          },
          { p: "Track not only the total but also \"daily consistency\". 8,000 steps every day is better than 0 steps on weekdays + 70,000 on the weekend." },
          { h2: "Recommendations for diabetes patients" },
          { h2: "Blood sugar and walking" },
          { p: "On insulin therapy, a long walk can sharply lower blood glucose. Especially after a walk longer than 30 minutes or before bedtime. Recommendations: 1) Measure blood glucose before and after a longer walk, 2) Carry glucose tablets or a banana with you, 3) If pre-walk glucose is below 5 mmol/L, eat a small snack (15 g of carbs) first, 4) Calculate your insulin dose so it accounts for the glucose-lowering effect of a post-meal walk — the diabetes nurse will help plan this." },
          { h2: "A few contraindications" },
          { p: "Although walking suits almost everyone, in certain situations medical advice is needed:" },
          {
            ul: [
              "With a diabetic foot ulcer — prolonged walking can irritate the wound",
              "With moderate to severe retinopathy — avoid large climbs/descents (risk of retinal haemorrhage)",
              "After recent heart surgery — cardiologist's approval is needed",
              "With severe foot neuropathy — appropriate footwear and frequent foot checks are needed",
              "With significant balance disorders — use poles (Nordic walking)",
            ],
          },
          { h2: "A walking plan for beginners" },
          { p: "If you have not walked regularly before, don't start with 10,000 steps a day — it can cause muscle soreness and loss of motivation. We recommend the following 8-week plan:" },
          {
            ul: [
              "Weeks 1–2: add 1 post-meal 10-minute walk a day + 3,000–4,000 steps total",
              "Weeks 3–4: 2 post-meal walks + 5,000–6,000 steps total",
              "Weeks 5–6: 3 post-meal walks + 7,000 steps total",
              "Weeks 7–8: 3 post-meal walks + 9,000–10,000 steps total",
            ],
          },
          { p: "If some days don't work out — don't worry. Continuing tomorrow matters more than being perfect." },
          { h2: "At our clinic" },
          { p: "At Tallinn Endocrinology Clinic we help you build a personal walking plan based on your current status, type of diabetes and insulin therapy. Diabetes nurses Riina Vello (Tallinn), Liina Sildnik (Tallinn) and Kersti Saar (Pärnu) give specific advice:" },
          {
            ul: [
              "How to adjust your insulin dose taking walking into account",
              "What step goals suit your health",
              "How to use a CGM sensor to track the impact of walking",
              "Foot care and choice of footwear (especially in neuropathy)",
            ],
          },
          {
            callout: {
              type: "info",
              title: "One simple habit, big impact",
              body:
                "Walking after meals and 10,000 steps a day is a free, safe and scientifically proven intervention that can lower HbA1c as much as a single medication. Start today — a friendly 10-minute walk after lunch may be the thing that changes everything.",
            },
          },
        ],
      },
    },
  },

];

export const DISEASE_CATEGORIES = [
  { id: "all", label: "Kõik" },
  { id: "Diabeet", label: "Diabeet" },
  { id: "Diabeedi tüsistused", label: "Diabeedi tüsistused" },
  { id: "Diagnoos", label: "Diagnoos" },
  { id: "Ravi", label: "Ravi" },
  { id: "Elustiil", label: "Elustiil" },
];

export function findArticleBySlug(slug) {
  return DISEASE_ARTICLES.find((a) => a.slug === slug);
}

/**
 * Returns the article fields in the requested language.
 * Falls back to the Estonian (root) version when a translation is missing,
 * but always preserves shared fields (image, slug, updated, readingMinutes, etc.) from the root.
 */
export function pickArticleLang(article, lang) {
  if (!article) return article;
  if (lang === "et" || !article.translations || !article.translations[lang]) {
    return article;
  }
  const tr = article.translations[lang];
  return {
    ...article,
    category: tr.category ?? article.category,
    title: tr.title ?? article.title,
    summary: tr.summary ?? article.summary,
    imageCaption: tr.imageCaption ?? article.imageCaption,
    author: tr.author ?? article.author,
    sections: tr.sections ?? article.sections,
  };
}
